/**
 * ErvaPOS - Reports Dashboard Page
 * Relatórios avançados com exportação
 */

import React, { useMemo, useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Loader2,
  BarChart3,
  Download,
  FileSpreadsheet,
  FileText,
  TrendingUp,
  TrendingDown,
  Package,
  Users,
  User,
  UserX,
  Euro,
  Calendar,
  ShoppingCart,
  AlertTriangle,
  Clock,
  RefreshCw,
  Receipt,
  Wallet,
  Landmark,
  Activity,
  CircleDollarSign,
  Stethoscope,
  BadgeEuro,
  RefreshCcw
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import api, { reportsAPI, providersAPI, campaignsAPI } from '../services/api';

const formatCurrency = (value) => {
  return new Intl.NumberFormat('pt-PT', { style: 'currency', currency: 'EUR' }).format(value || 0);
};

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('pt-PT');
};

const getPreviousPeriod = (from, to) => {
  const fromDate = new Date(from);
  const toDate = new Date(to);

  const diffMs = toDate.getTime() - fromDate.getTime();
  const prevTo = new Date(fromDate.getTime() - 24 * 60 * 60 * 1000);
  const prevFrom = new Date(prevTo.getTime() - diffMs);

  const toISO = (d) => new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().split('T')[0];

  return {
    dateFrom: toISO(prevFrom),
    dateTo: toISO(prevTo),
  };
};

const getHomologuePeriod = (from, to) => {
  const fromDate = new Date(from);
  const toDate = new Date(to);

  const prevFrom = new Date(fromDate);
  prevFrom.setFullYear(prevFrom.getFullYear() - 1);

  const prevTo = new Date(toDate);
  prevTo.setFullYear(prevTo.getFullYear() - 1);

  const toISO = (d) => new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().split('T')[0];

  return {
    dateFrom: toISO(prevFrom),
    dateTo: toISO(prevTo),
  };
};


const classifyExpiry = (dateStr) => {
  if (!dateStr) return null;

  const expiry = new Date(dateStr);
  const now = new Date();

  const diff = Math.ceil((expiry - now) / (1000 * 60 * 60 * 24));

  if (diff < 0) return "expired";
  if (diff <= 30) return "critical";
  return "ok";
};

export const ReportsDashboardPage = () => {
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('sales');
  const [periodMode, setPeriodMode] = useState('month');
  const [selectedMonth, setSelectedMonth] = useState(() => new Date().toISOString().slice(0, 7));
  
  // Date filters
  const [dateFrom, setDateFrom] = useState(() => {
    const date = new Date();
    date.setDate(1); // First day of month
    return date.toISOString().split('T')[0];
  });
  const [dateTo, setDateTo] = useState(() => new Date().toISOString().split('T')[0]);
  
  // Report data
  const [salesReport, setSalesReport] = useState(null);
  const [stockReport, setStockReport] = useState(null);
  const [cashClosesReport, setCashClosesReport] = useState(null);
  const [expandedCashClose, setExpandedCashClose] = useState(null);
  const [expiringProducts, setExpiringProducts] = useState([]);
  const [expiryReport, setExpiryReport] = useState(null);

  const expiryStats = {
    expired: expiryReport?.summary?.expired_count || 0,
    critical: expiryReport?.summary?.critical_30_count || 0,
    expiring60: expiryReport?.summary?.expiring_60_count || 0,
    expiring90: expiryReport?.summary?.expiring_90_count || 0,
  };
  const [profitLoss, setProfitLoss] = useState(null);
  const [financialOverview, setFinancialOverview] = useState(null);
  const [taxOverview, setTaxOverview] = useState(null);
  const [providersFinancial, setProvidersFinancial] = useState(null);
  const [previousSalesReport, setPreviousSalesReport] = useState(null);
  const [previousProfitLoss, setPreviousProfitLoss] = useState(null);
  const [previousFinancialOverview, setPreviousFinancialOverview] = useState(null);

  const [providersList, setProvidersList] = useState([]);
  const [providerFilter, setProviderFilter] = useState('all');
  const [prepaymentsSummary, setPrepaymentsSummary] = useState(null);
  const [prepaymentsDetail, setPrepaymentsDetail] = useState(null);
  const [rebuildingPrepayments, setRebuildingPrepayments] = useState(false);
  const [exportType, setExportType] = useState('sales');
  const [topProducts, setTopProducts] = useState([]);
  const [topCustomers, setTopCustomers] = useState([]);
  const [topCategories, setTopCategories] = useState([]);
  const [purchasesDashboard, setPurchasesDashboard] = useState(null);
  const [purchaseInvoices, setPurchaseInvoices] = useState([]);
  const [purchaseSupplierFilter, setPurchaseSupplierFilter] = useState('all');
  const [comparisonYear, setComparisonYear] = useState(() => String(new Date().getFullYear()));
  const [comparisonCurrent, setComparisonCurrent] = useState({
    sales: null,
    profit: null,
    financial: null,
    tax: null,
    purchases: [],
    purchasesDashboard: null,
    providers: null,
  });
  const [comparisonPrevious, setComparisonPrevious] = useState({
    sales: null,
    profit: null,
    financial: null,
    tax: null,
    purchases: [],
    purchasesDashboard: null,
    providers: null,
  });
  const [homologueCurrent, setHomologueCurrent] = useState({
    sales: null,
    profit: null,
    financial: null,
    tax: null,
    purchases: [],
    purchasesDashboard: null,
    providers: null,
  });
  const [homologuePrevious, setHomologuePrevious] = useState({
    sales: null,
    profit: null,
    financial: null,
    tax: null,
    purchases: [],
    purchasesDashboard: null,
    providers: null,
  });
  const [expiryCategoryFilter, setExpiryCategoryFilter] = useState('all');
  const [expiryPriorityFilter, setExpiryPriorityFilter] = useState('all');
  const [expiryOnlyActionable, setExpiryOnlyActionable] = useState(false);
  const [showExpiryCampaignModal, setShowExpiryCampaignModal] = useState(false);
  const [selectedExpiryItem, setSelectedExpiryItem] = useState(null);
  const [creatingExpiryCampaign, setCreatingExpiryCampaign] = useState(false);
  const [expiryCampaignForm, setExpiryCampaignForm] = useState({
    name: '',
    description: '',
    campaign_type: 'percentage',
    discount_value: 10,
    start_date: new Date().toISOString().split('T')[0],
    end_date: '',
    min_purchase: 0,
    applicable_categories: [],
    excluded_categories: ['MEDICINA'],
    auto_generate_vouchers: true,
    voucher_prefix: 'EXP',
  });

  const managementData = useMemo(() => {
    const totalRevenue = Number(salesReport?.summary?.total_revenue || 0);
    const totalSales = Number(salesReport?.summary?.total_sales || 0);
    const averageSale = Number(salesReport?.summary?.average_sale || 0);

    const internalTotal = Number(salesReport?.internal_sales?.total || 0);
    const fiscalTotal = Number(salesReport?.fiscal_sales?.total || 0);

    const internalWeight = totalRevenue > 0 ? (internalTotal / totalRevenue) * 100 : 0;
    const fiscalWeight = totalRevenue > 0 ? (fiscalTotal / totalRevenue) * 100 : 0;

    const paymentMix = Object.entries(salesReport?.by_payment_method || {}).map(([method, row]) => {
      const total = Number(row?.total || 0);
      return {
        method,
        total,
        count: Number(row?.count || 0),
        percent: totalRevenue > 0 ? (total / totalRevenue) * 100 : 0,
      };
    }).sort((a, b) => b.total - a.total);

    const previousPaymentMap = Object.fromEntries(
      Object.entries(previousSalesReport?.by_payment_method || {}).map(([method, row]) => [
        method,
        {
          total: Number(row?.total || 0),
          count: Number(row?.count || 0),
        }
      ])
    );

    const paymentMixComparison = paymentMix.map((row) => {
      const previous = previousPaymentMap[row.method] || { total: 0, count: 0 };
      const diff = row.total - previous.total;
      const percentChange = previous.total !== 0 ? ((diff / previous.total) * 100) : null;

      return {
        ...row,
        previousTotal: previous.total,
        previousCount: previous.count,
        diff,
        percentChange,
      };
    });

    const clientsWithMetrics = (Array.isArray(topCustomers) ? topCustomers : []).map((item) => {
      const total = Number(item.total || 0);
      const salesCount = Number(item.sales_count || 0);
      return {
        ...item,
        averageTicket: salesCount > 0 ? total / salesCount : 0,
        revenueWeight: totalRevenue > 0 ? (total / totalRevenue) * 100 : 0,
        customerLabel: (item.customer_name || '').trim() || 'Consumidor Final',
        isAnonymous: !String(item.customer_id || '').trim(),
      };
    });

    const recurringCustomers = clientsWithMetrics.filter((c) => !c.isAnonymous && Number(c.sales_count || 0) > 1).length;
    const oneTimeCustomers = clientsWithMetrics.filter((c) => !c.isAnonymous && Number(c.sales_count || 0) <= 1).length;
    const anonymousCustomers = clientsWithMetrics.filter((c) => c.isAnonymous).length;
    const averageTicketTopCustomers =
      clientsWithMetrics.length > 0
        ? clientsWithMetrics.reduce((acc, c) => acc + Number(c.averageTicket || 0), 0) / clientsWithMetrics.length
        : 0;

    const grossMargin = Number(profitLoss?.gross_margin || 0);
    const marginPercent = Number(profitLoss?.margin_percent || 0);
    const netProfit = Number(profitLoss?.net_profit || 0);
    const totalExpenses = Number(profitLoss?.total_expenses || 0);

    const totalReceipts = Number(financialOverview?.summary?.total_receipts || 0);
    const operationalBalance = Number(financialOverview?.summary?.operational_balance || 0);
    const providerOpen = Number(financialOverview?.summary?.total_provider_open || 0);
    const providerPaid = Number(financialOverview?.summary?.total_provider_paid || 0);

    const vatNet = Number(taxOverview?.summary?.vat_net_estimated || 0);
    const purchasesPending = Number(taxOverview?.summary?.total_purchases_pending || 0);
    const purchasesTotal = Number(taxOverview?.summary?.total_purchases || 0);

    const prepaymentsOpen = Number(prepaymentsSummary?.open_total_estimated || 0);
    const prepaymentsConsumed = Number(prepaymentsSummary?.consumed_linked_customer_orders ?? prepaymentsSummary?.consumed_in_sales ?? 0);

    const pressureTotal = providerOpen + purchasesPending + Math.max(0, vatNet) + prepaymentsOpen;

    const previousRevenue = Number(previousSalesReport?.summary?.total_revenue || 0);
    const previousAverageSale = Number(previousSalesReport?.summary?.average_sale || 0);
    const previousNetProfit = Number(previousProfitLoss?.net_profit || 0);
    const previousReceipts = Number(previousFinancialOverview?.summary?.total_receipts || 0);

    const compareValue = (current, previous) => ({
      current,
      previous,
      diff: current - previous,
      percent: previous !== 0 ? ((current - previous) / previous) * 100 : null,
    });

    const comparisons = {
      revenue: compareValue(totalRevenue, previousRevenue),
      averageSale: compareValue(averageSale, previousAverageSale),
      netProfit: compareValue(netProfit, previousNetProfit),
      receipts: compareValue(totalReceipts, previousReceipts),
    };

    const currentCategories = Array.isArray(salesReport?.top_categories) ? salesReport.top_categories : [];
    const previousCategories = Array.isArray(previousSalesReport?.top_categories) ? previousSalesReport.top_categories : [];

    const providerRows =
      (Array.isArray(providersFinancial?.providers) && providersFinancial.providers.length > 0 && providersFinancial.providers) ||
      (Array.isArray(providersFinancial?.rows) && providersFinancial.rows.length > 0 && providersFinancial.rows) ||
      (Array.isArray(providersFinancial?.items) && providersFinancial.items.length > 0 && providersFinancial.items) ||
      [];

    const topProviders = providerRows
      .map((row) => {
        const name =
          row?.provider_name ||
          row?.name ||
          row?.provider ||
          'Prestador';

        const produced =
          Number(
            row?.total_service_value ??
            row?.service_total ??
            row?.services_total ??
            row?.gross_total ??
            0
          ) || 0;

        const due =
          Number(
            row?.total_due ??
            row?.amount_due ??
            row?.due_total ??
            0
          ) || 0;

        const paid =
          Number(
            row?.total_paid ??
            row?.amount_paid ??
            row?.paid_total ??
            row?.paid ??
            0
          ) || 0;

        const open =
          Number(
            row?.total_open ??
            row?.amount_open ??
            row?.open_total ??
            row?.outstanding_total ??
            row?.pending_total ??
            row?.open ??
            0
          ) || 0;

        return {
          name,
          produced,
          due,
          paid,
          open,
        };
      })
      .sort((a, b) => b.produced - a.produced)
      .slice(0, 10);

    const previousCategoriesMap = Object.fromEntries(
      previousCategories.map((item) => [
        String(item?.category || 'SEM CATEGORIA'),
        {
          total: Number(item?.total || 0),
          quantity: Number(item?.quantity || 0),
          sales_count: Number(item?.sales_count || 0),
        }
      ])
    );

    const categoryComparisons = currentCategories.map((item) => {
      const category = String(item?.category || 'SEM CATEGORIA');
      const currentTotal = Number(item?.total || 0);
      const previousRow = previousCategoriesMap[category] || { total: 0, quantity: 0, sales_count: 0 };
      const previousTotal = Number(previousRow.total || 0);
      const diff = currentTotal - previousTotal;
      const percent = previousTotal !== 0 ? ((diff / previousTotal) * 100) : null;

      return {
        category,
        currentTotal,
        previousTotal,
        diff,
        percent,
        quantity: Number(item?.quantity || 0),
        salesCount: Number(item?.sales_count || 0),
      };
    }).sort((a, b) => Math.abs(b.diff) - Math.abs(a.diff));

    const alerts = [];
    if (netProfit < 0) alerts.push({ level: 'high', text: 'Lucro líquido negativo no período' });
    if (operationalBalance < 0) alerts.push({ level: 'high', text: 'Saldo operacional negativo' });
    if (vatNet > 0) alerts.push({ level: 'medium', text: 'IVA estimado a pagar' });
    if (providerOpen > 0) alerts.push({ level: 'medium', text: 'Existem valores em aberto a prestadores' });
    if (purchasesPending > 0) alerts.push({ level: 'medium', text: 'Existem compras pendentes por liquidar' });
    if (prepaymentsOpen > 0) alerts.push({ level: 'low', text: 'Existem pré-pagamentos ainda por consumir' });

    let healthLabel = 'Estável';
    let healthClass = 'text-emerald-400';
    if (netProfit < 0 || operationalBalance < 0) {
      healthLabel = 'Atenção';
      healthClass = 'text-rose-400';
    } else if (vatNet > 0 || providerOpen > 0 || purchasesPending > 0) {
      healthLabel = 'Vigiar';
      healthClass = 'text-amber-400';
    }

    return {
      totalRevenue,
      totalSales,
      averageSale,
      internalTotal,
      fiscalTotal,
      internalWeight,
      fiscalWeight,
      paymentMix,
      paymentMixComparison,
      clientsWithMetrics,
      recurringCustomers,
      oneTimeCustomers,
      anonymousCustomers,
      averageTicketTopCustomers,
      grossMargin,
      marginPercent,
      netProfit,
      totalExpenses,
      totalReceipts,
      operationalBalance,
      providerOpen,
      providerPaid,
      vatNet,
      purchasesPending,
      purchasesTotal,
      prepaymentsOpen,
      prepaymentsConsumed,
      pressureTotal,
      comparisons,
      categoryComparisons,
      topProviders,
      alerts,
      healthLabel,
      healthClass,
    };
  }, [salesReport, profitLoss, financialOverview, taxOverview, prepaymentsSummary, previousSalesReport, previousProfitLoss, previousFinancialOverview, providersFinancial, topCustomers]);

  const purchasesData = useMemo(() => {
    const rows = Array.isArray(purchaseInvoices) ? purchaseInvoices : [];

    const matchesSupplier = (item) => {
      if (purchaseSupplierFilter === 'all') return true;

      const supplierId = String(item?.supplier_id || '').trim();
      const supplierName =
        String(item?.supplier_name || '').trim() ||
        String(item?.supplier || '').trim();

      return supplierId === purchaseSupplierFilter || supplierName === purchaseSupplierFilter;
    };

    const selectedYear = String(dateTo || new Date().toISOString().split('T')[0]).slice(0, 4);
    const yearStart = `${selectedYear}-01-01`;
    const yearEnd = `${selectedYear}-12-31`;

    const supplierScoped = rows.filter(matchesSupplier);

    const civilYearFiltered = supplierScoped.filter((item) => {
      const invoiceDate = String(item?.invoice_date || '').slice(0, 10);
      if (!invoiceDate) return false;
      return invoiceDate >= yearStart && invoiceDate <= yearEnd;
    });

    const periodFiltered = supplierScoped.filter((item) => {
      const invoiceDate = String(item?.invoice_date || '').slice(0, 10);
      if (!invoiceDate) return false;
      return invoiceDate >= dateFrom && invoiceDate <= dateTo;
    });

    const effectiveRows = periodFiltered.length > 0 ? periodFiltered : civilYearFiltered;
    const usingCivilYearFallback = periodFiltered.length === 0 && civilYearFiltered.length > 0;

    const today = new Date().toISOString().split('T')[0];

    const getOpenAmount = (item) =>
      Math.max(0, Number(item?.total || 0) - Number(item?.amount_paid || 0));

    const isOverdueInvoice = (item) => {
      const status = String(item?.payment_status || '').toLowerCase();
      const dueDate = String(item?.due_date || '').slice(0, 10);
      const open = getOpenAmount(item);

      if (open <= 0 || status === 'paid') return false;
      return status === 'overdue' || (!!dueDate && dueDate < today);
    };

    const isOpenInvoice = (item) => {
      const status = String(item?.payment_status || '').toLowerCase();
      const open = getOpenAmount(item);
      if (open <= 0) return false;
      return status !== 'paid';
    };

    const debtRows = effectiveRows.filter(isOpenInvoice);

    const totalPurchased = effectiveRows.reduce((acc, item) => acc + Number(item?.total || 0), 0);
    const totalPaid = effectiveRows.reduce((acc, item) => acc + Number(item?.amount_paid || 0), 0);

    const totalVatDeductible = effectiveRows.reduce(
      (acc, item) => acc + Number(item?.vat_deductible ?? item?.vat_total ?? 0),
      0
    );

    const totalPending = debtRows.reduce((acc, item) => acc + getOpenAmount(item), 0);
    const overdueCount = debtRows.filter(isOverdueInvoice).length;

    const paidCount = effectiveRows.filter((item) => String(item?.payment_status || '').toLowerCase() === 'paid').length;
    const partialCount = debtRows.filter((item) => String(item?.payment_status || '').toLowerCase() === 'partial').length;
    const pendingCount = debtRows.filter((item) => {
      const status = String(item?.payment_status || '').toLowerCase();
      return status === 'pending' || status === 'partial' || status === 'overdue' || !status;
    }).length;

    const suppliersMap = {};
    for (const item of debtRows) {
      const supplierId = String(item?.supplier_id || '').trim();
      const supplierName =
        String(item?.supplier_name || '').trim() ||
        String(item?.supplier || '').trim() ||
        supplierId ||
        'Fornecedor';

      const key = supplierId || supplierName;
      if (!suppliersMap[key]) {
        suppliersMap[key] = {
          supplier_id: supplierId,
          supplier_name: supplierName,
          invoices_count: 0,
          total: 0,
          paid: 0,
          pending: 0,
        };
      }

      const total = Number(item?.total || 0);
      const paid = Number(item?.amount_paid || 0);
      const pending = getOpenAmount(item);

      suppliersMap[key].invoices_count += 1;
      suppliersMap[key].total += total;
      suppliersMap[key].paid += paid;
      suppliersMap[key].pending += pending;
    }

    const topSuppliers = Object.values(suppliersMap)
      .sort((a, b) => b.pending - a.pending || b.total - a.total)
      .slice(0, 12);

    const suppliersToPay = Object.values(
      debtRows.reduce((acc, item) => {
        const supplierName =
          String(item?.supplier_name || '').trim() ||
          String(item?.supplier || '').trim() ||
          String(item?.supplier_id || '').trim() ||
          'Fornecedor';

        const open = getOpenAmount(item);
        const dueDate = String(item?.due_date || '').slice(0, 10);
        const overdue = isOverdueInvoice(item);

        if (!acc[supplierName]) {
          acc[supplierName] = {
            supplierName,
            invoicesCount: 0,
            totalOpen: 0,
            overdueOpen: 0,
            notDueOpen: 0,
            nextDueDate: '',
          };
        }

        acc[supplierName].invoicesCount += 1;
        acc[supplierName].totalOpen += open;

        if (overdue) {
          acc[supplierName].overdueOpen += open;
        } else {
          acc[supplierName].notDueOpen += open;
        }

        if (dueDate) {
          if (!acc[supplierName].nextDueDate || dueDate < acc[supplierName].nextDueDate) {
            acc[supplierName].nextDueDate = dueDate;
          }
        }

        return acc;
      }, {})
    )
      .sort((a, b) => b.totalOpen - a.totalOpen)
      .slice(0, 12);

    const latestInvoices = [...effectiveRows]
      .sort((a, b) => {
        const da = String(a?.invoice_date || '');
        const db = String(b?.invoice_date || '');
        return db.localeCompare(da);
      })
      .slice(0, 15);

    const overdueInvoices = [...debtRows]
      .filter(isOverdueInvoice)
      .sort((a, b) => String(a?.due_date || '9999-12-31').localeCompare(String(b?.due_date || '9999-12-31')))
      .slice(0, 12);

    const agingBuckets = debtRows.reduce((acc, item) => {
      const open = getOpenAmount(item);
      const dueDate = String(item?.due_date || '').slice(0, 10);

      if (open <= 0) return acc;

      if (!dueDate) {
        acc.no_due_date += open;
        return acc;
      }

      const diffMs = new Date(today) - new Date(dueDate);
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

      if (diffDays < 0) {
        acc.not_due += open;
      } else if (diffDays <= 30) {
        acc.overdue_1_30 += open;
      } else if (diffDays <= 60) {
        acc.overdue_31_60 += open;
      } else {
        acc.overdue_61_plus += open;
      }

      return acc;
    }, {
      not_due: 0,
      overdue_1_30: 0,
      overdue_31_60: 0,
      overdue_61_plus: 0,
      no_due_date: 0,
    });

    return {
      filtered: periodFiltered,
      civilYearFiltered,
      effectiveRows,
      usingCivilYearFallback,
      selectedYear,
      totalPurchased,
      totalPaid,
      totalPending,
      totalVatDeductible,
      overdueCount,
      paidCount,
      partialCount,
      pendingCount,
      topSuppliers,
      suppliersToPay,
      latestInvoices,
      overdueInvoices,
      agingBuckets,
      dashboard: purchasesDashboard,
    };
  }, [purchaseInvoices, purchasesDashboard, dateFrom, dateTo, purchaseSupplierFilter]);

  const purchaseSupplierOptions = useMemo(() => {
    const rows = Array.isArray(purchaseInvoices) ? purchaseInvoices : [];

    const inPeriod = rows.filter((item) => {
      const invoiceDate = String(item?.invoice_date || '').slice(0, 10);
      if (!invoiceDate) return false;
      return invoiceDate >= dateFrom && invoiceDate <= dateTo;
    });

    const map = new Map();

    for (const item of inPeriod) {
      const supplierId = String(item?.supplier_id || '').trim();
      const supplierName =
        String(item?.supplier_name || '').trim() ||
        String(item?.supplier || '').trim() ||
        supplierId;

      if (!supplierName && !supplierId) continue;

      const key = supplierId || supplierName;
      if (!map.has(key)) {
        map.set(key, {
          value: key,
          label: supplierName || supplierId || 'Fornecedor',
        });
      }
    }

    return Array.from(map.values()).sort((a, b) => a.label.localeCompare(b.label, 'pt'));
  }, [purchaseInvoices, dateFrom, dateTo]);

  const homologueData = useMemo(() => {
    const currentSales = Number(homologueCurrent?.sales?.summary?.total_revenue || 0);
    const previousSales = Number(homologuePrevious?.sales?.summary?.total_revenue || 0);

    const currentAverageSale = Number(homologueCurrent?.sales?.summary?.average_sale || 0);
    const previousAverageSale = Number(homologuePrevious?.sales?.summary?.average_sale || 0);

    const currentNetProfit = Number(homologueCurrent?.profit?.net_profit || 0);
    const previousNetProfit = Number(homologuePrevious?.profit?.net_profit || 0);

    const currentReceipts = Number(homologueCurrent?.financial?.summary?.total_receipts || 0);
    const previousReceipts = Number(homologuePrevious?.financial?.summary?.total_receipts || 0);

    const currentInternalSales = Number(homologueCurrent?.sales?.internal_sales?.total || 0);
    const previousInternalSales = Number(homologuePrevious?.sales?.internal_sales?.total || 0);

    const currentFiscalSales = Number(homologueCurrent?.sales?.fiscal_sales?.total || 0);
    const previousFiscalSales = Number(homologuePrevious?.sales?.fiscal_sales?.total || 0);

    const currentPurchases = Array.isArray(homologueCurrent?.purchases)
      ? homologueCurrent.purchases.reduce((acc, item) => acc + Number(item?.total || 0), 0)
      : 0;

    const previousPurchases = Array.isArray(homologuePrevious?.purchases)
      ? homologuePrevious.purchases.reduce((acc, item) => acc + Number(item?.total || 0), 0)
      : 0;

    const currentPurchasesPending = Array.isArray(homologueCurrent?.purchases)
      ? homologueCurrent.purchases.reduce((acc, item) => acc + Math.max(0, Number(item?.total || 0) - Number(item?.amount_paid || 0)), 0)
      : 0;

    const previousPurchasesPending = Array.isArray(homologuePrevious?.purchases)
      ? homologuePrevious.purchases.reduce((acc, item) => acc + Math.max(0, Number(item?.total || 0) - Number(item?.amount_paid || 0)), 0)
      : 0;

    const currentCustomers = Array.isArray(homologueCurrent?.sales?.top_customers) ? homologueCurrent.sales.top_customers.length : 0;
    const previousCustomers = Array.isArray(homologuePrevious?.sales?.top_customers) ? homologuePrevious.sales.top_customers.length : 0;

    const currentCategories = Array.isArray(homologueCurrent?.sales?.top_categories) ? homologueCurrent.sales.top_categories.length : 0;
    const previousCategories = Array.isArray(homologuePrevious?.sales?.top_categories) ? homologuePrevious.sales.top_categories.length : 0;

    const currentPaymentMethods = homologueCurrent?.sales?.by_payment_method ? Object.keys(homologueCurrent.sales.by_payment_method).length : 0;
    const previousPaymentMethods = homologuePrevious?.sales?.by_payment_method ? Object.keys(homologuePrevious.sales.by_payment_method).length : 0;

    const currentProviders = Array.isArray(homologueCurrent?.providers?.providers) ? homologueCurrent.providers.providers.length : 0;
    const previousProviders = Array.isArray(homologuePrevious?.providers?.providers) ? homologuePrevious.providers.providers.length : 0;

    const buildRow = (current, previous) => {
      const diff = Number(current || 0) - Number(previous || 0);
      const percent = Number(previous || 0) !== 0 ? (diff / Number(previous || 1)) * 100 : null;
      return {
        current: Number(current || 0),
        previous: Number(previous || 0),
        diff,
        percent,
      };
    };

    return {
      sales: buildRow(currentSales, previousSales),
      averageSale: buildRow(currentAverageSale, previousAverageSale),
      netProfit: buildRow(currentNetProfit, previousNetProfit),
      receipts: buildRow(currentReceipts, previousReceipts),
      internalSales: buildRow(currentInternalSales, previousInternalSales),
      fiscalSales: buildRow(currentFiscalSales, previousFiscalSales),
      purchases: buildRow(currentPurchases, previousPurchases),
      purchasesPending: buildRow(currentPurchasesPending, previousPurchasesPending),
      customers: buildRow(currentCustomers, previousCustomers),
      categories: buildRow(currentCategories, previousCategories),
      paymentMethods: buildRow(currentPaymentMethods, previousPaymentMethods),
      providers: buildRow(currentProviders, previousProviders),
    };
  }, [homologueCurrent, homologuePrevious]);

  const comparisonData = useMemo(() => {
    const currentSales = Number(comparisonCurrent?.sales?.summary?.total_revenue || 0);
    const previousSales = Number(comparisonPrevious?.sales?.summary?.total_revenue || 0);

    const currentAverageSale = Number(comparisonCurrent?.sales?.summary?.average_sale || 0);
    const previousAverageSale = Number(comparisonPrevious?.sales?.summary?.average_sale || 0);

    const currentNetProfit = Number(comparisonCurrent?.profit?.net_profit || 0);
    const previousNetProfit = Number(comparisonPrevious?.profit?.net_profit || 0);

    const currentReceipts = Number(comparisonCurrent?.financial?.summary?.total_receipts || 0);
    const previousReceipts = Number(comparisonPrevious?.financial?.summary?.total_receipts || 0);

    const currentInternalSales = Number(comparisonCurrent?.sales?.internal_sales?.total || 0);
    const previousInternalSales = Number(comparisonPrevious?.sales?.internal_sales?.total || 0);

    const currentFiscalSales = Number(comparisonCurrent?.sales?.fiscal_sales?.total || 0);
    const previousFiscalSales = Number(comparisonPrevious?.sales?.fiscal_sales?.total || 0);

    const currentPurchases = Array.isArray(comparisonCurrent?.purchases)
      ? comparisonCurrent.purchases.reduce((acc, item) => acc + Number(item?.total || 0), 0)
      : 0;

    const previousPurchases = Array.isArray(comparisonPrevious?.purchases)
      ? comparisonPrevious.purchases.reduce((acc, item) => acc + Number(item?.total || 0), 0)
      : 0;

    const currentPurchasesPending = Array.isArray(comparisonCurrent?.purchases)
      ? comparisonCurrent.purchases.reduce((acc, item) => acc + Math.max(0, Number(item?.total || 0) - Number(item?.amount_paid || 0)), 0)
      : 0;

    const previousPurchasesPending = Array.isArray(comparisonPrevious?.purchases)
      ? comparisonPrevious.purchases.reduce((acc, item) => acc + Math.max(0, Number(item?.total || 0) - Number(item?.amount_paid || 0)), 0)
      : 0;

    const currentCustomers = Array.isArray(comparisonCurrent?.sales?.top_customers) ? comparisonCurrent.sales.top_customers.length : 0;
    const previousCustomers = Array.isArray(comparisonPrevious?.sales?.top_customers) ? comparisonPrevious.sales.top_customers.length : 0;

    const currentCategories = Array.isArray(comparisonCurrent?.sales?.top_categories) ? comparisonCurrent.sales.top_categories.length : 0;
    const previousCategories = Array.isArray(comparisonPrevious?.sales?.top_categories) ? comparisonPrevious.sales.top_categories.length : 0;

    const currentPaymentMethods = comparisonCurrent?.sales?.by_payment_method ? Object.keys(comparisonCurrent.sales.by_payment_method).length : 0;
    const previousPaymentMethods = comparisonPrevious?.sales?.by_payment_method ? Object.keys(comparisonPrevious.sales.by_payment_method).length : 0;

    const currentProviders = Array.isArray(comparisonCurrent?.providers?.providers) ? comparisonCurrent.providers.providers.length : 0;
    const previousProviders = Array.isArray(comparisonPrevious?.providers?.providers) ? comparisonPrevious.providers.providers.length : 0;

    const currentProviderOpen = Number(comparisonCurrent?.providers?.summary?.total_open || 0);
    const previousProviderOpen = Number(comparisonPrevious?.providers?.summary?.total_open || 0);

    const topCustomerCurrent =
      Array.isArray(comparisonCurrent?.sales?.top_customers) && comparisonCurrent.sales.top_customers.length > 0
        ? comparisonCurrent.sales.top_customers[0]
        : null;

    const topCustomerPrevious =
      Array.isArray(comparisonPrevious?.sales?.top_customers) && comparisonPrevious.sales.top_customers.length > 0
        ? comparisonPrevious.sales.top_customers[0]
        : null;

    const topCategoryCurrent =
      Array.isArray(comparisonCurrent?.sales?.top_categories) && comparisonCurrent.sales.top_categories.length > 0
        ? comparisonCurrent.sales.top_categories[0]
        : null;

    const topCategoryPrevious =
      Array.isArray(comparisonPrevious?.sales?.top_categories) && comparisonPrevious.sales.top_categories.length > 0
        ? comparisonPrevious.sales.top_categories[0]
        : null;

    const buildRow = (current, previous) => {
      const diff = Number(current || 0) - Number(previous || 0);
      const percent = Number(previous || 0) !== 0 ? (diff / Number(previous || 1)) * 100 : null;
      return {
        current: Number(current || 0),
        previous: Number(previous || 0),
        diff,
        percent,
      };
    };

    return {
      sales: buildRow(currentSales, previousSales),
      averageSale: buildRow(currentAverageSale, previousAverageSale),
      netProfit: buildRow(currentNetProfit, previousNetProfit),
      receipts: buildRow(currentReceipts, previousReceipts),
        internalSales: buildRow(currentInternalSales, previousInternalSales),
        fiscalSales: buildRow(currentFiscalSales, previousFiscalSales),
      purchases: buildRow(currentPurchases, previousPurchases),
      purchasesPending: buildRow(currentPurchasesPending, previousPurchasesPending),
      customers: buildRow(currentCustomers, previousCustomers),
      categories: buildRow(currentCategories, previousCategories),
      paymentMethods: buildRow(currentPaymentMethods, previousPaymentMethods),
      providers: buildRow(currentProviders, previousProviders),
      providersOpen: buildRow(currentProviderOpen, previousProviderOpen),
      topCustomer: {
        currentName: topCustomerCurrent?.customer_name || 'Sem dados',
        currentTotal: Number(topCustomerCurrent?.total || 0),
        previousName: topCustomerPrevious?.customer_name || 'Sem dados',
        previousTotal: Number(topCustomerPrevious?.total || 0),
      },
      topCategory: {
        currentName: topCategoryCurrent?.category || 'Sem dados',
        currentTotal: Number(topCategoryCurrent?.total || 0),
        previousName: topCategoryPrevious?.category || 'Sem dados',
        previousTotal: Number(topCategoryPrevious?.total || 0),
      },
    };
  }, [comparisonCurrent, comparisonPrevious]);

  const integratedFinancialData = useMemo(() => {
    const purchasesPending = Number(purchasesData?.totalPending || 0);
    const purchasesTotal = Number(purchasesData?.totalPurchased || 0);

    const suppliersOverdue =
      Number(purchasesData?.agingBuckets?.overdue_1_30 || 0) +
      Number(purchasesData?.agingBuckets?.overdue_31_60 || 0) +
      Number(purchasesData?.agingBuckets?.overdue_61_plus || 0);

    const supplierNotDue = Number(purchasesData?.agingBuckets?.not_due || 0);

    const providerOpen = Number(providersFinancial?.summary?.total_open || 0);
    const providerPaid = Number(providersFinancial?.summary?.total_paid || 0);

    const vatNet = Number(taxOverview?.summary?.vat_net_estimated || 0);
    const vatPayable = vatNet > 0 ? vatNet : 0;
    const vatRecoverable = vatNet < 0 ? Math.abs(vatNet) : 0;
    const vatLabel = vatNet > 0 ? 'A pagar' : vatNet < 0 ? 'A recuperar' : 'Neutro';

    const pressureTotal = vatPayable + purchasesPending + providerOpen;

    const totalReceipts = Number(
      taxOverview?.summary?.total_receipts ||
      0
    );

    const pressureVsReceiptsPercent =
      totalReceipts > 0 ? (pressureTotal / totalReceipts) * 100 : null;

    const overduePurchasesPercent =
      purchasesPending > 0 ? (suppliersOverdue / purchasesPending) * 100 : null;

    let riskLabel = 'Controlado';
    let riskClass = 'text-emerald-400';

    if (pressureTotal > 0 && (pressureVsReceiptsPercent || 0) >= 60) {
      riskLabel = 'Elevado';
      riskClass = 'text-rose-400';
    } else if (pressureTotal > 0 && (pressureVsReceiptsPercent || 0) >= 30) {
      riskLabel = 'Atenção';
      riskClass = 'text-amber-400';
    }

    return {
      purchasesPending,
      purchasesTotal,
      suppliersOverdue,
      supplierNotDue,
      providerOpen,
      providerPaid,
      vatNet,
      vatPayable,
      vatRecoverable,
      vatLabel,
      pressureTotal,
      totalReceipts,
      pressureVsReceiptsPercent,
      overduePurchasesPercent,
      riskLabel,
      riskClass,
    };
  }, [purchasesData, providersFinancial, taxOverview]);

  const { isManager } = useAuth();

  useEffect(() => {
    loadReports();
  }, [dateFrom, dateTo, activeTab, providerFilter, comparisonYear]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (periodMode !== 'month' || !selectedMonth) return;
    const [year, month] = selectedMonth.split('-').map(Number);
    const start = `${selectedMonth}-01`;
    const lastDay = String(new Date(year, month, 0).getDate()).padStart(2, '0');
    const end = `${selectedMonth}-${lastDay}`;
    setDateFrom(start);
    setDateTo(end);
  }, [periodMode, selectedMonth]);

  useEffect(() => {
    loadProvidersList();
  }, []);

  useEffect(() => {
    const loadHeaderExpiry = async () => {
      try {
        await loadExpiringProducts();
      } catch (error) {
        console.error('Erro ao carregar alerta de validades:', error);
      }
    };

    loadHeaderExpiry();
  }, [dateFrom, dateTo]);

  const loadReports = async () => {
    setLoading(true);
    try {
      switch (activeTab) {
        case 'management':
          await Promise.all([
            loadSalesReport(),
            loadProfitLoss(),
            loadFinancialOverview(),
            loadTaxOverview(),
            loadPrepaymentsReport(),
            loadPreviousComparisonData(),
            loadProvidersFinancial(),
          ]);
          break;
        case 'sales':
          await loadSalesReport();
          break;
        case 'cash-closes':
          await loadCashClosesReport();
          break;

        case 'categories':
          await loadSalesReport();
          break;

        case 'clients':
          await loadSalesReport();
          break;
        case 'stock':
          await loadStockReport();
          break;
        case 'expiring':
          await loadExpiringProducts();
          break;
        case 'profit':
          await loadProfitLoss();
          break;
        case 'financial':
          await loadFinancialOverview();
          break;
        case 'tax':
          await loadTaxOverview();
          break;
        case 'comparison':
          await Promise.all([
            loadYearComparisonData(),
            loadHomologueComparisonData(),
          ]);
          break;
        case 'purchases':
          await Promise.all([
            loadPurchasesDashboard(),
            loadPurchaseInvoices(),
            loadTaxOverview(),
          ]);
          break;
        case 'providers':
          await loadProvidersFinancial();
          break;
        case 'prepayments':
          await loadPrepaymentsReport();
          break;
      }
    } catch (error) {
      console.error('Error loading reports:', error);
      toast.error('Erro ao carregar relatórios');
    } finally {
      setLoading(false);
    }
  };

  const loadSalesReport = async () => {
    const response = await api.get('/reports-export/sales-summary', {
      params: { start_date: dateFrom, end_date: dateTo }
    });

    const data = response.data || {};
    setSalesReport(data);
    setTopProducts(Array.isArray(data.top_products) ? data.top_products : []);
    setTopCustomers(Array.isArray(data.top_customers) ? data.top_customers : []);
    setTopCategories(Array.isArray(data.top_categories) ? data.top_categories : []);
  };

  const loadCashClosesReport = async () => {
    const response = await reportsAPI.getCashCloses(dateFrom, dateTo);
    setCashClosesReport(response.data || { closes: [], totals: {} });
  };

  const downloadCashClosesCSV = () => {
    const rows = cashClosesReport?.closes || [];
    const escape = (value) => `"${String(value ?? '').replace(/"/g, '""')}"`;
    const header = ['Data', 'Hora', 'Tipo', 'Fechado por', 'Operadores', 'Caixas', 'Vendas', 'Valor vendas', 'Esperado', 'Conferido', 'Diferença', 'Observações'];
    const lines = rows.map((row) => [
      row.date,
      row.created_at ? new Date(row.created_at).toLocaleTimeString('pt-PT', { hour: '2-digit', minute: '2-digit' }) : '',
      row.kind === 'consolidated' ? 'Consolidado' : 'Individual',
      row.closed_by_name,
      (row.operator_names || []).join(' / '),
      row.session_count,
      row.sales_count,
      Number(row.sales_total || 0).toFixed(2),
      Number(row.expected_total || 0).toFixed(2),
      Number(row.actual_total || 0).toFixed(2),
      Number(row.difference || 0).toFixed(2),
      row.note || '',
    ].map(escape).join(';'));
    const blob = new Blob([`\uFEFF${header.map(escape).join(';')}\n${lines.join('\n')}`], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `fechos-caixa-${dateFrom}-${dateTo}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const loadStockReport = async () => {
    const response = await reportsAPI.getInventoryValue();
    setStockReport(response.data);
  };

  const loadExpiringProducts = async () => {
    const response = await reportsAPI.getExpiryRisk(90);
    const data = response.data || {};
    setExpiryReport(data);
    setExpiringProducts(Array.isArray(data.products) ? data.products : []);
  };

  const loadProfitLoss = async () => {
    const response = await api.get('/reports-export/profit-loss', {
      params: { date_from: dateFrom, date_to: dateTo }
    });
    setProfitLoss(response.data);
  };

  const loadFinancialOverview = async () => {
    const response = await reportsAPI.getFinancialOverview({
      date_from: dateFrom,
      date_to: dateTo,
    });
    setFinancialOverview(response.data);
  };

  const loadTaxOverview = async () => {
    const response = await reportsAPI.getTaxOverview({
      date_from: dateFrom,
      date_to: dateTo,
    });
    setTaxOverview(response.data);
  };

  const loadProvidersList = async () => {
    try {
      const response = await providersAPI.getAll();
      const rows = Array.isArray(response?.data) ? response.data : [];
      rows.sort((a, b) =>
        String(a?.name || '').localeCompare(String(b?.name || ''), 'pt')
      );
      setProvidersList(rows);
    } catch (error) {
      console.error('Erro ao carregar lista de prestadores:', error);
    }
  };

  const loadProvidersFinancial = async () => {
    const params = {
      start_date: dateFrom,
      end_date: dateTo,
    };

    if (providerFilter && providerFilter !== 'all') {
      params.provider_id = providerFilter;
    }

    const response = await reportsAPI.getProvidersFinancial(params);
    setProvidersFinancial(response.data);
  };

  const loadPurchasesDashboard = async () => {
    const response = await api.get('/invoices/dashboard');
    setPurchasesDashboard(response.data || null);
  };

  const loadPurchaseInvoices = async () => {
    const response = await api.get('/invoices');
    setPurchaseInvoices(Array.isArray(response.data) ? response.data : []);
  };

  const loadPrepaymentsReport = async () => {
    const [summaryRes, detailRes] = await Promise.all([
      reportsAPI.getPrepaymentsSummary(),
      reportsAPI.getPrepaymentsDetail(),
    ]);

    setPrepaymentsSummary(summaryRes.data?.summary || null);
    setPrepaymentsDetail(detailRes.data || null);
  };

  const loadPreviousComparisonData = async () => {
    const previous = getPreviousPeriod(dateFrom, dateTo);

    const [salesRes, profitRes, financialRes] = await Promise.all([
      api.get('/reports-export/sales-summary', {
        params: { start_date: previous.dateFrom, end_date: previous.dateTo }
      }),
      api.get('/reports-export/profit-loss', {
        params: { date_from: previous.dateFrom, date_to: previous.dateTo }
      }),
      reportsAPI.getFinancialOverview({
        date_from: previous.dateFrom,
        date_to: previous.dateTo,
      }),
    ]);

    setPreviousSalesReport(salesRes.data || null);
    setPreviousProfitLoss(profitRes.data || null);
    setPreviousFinancialOverview(financialRes.data || null);
  };

  const loadHomologueComparisonData = async () => {
    const currentFrom = dateFrom;
    const currentTo = dateTo;
    const previous = getHomologuePeriod(dateFrom, dateTo);

    const [
      currentSalesRes,
      currentProfitRes,
      currentFinancialRes,
      currentTaxRes,
      currentPurchasesRes,
      currentPurchasesDashboardRes,
      currentProvidersRes,
      previousSalesRes,
      previousProfitRes,
      previousFinancialRes,
      previousTaxRes,
      previousPurchasesRes,
      previousPurchasesDashboardRes,
      previousProvidersRes,
    ] = await Promise.all([
      api.get('/reports-export/sales-summary', {
        params: { start_date: currentFrom, end_date: currentTo }
      }),
      api.get('/reports-export/profit-loss', {
        params: { date_from: currentFrom, date_to: currentTo }
      }),
      reportsAPI.getFinancialOverview({
        date_from: currentFrom,
        date_to: currentTo,
      }),
      reportsAPI.getTaxOverview({
        date_from: currentFrom,
        date_to: currentTo,
      }),
      api.get('/invoices', {
        params: {}
      }),
      api.get('/invoices/dashboard'),
      reportsAPI.getProvidersFinancial({
        start_date: currentFrom,
        end_date: currentTo,
      }),

      api.get('/reports-export/sales-summary', {
        params: { start_date: previous.dateFrom, end_date: previous.dateTo }
      }),
      api.get('/reports-export/profit-loss', {
        params: { date_from: previous.dateFrom, date_to: previous.dateTo }
      }),
      reportsAPI.getFinancialOverview({
        date_from: previous.dateFrom,
        date_to: previous.dateTo,
      }),
      reportsAPI.getTaxOverview({
        date_from: previous.dateFrom,
        date_to: previous.dateTo,
      }),
      api.get('/invoices', {
        params: {}
      }),
      api.get('/invoices/dashboard'),
      reportsAPI.getProvidersFinancial({
        start_date: previous.dateFrom,
        end_date: previous.dateTo,
      }),
    ]);

    const currentPurchasesRows = Array.isArray(currentPurchasesRes?.data)
      ? currentPurchasesRes.data.filter((item) => {
          const d = String(item?.invoice_date || '').slice(0, 10);
          return d >= currentFrom && d <= currentTo;
        })
      : [];

    const previousPurchasesRows = Array.isArray(previousPurchasesRes?.data)
      ? previousPurchasesRes.data.filter((item) => {
          const d = String(item?.invoice_date || '').slice(0, 10);
          return d >= previous.dateFrom && d <= previous.dateTo;
        })
      : [];

    setHomologueCurrent({
      sales: currentSalesRes?.data || null,
      profit: currentProfitRes?.data || null,
      financial: currentFinancialRes?.data || null,
      tax: currentTaxRes?.data || null,
      purchases: currentPurchasesRows,
      purchasesDashboard: currentPurchasesDashboardRes?.data || null,
      providers: currentProvidersRes?.data || null,
    });

    setHomologuePrevious({
      sales: previousSalesRes?.data || null,
      profit: previousProfitRes?.data || null,
      financial: previousFinancialRes?.data || null,
      tax: previousTaxRes?.data || null,
      purchases: previousPurchasesRows,
      purchasesDashboard: previousPurchasesDashboardRes?.data || null,
      providers: previousProvidersRes?.data || null,
    });
  };

  const loadYearComparisonData = async () => {
    const currentYear = String(comparisonYear || new Date().getFullYear());
    const previousYear = String(Number(currentYear) - 1);

    const currentFrom = `${currentYear}-01-01`;
    const currentTo = `${currentYear}-12-31`;
    const previousFrom = `${previousYear}-01-01`;
    const previousTo = `${previousYear}-12-31`;

    const [
      currentSalesRes,
      currentProfitRes,
      currentFinancialRes,
      currentTaxRes,
      currentPurchasesRes,
      currentPurchasesDashboardRes,
      currentProvidersRes,
      previousSalesRes,
      previousProfitRes,
      previousFinancialRes,
      previousTaxRes,
      previousPurchasesRes,
      previousPurchasesDashboardRes,
      previousProvidersRes,
    ] = await Promise.all([
      api.get('/reports-export/sales-summary', {
        params: { start_date: currentFrom, end_date: currentTo }
      }),
      api.get('/reports-export/profit-loss', {
        params: { date_from: currentFrom, date_to: currentTo }
      }),
      reportsAPI.getFinancialOverview({
        date_from: currentFrom,
        date_to: currentTo,
      }),
      reportsAPI.getTaxOverview({
        date_from: currentFrom,
        date_to: currentTo,
      }),
      api.get('/invoices', { params: {} }),
      api.get('/invoices/dashboard'),
      reportsAPI.getProvidersFinancial({
        start_date: currentFrom,
        end_date: currentTo,
      }),
      api.get('/reports-export/sales-summary', {
        params: { start_date: previousFrom, end_date: previousTo }
      }),
      api.get('/reports-export/profit-loss', {
        params: { date_from: previousFrom, date_to: previousTo }
      }),
      reportsAPI.getFinancialOverview({
        date_from: previousFrom,
        date_to: previousTo,
      }),
      reportsAPI.getTaxOverview({
        date_from: previousFrom,
        date_to: previousTo,
      }),
      api.get('/invoices', { params: {} }),
      api.get('/invoices/dashboard'),
      reportsAPI.getProvidersFinancial({
        start_date: previousFrom,
        end_date: previousTo,
      }),
    ]);

    const currentPurchasesRows = Array.isArray(currentPurchasesRes?.data)
      ? currentPurchasesRes.data.filter((item) => {
          const d = String(item?.invoice_date || '').slice(0, 10);
          return d >= currentFrom && d <= currentTo;
        })
      : [];

    const previousPurchasesRows = Array.isArray(previousPurchasesRes?.data)
      ? previousPurchasesRes.data.filter((item) => {
          const d = String(item?.invoice_date || '').slice(0, 10);
          return d >= previousFrom && d <= previousTo;
        })
      : [];

    setComparisonCurrent({
      sales: currentSalesRes.data || null,
      profit: currentProfitRes.data || null,
      financial: currentFinancialRes.data || null,
      tax: currentTaxRes.data || null,
      purchases: currentPurchasesRows,
      purchasesDashboard: currentPurchasesDashboardRes.data || null,
      providers: currentProvidersRes.data || null,
    });

    setComparisonPrevious({
      sales: previousSalesRes.data || null,
      profit: previousProfitRes.data || null,
      financial: previousFinancialRes.data || null,
      tax: previousTaxRes.data || null,
      purchases: previousPurchasesRows,
      purchasesDashboard: previousPurchasesDashboardRes.data || null,
      providers: previousProvidersRes.data || null,
    });
  };

  const handleRebuildPrepaymentsLedger = async () => {
    try {
      setRebuildingPrepayments(true);
      const res = await reportsAPI.rebuildPrepaymentsLedger();
      toast.success(res?.data?.message || 'Ledger reconstruído com sucesso');
      await loadPrepaymentsReport();
    } catch (error) {
      console.error('Erro ao reconstruir ledger de pré-pagamentos:', error);
      toast.error(error?.response?.data?.detail || 'Erro ao reconstruir ledger');
    } finally {
      setRebuildingPrepayments(false);
    }
  };


  const normalizeExportType = (value) => {
    if (value && typeof value === 'object' && value.type) return exportType || activeTab || 'sales';

    const v = String(value || '').trim().toLowerCase();

    if (!v) return exportType || activeTab || 'sales';
    if (['sales', 'vendas'].includes(v)) return 'sales';
    if (['expenses', 'despesas'].includes(v)) return 'expenses';
    if (['providers', 'prestadores'].includes(v)) return 'providers';
    if (['comparison', 'comparacao', 'comparação'].includes(v)) return 'comparison';
    if (['expiry', 'expiring', 'validades', 'validade'].includes(v)) return 'expiry';
    if (['financial', 'estado-financeiro', 'estado financeiro'].includes(v)) return 'financial';
    if (['profit', 'lucro', 'lucro/prejuizo', 'lucro-prejuizo', 'lucroprejuizo'].includes(v)) return 'profit';
    if (['tax', 'fiscal', 'iva', 'fiscal/iva', 'fiscal-iva'].includes(v)) return 'tax';

    return v;
  };

  const downloadCSV = async (reportType = exportType) => {
    try {
      const selectedType = normalizeExportType(reportType || exportType || activeTab);

      const downloadTextFile = (content, filename, mime = 'text/csv;charset=utf-8;') => {
        const blob = new Blob([content], { type: mime });
        const urlObj = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = urlObj;
        a.download = filename;
        a.click();
        window.URL.revokeObjectURL(urlObj);
      };

      const csvCell = (value) => {
        if (value === null || value === undefined) return '';
        const s = String(value).replace(/"/g, '""');
        return `"${s}"`;
      };

      const rowsToCsv = (rows) => rows.map((row) => row.map(csvCell).join(';')).join('\n');

      switch (selectedType) {
        case 'sales': {
          const response = await api.get('/reports-export/sales-csv', {
            params: {
              start_date: dateFrom,
              end_date: dateTo,
              date_from: dateFrom,
              date_to: dateTo,
            },
            responseType: 'blob',
          });

          const urlObj = window.URL.createObjectURL(new Blob([response.data], { type: 'text/csv;charset=utf-8;' }));
          const a = document.createElement('a');
          a.href = urlObj;
          a.download = `vendas_${dateFrom}_${dateTo}.csv`;
          a.click();
          window.URL.revokeObjectURL(urlObj);
          toast.success('CSV de vendas exportado');
          return;
        }

        case 'expenses': {
          const response = await api.get('/reports-export/expenses-csv', {
            params: {
              start_date: dateFrom,
              end_date: dateTo,
              date_from: dateFrom,
              date_to: dateTo,
            },
            responseType: 'blob',
          });

          const urlObj = window.URL.createObjectURL(new Blob([response.data], { type: 'text/csv;charset=utf-8;' }));
          const a = document.createElement('a');
          a.href = urlObj;
          a.download = `despesas_${dateFrom}_${dateTo}.csv`;
          a.click();
          window.URL.revokeObjectURL(urlObj);
          toast.success('CSV de despesas exportado');
          return;
        }

        case 'providers': {
          const params = {
            date_from: dateFrom,
            date_to: dateTo,
            start_date: dateFrom,
            end_date: dateTo,
          };

          if (providerFilter && providerFilter !== 'all') {
            params.provider_id = providerFilter;
          }

          const response = await reportsAPI.exportProvidersFinancial(params);

          const urlObj = window.URL.createObjectURL(
            new Blob([response.data], { type: 'text/csv;charset=utf-8;' })
          );
          const a = document.createElement('a');
          a.href = urlObj;
          a.download = `prestadores_${dateFrom}_${dateTo}.csv`;
          a.click();
          window.URL.revokeObjectURL(urlObj);
          toast.success('CSV de prestadores exportado');
          return;
        }

        case 'comparison': {
          const currentYear = String(comparisonYear || new Date().getFullYear());
          const previousYear = String(Number(currentYear) - 1);
          const homologue = getHomologuePeriod(dateFrom, dateTo);

          const annualRows = [
            ['COMPARAÇÃO ANUAL'],
            ['Indicador', currentYear, previousYear, 'Diferença', 'Variação %'],
            ['Vendas', comparisonData.sales.current, comparisonData.sales.previous, comparisonData.sales.diff, comparisonData.sales.percent],
            ['Internas (NCP)', comparisonData.internalSales.current, comparisonData.internalSales.previous, comparisonData.internalSales.diff, comparisonData.internalSales.percent],
            ['Fiscais diretas POS', comparisonData.fiscalSales.current, comparisonData.fiscalSales.previous, comparisonData.fiscalSales.diff, comparisonData.fiscalSales.percent],
            ['Ticket médio', comparisonData.averageSale.current, comparisonData.averageSale.previous, comparisonData.averageSale.diff, comparisonData.averageSale.percent],
            ['Lucro líquido', comparisonData.netProfit.current, comparisonData.netProfit.previous, comparisonData.netProfit.diff, comparisonData.netProfit.percent],
            ['Recebimentos', comparisonData.receipts.current, comparisonData.receipts.previous, comparisonData.receipts.diff, comparisonData.receipts.percent],
            ['Compras', comparisonData.purchases.current, comparisonData.purchases.previous, comparisonData.purchases.diff, comparisonData.purchases.percent],
            ['Compras pendentes', comparisonData.purchasesPending.current, comparisonData.purchasesPending.previous, comparisonData.purchasesPending.diff, comparisonData.purchasesPending.percent],
            ['Clientes', comparisonData.customers.current, comparisonData.customers.previous, comparisonData.customers.diff, comparisonData.customers.percent],
            ['Categorias', comparisonData.categories.current, comparisonData.categories.previous, comparisonData.categories.diff, comparisonData.categories.percent],
            ['Métodos pagamento', comparisonData.paymentMethods.current, comparisonData.paymentMethods.previous, comparisonData.paymentMethods.diff, comparisonData.paymentMethods.percent],
            ['Prestadores', comparisonData.providers.current, comparisonData.providers.previous, comparisonData.providers.diff, comparisonData.providers.percent],
            ['Prestadores em aberto', comparisonData.providersOpen.current, comparisonData.providersOpen.previous, comparisonData.providersOpen.diff, comparisonData.providersOpen.percent],
            [],
            ['Top cliente anual', comparisonData.topCustomer.currentName, comparisonData.topCustomer.currentTotal],
            ['Top categoria anual', comparisonData.topCategory.currentName, comparisonData.topCategory.currentTotal],
          ];

          const homologueRows = [
            ['COMPARAÇÃO HOMÓLOGA'],
            ['Período atual', dateFrom, dateTo],
            ['Período homólogo', homologue.dateFrom, homologue.dateTo],
            ['Indicador', 'Atual', 'Homólogo', 'Diferença', 'Variação %'],
            ['Vendas', homologueData.sales.current, homologueData.sales.previous, homologueData.sales.diff, homologueData.sales.percent],
            ['Internas (NCP)', homologueData.internalSales.current, homologueData.internalSales.previous, homologueData.internalSales.diff, homologueData.internalSales.percent],
            ['Fiscais diretas POS', homologueData.fiscalSales.current, homologueData.fiscalSales.previous, homologueData.fiscalSales.diff, homologueData.fiscalSales.percent],
            ['Ticket médio', homologueData.averageSale.current, homologueData.averageSale.previous, homologueData.averageSale.diff, homologueData.averageSale.percent],
            ['Lucro líquido', homologueData.netProfit.current, homologueData.netProfit.previous, homologueData.netProfit.diff, homologueData.netProfit.percent],
            ['Recebimentos', homologueData.receipts.current, homologueData.receipts.previous, homologueData.receipts.diff, homologueData.receipts.percent],
            ['Compras', homologueData.purchases.current, homologueData.purchases.previous, homologueData.purchases.diff, homologueData.purchases.percent],
            ['Compras pendentes', homologueData.purchasesPending.current, homologueData.purchasesPending.previous, homologueData.purchasesPending.diff, homologueData.purchasesPending.percent],
            ['Clientes', homologueData.customers.current, homologueData.customers.previous, homologueData.customers.diff, homologueData.customers.percent],
            ['Categorias', homologueData.categories.current, homologueData.categories.previous, homologueData.categories.diff, homologueData.categories.percent],
            ['Métodos pagamento', homologueData.paymentMethods.current, homologueData.paymentMethods.previous, homologueData.paymentMethods.diff, homologueData.paymentMethods.percent],
            ['Prestadores', homologueData.providers.current, homologueData.providers.previous, homologueData.providers.diff, homologueData.providers.percent],
          ];

          downloadTextFile(
            rowsToCsv([...annualRows, [], ...homologueRows]),
            `comparacao_global_${currentYear}_vs_${previousYear}.csv`
          );
          toast.success('CSV global da comparação exportado');
          return;
        }

        case 'expiry': {
          const response = await api.get('/reports-export/expiry-risk-csv', {
            responseType: 'blob',
          });

          const urlObj = window.URL.createObjectURL(new Blob([response.data], { type: 'text/csv;charset=utf-8;' }));
          const a = document.createElement('a');
          a.href = urlObj;
          a.download = 'validades_risco_90dias.csv';
          a.click();
          window.URL.revokeObjectURL(urlObj);
          toast.success('CSV de validades exportado');
          return;
        }

        default:
          toast.error('Tipo de exportação CSV não suportado');
          return;
      }
    } catch (error) {
      console.error('Erro ao exportar CSV:', error);
      toast.error('Erro ao exportar CSV');
    }
  };

  const downloadPDF = async (reportType = exportType) => {
    try {
      const selectedType = normalizeExportType(reportType || exportType || activeTab);

      const openPrintHtml = (html, okMessage) => {
        const printWindow = window.open('', '_blank');
        if (!printWindow) {
          toast.error('Não foi possível abrir a janela de impressão');
          return;
        }

        printWindow.document.open();
        printWindow.document.write(html);
        printWindow.document.close();
        printWindow.focus();

        setTimeout(() => {
          printWindow.print();
        }, 300);

        toast.success(okMessage);
      };

      let request = null;
      let filename = `relatorio_${selectedType}_${dateFrom}_${dateTo}.pdf`;

      switch (selectedType) {
        case 'sales':
          request = reportsAPI.exportSalesSummaryPDF({
            start_date: dateFrom,
            end_date: dateTo,
          });
          filename = `relatorio_vendas_${dateFrom}_${dateTo}.pdf`;
          break;

        case 'financial':
          request = reportsAPI.exportFinancialOverviewPDF({
            date_from: dateFrom,
            date_to: dateTo,
          });
          filename = `estado_financeiro_${dateFrom}_${dateTo}.pdf`;
          break;

        case 'profit':
          request = reportsAPI.exportProfitLossPDF({
            date_from: dateFrom,
            date_to: dateTo,
          });
          filename = `lucro_prejuizo_${dateFrom}_${dateTo}.pdf`;
          break;

        case 'tax':
          if (reportsAPI.exportTaxOverviewPDF) {
            request = reportsAPI.exportTaxOverviewPDF({
              date_from: dateFrom,
              date_to: dateTo,
            });
            filename = `fiscal_iva_${dateFrom}_${dateTo}.pdf`;
          } else {
            toast.error('PDF fiscal/IVA ainda não está disponível');
            return;
          }
          break;

        case 'expiry':
          request = reportsAPI.exportExpiryRiskPDF(90);
          filename = 'validades_risco_90dias.pdf';
          break;

        case 'providers': {
          request = reportsAPI.exportProvidersFinancialPDF({
            date_from: dateFrom,
            date_to: dateTo,
            ...(providerFilter && providerFilter !== 'all'
              ? { provider_id: providerFilter }
              : {}),
          });
          filename = `prestadores_${dateFrom}_${dateTo}.pdf`;
          break;
        }

        case 'comparison': {
          const currentYear = String(comparisonYear || new Date().getFullYear());
          const previousYear = String(Number(currentYear) - 1);
          const homologue = getHomologuePeriod(dateFrom, dateTo);

          const renderRows = (rows, leftA, leftB) => rows.map(([label, row]) => {
            const percent = row?.percent === null || row?.percent === undefined ? 'n/d' : `${Number(row.percent).toFixed(1)}%`;
            return `
              <tr>
                <td>${label}</td>
                <td>${typeof row?.current === 'number' ? row.current.toFixed(2) : row?.current ?? ''}</td>
                <td>${typeof row?.previous === 'number' ? row.previous.toFixed(2) : row?.previous ?? ''}</td>
                <td>${typeof row?.diff === 'number' ? row.diff.toFixed(2) : row?.diff ?? ''}</td>
                <td>${percent}</td>
              </tr>
            `;
          }).join('');

          const annualRows = [
            ['Vendas', comparisonData.sales],
            ['Internas (NCP)', comparisonData.internalSales],
            ['Fiscais diretas POS', comparisonData.fiscalSales],
            ['Ticket médio', comparisonData.averageSale],
            ['Lucro líquido', comparisonData.netProfit],
            ['Recebimentos', comparisonData.receipts],
            ['Compras', comparisonData.purchases],
            ['Compras pendentes', comparisonData.purchasesPending],
            ['Clientes', comparisonData.customers],
            ['Categorias', comparisonData.categories],
            ['Métodos pagamento', comparisonData.paymentMethods],
            ['Prestadores', comparisonData.providers],
            ['Prestadores em aberto', comparisonData.providersOpen],
          ];

          const homologueRows = [
            ['Vendas', homologueData.sales],
            ['Internas (NCP)', homologueData.internalSales],
            ['Fiscais diretas POS', homologueData.fiscalSales],
            ['Ticket médio', homologueData.averageSale],
            ['Lucro líquido', homologueData.netProfit],
            ['Recebimentos', homologueData.receipts],
            ['Compras', homologueData.purchases],
            ['Compras pendentes', homologueData.purchasesPending],
            ['Clientes', homologueData.customers],
            ['Categorias', homologueData.categories],
            ['Métodos pagamento', homologueData.paymentMethods],
            ['Prestadores', homologueData.providers],
          ];

          const html = `
            <html>
              <head>
                <meta charset="utf-8" />
                <title>Comparação Global</title>
                <style>
                  body { font-family: Arial, sans-serif; padding: 24px; color: #111; }
                  h1, h2 { margin: 0 0 12px; }
                  h2 { margin-top: 28px; }
                  p { margin: 8px 0 16px; }
                  table { width: 100%; border-collapse: collapse; margin-top: 10px; }
                  th, td { border: 1px solid #ccc; padding: 8px; font-size: 12px; text-align: left; }
                  th { background: #f3f4f6; }
                  .meta { margin-top: 6px; color: #555; font-size: 12px; }
                  .box { border: 1px solid #ddd; padding: 12px; margin-top: 16px; }
                </style>
              </head>
              <body>
                <h1>Comparação Global</h1>
                <p class="meta">Base contabilística: ano civil e período homólogo.</p>

                <h2>Comparação anual ${currentYear} vs ${previousYear}</h2>
                <table>
                  <thead>
                    <tr>
                      <th>Indicador</th>
                      <th>${currentYear}</th>
                      <th>${previousYear}</th>
                      <th>Diferença</th>
                      <th>Variação %</th>
                    </tr>
                  </thead>
                  <tbody>
                    ${renderRows(annualRows, currentYear, previousYear)}
                  </tbody>
                </table>

                <h2>Comparação homóloga</h2>
                <p class="meta">${dateFrom} a ${dateTo} vs ${homologue.dateFrom} a ${homologue.dateTo}</p>
                <table>
                  <thead>
                    <tr>
                      <th>Indicador</th>
                      <th>Atual</th>
                      <th>Homólogo</th>
                      <th>Diferença</th>
                      <th>Variação %</th>
                    </tr>
                  </thead>
                  <tbody>
                    ${renderRows(homologueRows, 'Atual', 'Homólogo')}
                  </tbody>
                </table>

                <div class="box">
                  <h2>Topos</h2>
                  <p><strong>Top cliente anual:</strong> ${comparisonData.topCustomer.currentName} (${formatCurrency(comparisonData.topCustomer.currentTotal)})</p>
                  <p><strong>Top categoria anual:</strong> ${comparisonData.topCategory.currentName} (${formatCurrency(comparisonData.topCategory.currentTotal)})</p>
                </div>
              </body>
            </html>
          `;

          openPrintHtml(html, 'PDF global da comparação preparado para impressão');
          return;
        }

        case 'expenses':
          request = api.get('/reports-export/expenses-pdf', {
            params: {
              start_date: dateFrom,
              end_date: dateTo,
              date_from: dateFrom,
              date_to: dateTo,
            },
            responseType: 'blob',
          });
          filename = `despesas_${dateFrom}_${dateTo}.pdf`;
          break;

        default:
          toast.error('Este tipo de exportação PDF ainda não está disponível');
          return;
      }

      if (!request) {
        toast.error('Não foi possível preparar o PDF');
        return;
      }

      const response = await request;
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const urlObj = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = urlObj;
      a.download = filename;
      a.click();
      window.URL.revokeObjectURL(urlObj);
      toast.success('PDF exportado');
    } catch (error) {
      console.error('Erro ao exportar PDF:', error);
      toast.error('Erro ao exportar PDF');
    }
  };

  const expiryCategories = useMemo(() => {
    return [...new Set(
      (expiringProducts || [])
        .map((item) => String(item?.category || 'SEM CATEGORIA').trim().toUpperCase())
        .filter(Boolean)
    )].sort((a, b) => a.localeCompare(b, 'pt'));
  }, [expiringProducts]);

  const priorityOrder = useMemo(() => ({
    expired: 0,
    very_high: 1,
    high: 2,
    medium: 3,
    low: 4,
  }), []);

  const filteredExpiringProducts = useMemo(() => {
    return [...(expiringProducts || [])]
      .filter((item) => {
        const category = String(item?.category || 'SEM CATEGORIA').trim().toUpperCase();
        if (expiryCategoryFilter === 'all') return true;
        return category === expiryCategoryFilter;
      })
      .filter((item) => {
        if (expiryPriorityFilter === 'all') return true;
        return String(item?.campaign_priority || '') === expiryPriorityFilter;
      })
      .filter((item) => {
        if (!expiryOnlyActionable) return true;
        return (
          Number(item?.suggested_discount_percent || 0) > 0 ||
          ['very_high', 'high', 'medium'].includes(String(item?.campaign_priority || ''))
        );
      })
      .sort((a, b) => {
        const daysA = Number(a?.days_remaining ?? 999999);
        const daysB = Number(b?.days_remaining ?? 999999);
        if (daysA !== daysB) return daysA - daysB;

        const prioA = priorityOrder[String(a?.campaign_priority || '')] ?? 99;
        const prioB = priorityOrder[String(b?.campaign_priority || '')] ?? 99;
        if (prioA !== prioB) return prioA - prioB;

        return Number(b?.cost_risk || 0) - Number(a?.cost_risk || 0);
      });
  }, [expiringProducts, expiryCategoryFilter, expiryPriorityFilter, expiryOnlyActionable, priorityOrder]);


  const refreshExpiryOnly = async () => {
    const response = await reportsAPI.getExpiryRisk(90);
    const data = response.data || {};
    setExpiryReport(data);
    setExpiringProducts(Array.isArray(data.products) ? data.products : []);
  };

  const setExpiryAction = async (item, action) => {
    try {
      await reportsAPI.setExpiryRiskAction({
        product_id: item.product_id,
        lot_number: item.lot_number,
        action,
      });
      await refreshExpiryOnly();
      toast.success('Estado comercial atualizado');
    } catch (error) {
      console.error('Erro ao atualizar estado comercial:', error);
      toast.error('Erro ao atualizar estado comercial');
    }
  };

  const clearExpiryAction = async (item) => {
    try {
      await reportsAPI.clearExpiryRiskAction(item.product_id, item.lot_number);
      await refreshExpiryOnly();
      toast.success('Estado comercial limpo');
    } catch (error) {
      console.error('Erro ao limpar estado comercial:', error);
      toast.error('Erro ao limpar estado comercial');
    }
  };

  const buildExpiryCampaignDefaults = (item) => {
    const today = new Date().toISOString().split('T')[0];
    const category = String(item?.category || '').trim().toUpperCase();
    const discount = Number(item?.suggested_discount_percent || 10) > 0
      ? Number(item?.suggested_discount_percent || 10)
      : 10;

    return {
      name: `Campanha anti-validade ${item?.product_name || ''}`.trim(),
      description: `Campanha criada a partir do relatório de validades para o lote ${item?.lot_number || '-'}.`,
      campaign_type: 'percentage',
      discount_value: discount,
      start_date: today,
      end_date: item?.expiry_date || today,
      min_purchase: 0,
      applicable_categories: category && category !== 'SEM CATEGORIA' ? [category] : [],
      excluded_categories: ['MEDICINA'],
      auto_generate_vouchers: true,
      voucher_prefix: 'EXP',
    };
  };

  const openExpiryCampaignModal = (item) => {
    setSelectedExpiryItem(item);
    setExpiryCampaignForm(buildExpiryCampaignDefaults(item));
    setShowExpiryCampaignModal(true);
  };

  const createExpiryCampaign = async () => {
    if (!expiryCampaignForm.name || !expiryCampaignForm.end_date) {
      toast.error('Preenche nome e data de fim');
      return;
    }

    setCreatingExpiryCampaign(true);
    try {
      await campaignsAPI.create(expiryCampaignForm);
      toast.success('Campanha criada');
      setShowExpiryCampaignModal(false);
      setSelectedExpiryItem(null);
      await loadExpiringProducts();
    } catch (error) {
      console.error('Erro ao criar campanha de validade:', error);
      toast.error(error?.response?.data?.detail || 'Erro ao criar campanha');
    } finally {
      setCreatingExpiryCampaign(false);
    }
  };

  const downloadExpiryCSV = async () => {
    try {
      const response = await reportsAPI.exportExpiryRiskCSV(90);
      const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
      const urlObj = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = urlObj;
      a.download = `validades_${dateFrom}_${dateTo}.csv`;
      a.click();
      window.URL.revokeObjectURL(urlObj);
      toast.success('CSV exportado');
    } catch (error) {
      console.error('Erro ao exportar CSV de validades:', error);
      toast.error('Erro ao exportar CSV');
    }
  };

  const exportExpiryPDF = () => {
    const rows = filteredExpiringProducts || [];

    const esc = (value) =>
      String(value ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');

    const htmlRows = rows.map((item) => `
      <tr>
        <td>${esc(item.product_name)}</td>
        <td>${esc(item.category || '-')}</td>
        <td>${esc(item.lot_number || '-')}</td>
        <td>${esc(item.expiry_date || '-')}</td>
        <td style="text-align:right;">${esc(item.days_remaining)}</td>
        <td style="text-align:right;">${esc(item.quantity)}</td>
        <td style="text-align:right;">${esc(item.sold_last_30 || 0)}</td>
        <td>${esc(item.rotation_status || '-')}</td>
        <td>${esc(item.campaign_priority || '-')}</td>
        <td style="text-align:right;">${esc(formatCurrency(item.cost_risk || 0))}</td>
        <td style="text-align:right;">${esc(item.suggested_discount_percent ? `${item.suggested_discount_percent}%` : '-')}</td>
        <td>${esc(item.campaign_suggestion || '-')}</td>
      </tr>
    `).join('');

    const win = window.open('', '_blank', 'width=1400,height=900');
    if (!win) {
      toast.error('O navegador bloqueou a janela de impressão');
      return;
    }

    win.document.write(`
      <html>
        <head>
          <title>Relatório de Validades</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 24px; color: #111; }
            h1 { margin: 0 0 8px 0; }
            .muted { color: #555; margin-bottom: 16px; }
            .cards { display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; margin-bottom: 18px; }
            .card { border: 1px solid #ddd; border-radius: 10px; padding: 12px; }
            .label { font-size: 12px; color: #666; margin-bottom: 6px; }
            .value { font-size: 20px; font-weight: 700; }
            table { width: 100%; border-collapse: collapse; margin-top: 16px; }
            th, td { border: 1px solid #ddd; padding: 8px; font-size: 12px; vertical-align: top; }
            th { background: #f5f5f5; text-align: left; }
            @media print {
              body { padding: 0; }
              .cards { break-inside: avoid; }
            }
          </style>
        </head>
        <body>
          <h1>Relatório de Validades</h1>
          <div class="muted">Gerado em ${esc(new Date().toLocaleString('pt-PT'))}</div>

          <div class="cards">
            <div class="card"><div class="label">Expirados</div><div class="value">${esc(expiryReport?.summary?.expired_count || 0)}</div></div>
            <div class="card"><div class="label">Críticos até 30 dias</div><div class="value">${esc(expiryReport?.summary?.critical_30_count || 0)}</div></div>
            <div class="card"><div class="label">Valor custo em risco</div><div class="value">${esc(formatCurrency(expiryReport?.summary?.total_cost_at_risk || 0))}</div></div>
            <div class="card"><div class="label">Itens filtrados</div><div class="value">${esc(rows.length)}</div></div>
          </div>

          <table>
            <thead>
              <tr>
                <th>Produto</th>
                <th>Categoria</th>
                <th>Lote</th>
                <th>Validade</th>
                <th>Dias</th>
                <th>Qtd.</th>
                <th>Vend. 30d</th>
                <th>Rotação</th>
                <th>Prioridade</th>
                <th>Risco custo</th>
                <th>Desc.</th>
                <th>Sugestão</th>
              </tr>
            </thead>
            <tbody>
              ${htmlRows || '<tr><td colspan="12">Sem dados</td></tr>'}
            </tbody>
          </table>
        </body>
      </html>
    `);
    win.document.close();
    win.focus();
    win.print();
  };

  if (loading && !salesReport && !stockReport) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="reports-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white font-['Manrope']">Relatórios</h1>
          <p className="text-slate-400">Dashboard de relatórios e análises</p>

      

        </div>
        <Button
          variant="outline"
          onClick={loadReports}
          disabled={loading}
          className="border-slate-700"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Atualizar
        </Button>
      </div>

      {/* ALERTA VALIDADE */}
      <div className="bg-slate-900 border border-slate-700 rounded-xl p-4">
        <h3 className="text-white font-semibold mb-2">Validade de Produtos</h3>

        {expiryStats.expired > 0 && (
          <p className="text-red-400">🚨 {expiryStats.expired} produto(s) expirado(s)</p>
        )}

        {expiryStats.critical > 0 && (
          <p className="text-orange-400">⚠️ {expiryStats.critical} produto(s) críticos (≤30 dias)</p>
        )}

        {expiryStats.expired === 0 && expiryStats.critical === 0 && (
          <p className="text-green-400">✔️ Tudo OK</p>
        )}
      </div>


      {/* Date Filters */}
      <Card className="card-dark p-4">
        <div className="flex flex-wrap items-end gap-4">
          <div>
            <Label className="text-slate-400 text-xs">Período</Label>
            <select value={periodMode} onChange={(e) => setPeriodMode(e.target.value)} className="block bg-slate-800 border border-slate-700 text-slate-100 rounded-md h-9 px-3 w-44">
              <option value="month">Mês</option>
              <option value="custom">Personalizado</option>
            </select>
          </div>
          {periodMode === 'month' ? (
            <div>
              <Label className="text-slate-400 text-xs">Mês a consultar</Label>
              <Input type="month" value={selectedMonth} onChange={(e) => setSelectedMonth(e.target.value)} className="bg-slate-800 border-slate-700 w-44" />
            </div>
          ) : (
            <>
              <div>
                <Label className="text-slate-400 text-xs">Data Início</Label>
                <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="bg-slate-800 border-slate-700 w-40" />
              </div>
              <div>
                <Label className="text-slate-400 text-xs">Data Fim</Label>
                <Input type="date" value={dateTo} min={dateFrom} onChange={(e) => setDateTo(e.target.value)} className="bg-slate-800 border-slate-700 w-40" />
              </div>
            </>
          )}
          <div className="pb-2 text-xs text-slate-400">
            A mostrar <strong className="text-slate-200">{formatDate(dateFrom)}</strong> a <strong className="text-slate-200">{formatDate(dateTo)}</strong>
          </div>
          {activeTab === 'providers' && (
            <div>
              <Label className="text-slate-400 text-xs">Prestador</Label>
              <select
                value={providerFilter}
                onChange={(e) => setProviderFilter(e.target.value)}
                className="bg-slate-800 border border-slate-700 text-slate-100 rounded-md h-9 px-3 w-64"
              >
                <option value="all">Todos os prestadores</option>
                {providersList.map((provider) => (
                  <option key={provider.id} value={provider.id}>
                    {provider.name}{provider.specialty ? ` · ${provider.specialty}` : ''}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="flex items-end gap-2">
            <div>
              <Label className="text-slate-400 text-xs">Exportação CSV</Label>
              <select
                value={exportType}
                onChange={(e) => setExportType(e.target.value)}
                className="bg-slate-800 border border-slate-700 text-slate-100 rounded-md h-9 px-3 w-52"
              >
                <option value="sales">Vendas</option>
                <option value="expenses">Despesas</option>
                <option value="providers">Prestadores</option>
                <option value="comparison">Comparação</option>
                <option value="expiry">Validades</option>
              </select>
            </div>

            <Button
              variant="outline"
              size="sm"
              onClick={() => downloadCSV(exportType)}
              className="border-slate-700"
            >
              <FileSpreadsheet className="w-4 h-4 mr-1" />
              Exportar CSV
            </Button>

            <Button
              variant="outline"
              size="sm"
              onClick={() => downloadPDF(exportType)}
              className="border-slate-700"
            >
              <FileText className="w-4 h-4 mr-1" />
              Exportar PDF
            </Button>
          </div>
        </div>
      </Card>

      {/* Tabs */}
              <Card className="card-dark">
          <CardContent className="pt-6">
            <div className="flex flex-wrap gap-2">
              {[
                ['management', 'Gestão'],
                ['sales', 'Vendas'],
                ['cash-closes', 'Fechos de Caixa'],
                ['stock', 'Stock'],
                ['expiring', 'Validades'],
                ['profit', 'Lucro/Prejuízo'],
                ['financial', 'Estado Financeiro'],
                ['tax', 'Fiscal / IVA'],
                ['comparison', 'Comparação'],
                ['purchases', 'Compras'],
                ['providers', 'Prestadores'],
                ['categories', 'Categorias'],
                ['clients', 'Clientes'],
                ['prepayments', 'Pré-pagamentos'],
              ].map(([value, label]) => (
                <Button
                  key={value}
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setActiveTab(value)}
                  className={`border-slate-700 ${
                    activeTab === value
                      ? 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30'
                      : 'text-slate-300 hover:text-white hover:bg-slate-800'
                  }`}
                >
                  {label}
                </Button>
              ))}
            </div>
          </CardContent>
        </Card>

<Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="hidden">
          <TabsTrigger value="management" className="h-9 px-3 flex-shrink-0 whitespace-nowrap rounded-md data-[state=active]:bg-emerald-500/20 data-[state=active]:text-emerald-300">
            <BarChart3 className="w-4 h-4 mr-2" />
            Gestão
          </TabsTrigger>
          <TabsTrigger value="sales" className="h-9 px-3 flex-shrink-0 whitespace-nowrap rounded-md data-[state=active]:bg-emerald-500/20 data-[state=active]:text-emerald-300">
            <ShoppingCart className="w-4 h-4 mr-2" />
            Vendas
          </TabsTrigger>
          <TabsTrigger value="cash-closes" className="h-9 px-3 flex-shrink-0 whitespace-nowrap rounded-md data-[state=active]:bg-emerald-500/20 data-[state=active]:text-emerald-300">
            <Landmark className="w-4 h-4 mr-2" />
            Fechos de Caixa
          </TabsTrigger>
          <TabsTrigger value="stock" className="h-9 px-3 flex-shrink-0 whitespace-nowrap rounded-md data-[state=active]:bg-emerald-500/20 data-[state=active]:text-emerald-300">
            <Package className="w-4 h-4 mr-2" />
            Stock
          </TabsTrigger>
          <TabsTrigger value="expiring" className="h-9 px-3 flex-shrink-0 whitespace-nowrap rounded-md data-[state=active]:bg-emerald-500/20 data-[state=active]:text-emerald-300">
            <AlertTriangle className="w-4 h-4 mr-2" />
            Validades
          </TabsTrigger>
          <TabsTrigger value="profit" className="h-9 px-3 flex-shrink-0 whitespace-nowrap rounded-md data-[state=active]:bg-emerald-500/20 data-[state=active]:text-emerald-300">
            <TrendingUp className="w-4 h-4 mr-2" />
            Lucro/Prejuízo
          </TabsTrigger>
          <TabsTrigger value="financial" className="h-9 px-3 flex-shrink-0 whitespace-nowrap rounded-md data-[state=active]:bg-emerald-500/20 data-[state=active]:text-emerald-300">
            <Activity className="w-4 h-4 mr-2" />
            Estado Financeiro
          </TabsTrigger>
          <TabsTrigger value="tax" className="h-9 px-3 flex-shrink-0 whitespace-nowrap rounded-md data-[state=active]:bg-emerald-500/20 data-[state=active]:text-emerald-300">
            <BadgeEuro className="w-4 h-4 mr-2" />
            Fiscal / IVA
          </TabsTrigger>
          <TabsTrigger value="comparison" className="h-9 px-3 flex-shrink-0 whitespace-nowrap rounded-md data-[state=active]:bg-emerald-500/20 data-[state=active]:text-emerald-300">
            <BarChart3 className="w-4 h-4 mr-2" />
            Comparação
          </TabsTrigger>
          <TabsTrigger value="purchases" className="h-9 px-3 flex-shrink-0 whitespace-nowrap rounded-md data-[state=active]:bg-emerald-500/20 data-[state=active]:text-emerald-300">
            <Receipt className="w-4 h-4 mr-2" />
            Compras
          </TabsTrigger>
          <TabsTrigger value="providers" className="h-9 px-3 flex-shrink-0 whitespace-nowrap rounded-md data-[state=active]:bg-emerald-500/20 data-[state=active]:text-emerald-300">
            <Users className="w-4 h-4 mr-2" />
            Prestadores
          </TabsTrigger>
          <TabsTrigger value="categories" className="h-9 px-3 flex-shrink-0 whitespace-nowrap rounded-md data-[state=active]:bg-emerald-500/20 data-[state=active]:text-emerald-300">
            Categorias
          </TabsTrigger>
          <TabsTrigger value="clients" className="h-9 px-3 flex-shrink-0 whitespace-nowrap rounded-md data-[state=active]:bg-emerald-500/20 data-[state=active]:text-emerald-300">
            Clientes
          </TabsTrigger>
          <TabsTrigger value="prepayments" className="h-9 px-3 flex-shrink-0 whitespace-nowrap rounded-md data-[state=active]:bg-emerald-500/20 data-[state=active]:text-emerald-300">
            <Wallet className="w-4 h-4 mr-2" />
            Pré-pagamentos
          </TabsTrigger>
        </TabsList>


        <TabsContent value="management">
          {(salesReport || profitLoss || financialOverview || taxOverview || prepaymentsSummary) && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
                <Card className="card-dark p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-emerald-500/20 flex items-center justify-center">
                      <Euro className="w-5 h-5 text-emerald-400" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-400">Vendas</p>
                      <p className="text-xl font-bold text-emerald-400">{formatCurrency(managementData.totalRevenue)}</p>
                    </div>
                  </div>
                </Card>

                <Card className="card-dark p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-blue-500/20 flex items-center justify-center">
                      <ShoppingCart className="w-5 h-5 text-blue-400" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-400">Nº vendas</p>
                      <p className="text-xl font-bold text-white">{managementData.totalSales}</p>
                    </div>
                  </div>
                </Card>

                <Card className="card-dark p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-cyan-500/20 flex items-center justify-center">
                      <Receipt className="w-5 h-5 text-cyan-400" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-400">Ticket médio</p>
                      <p className="text-xl font-bold text-white">{formatCurrency(managementData.averageSale)}</p>
                    </div>
                  </div>
                </Card>

                <Card className="card-dark p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-amber-500/20 flex items-center justify-center">
                      <Package className="w-5 h-5 text-amber-400" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-400">Margem bruta</p>
                      <p className="text-xl font-bold text-amber-400">{formatCurrency(managementData.grossMargin)}</p>
                    </div>
                  </div>
                </Card>

                <Card className="card-dark p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-sky-500/20 flex items-center justify-center">
                      <TrendingUp className="w-5 h-5 text-sky-400" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-400">% margem</p>
                      <p className="text-xl font-bold text-white">{managementData.marginPercent.toFixed(1)}%</p>
                    </div>
                  </div>
                </Card>

                <Card className="card-dark p-4">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${managementData.netProfit >= 0 ? 'bg-emerald-500/20' : 'bg-rose-500/20'}`}>
                      <CircleDollarSign className={`w-5 h-5 ${managementData.netProfit >= 0 ? 'text-emerald-400' : 'text-rose-400'}`} />
                    </div>
                    <div>
                      <p className="text-xs text-slate-400">Lucro líquido</p>
                      <p className={`text-xl font-bold ${managementData.netProfit >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                        {formatCurrency(managementData.netProfit)}
                      </p>
                    </div>
                  </div>
                </Card>

                <Card className="card-dark p-4">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${managementData.operationalBalance >= 0 ? 'bg-emerald-500/20' : 'bg-rose-500/20'}`}>
                      <Wallet className={`w-5 h-5 ${managementData.operationalBalance >= 0 ? 'text-emerald-400' : 'text-rose-400'}`} />
                    </div>
                    <div>
                      <p className="text-xs text-slate-400">Saldo operacional</p>
                      <p className={`text-xl font-bold ${managementData.operationalBalance >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                        {formatCurrency(managementData.operationalBalance)}
                      </p>
                    </div>
                  </div>
                </Card>

                <Card className="card-dark p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-indigo-500/20 flex items-center justify-center">
                      <Activity className="w-5 h-5 text-indigo-400" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-400">Estado</p>
                      <p className={`text-xl font-bold ${managementData.healthClass}`}>{managementData.healthLabel}</p>
                    </div>
                  </div>
                </Card>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 2xl:grid-cols-3 gap-4">
                <Card className="card-dark">
                  <CardHeader>
                    <CardTitle className="text-white text-lg">Resumo Executivo</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex justify-between items-center p-3 bg-slate-800 rounded-lg">
                      <span className="text-slate-400">Recebimentos</span>
                      <span className="text-white font-medium">{formatCurrency(managementData.totalReceipts)}</span>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-slate-800 rounded-lg">
                      <span className="text-slate-400">Despesas</span>
                      <span className="text-rose-400 font-medium">{formatCurrency(managementData.totalExpenses)}</span>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-slate-800 rounded-lg">
                      <span className="text-slate-400">Pago prestadores</span>
                      <span className="text-blue-400 font-medium">{formatCurrency(managementData.providerPaid)}</span>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-slate-800 rounded-lg">
                      <span className="text-slate-400">Aberto prestadores</span>
                      <span className="text-orange-400 font-medium">{formatCurrency(managementData.providerOpen)}</span>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-slate-800 rounded-lg">
                      <span className="text-slate-400">Pré-pagamentos em aberto</span>
                      <span className="text-amber-400 font-medium">{formatCurrency(managementData.prepaymentsOpen)}</span>
                    </div>
                  </CardContent>
                </Card>

                <Card className="card-dark">
                  <CardHeader>
                    <CardTitle className="text-white text-lg">Pressão Financeira</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex justify-between items-center p-3 bg-slate-800 rounded-lg">
                      <span className="text-slate-400">IVA estimado</span>
                      <span className={`${managementData.vatNet > 0 ? 'text-amber-400' : 'text-emerald-400'} font-medium`}>
                        {formatCurrency(managementData.vatNet)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-slate-800 rounded-lg">
                      <span className="text-slate-400">Compras pendentes</span>
                      <span className="text-orange-400 font-medium">{formatCurrency(managementData.purchasesPending)}</span>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-slate-800 rounded-lg">
                      <span className="text-slate-400">Compras do período</span>
                      <span className="text-purple-400 font-medium">{formatCurrency(managementData.purchasesTotal)}</span>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-slate-800 rounded-lg">
                      <span className="text-slate-400">Consumido em pré-pagos</span>
                      <span className="text-emerald-400 font-medium">{formatCurrency(managementData.prepaymentsConsumed)}</span>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-slate-800 rounded-lg border border-slate-700">
                      <span className="text-slate-300">Pressão total estimada</span>
                      <span className="text-white font-semibold">{formatCurrency(managementData.pressureTotal)}</span>
                    </div>
                  </CardContent>
                </Card>

                <Card className="card-dark">
                  <CardHeader>
                    <CardTitle className="text-white text-lg">Comparação com período anterior</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {[
                      ['Vendas', managementData.comparisons?.revenue],
                      ['Ticket médio', managementData.comparisons?.averageSale],
                      ['Lucro líquido', managementData.comparisons?.netProfit],
                      ['Recebimentos', managementData.comparisons?.receipts],
                    ].map(([label, row]) => {
                      const positive = Number(row?.diff || 0) >= 0;
                      const hasPercent = row?.percent !== null && row?.percent !== undefined;

                      return (
                        <div key={label} className="p-3 bg-slate-800 rounded-lg">
                          <div className="flex items-center justify-between gap-3">
                            <span className="text-slate-400">{label}</span>
                            <span className={`font-medium ${positive ? 'text-emerald-400' : 'text-rose-400'}`}>
                              {positive ? '+' : ''}{formatCurrency(row?.diff || 0)}
                            </span>
                          </div>
                          <div className="flex items-center justify-between gap-3 mt-1 text-xs">
                            <span className="text-slate-500">
                              Atual: {formatCurrency(row?.current || 0)} · Anterior: {formatCurrency(row?.previous || 0)}
                            </span>
                            <span className={`${positive ? 'text-emerald-400' : 'text-rose-400'}`}>
                              {hasPercent ? `${positive ? '+' : ''}${row.percent.toFixed(1)}%` : 'n/d'}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </CardContent>
                </Card>

                <Card className="card-dark">
                  <CardHeader>
                    <CardTitle className="text-white text-lg">Peso de vendas</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="p-3 bg-slate-800 rounded-lg">
                      <div className="flex items-center justify-between gap-3">
                        <span className="text-slate-400">Internas (NCP)</span>
                        <span className="text-white font-medium">{formatCurrency(managementData.internalTotal)}</span>
                      </div>
                      <p className="text-xs text-slate-500 mt-1">{managementData.internalWeight.toFixed(1)}% do total</p>
                    </div>

                    <div className="p-3 bg-slate-800 rounded-lg">
                      <div className="flex items-center justify-between gap-3">
                        <span className="text-slate-400">Fiscais diretas POS</span>
                        <span className="text-white font-medium">{formatCurrency(managementData.fiscalTotal)}</span>
                      </div>
                      <p className="text-xs text-slate-500 mt-1">{managementData.fiscalWeight.toFixed(1)}% do total</p>
                    </div>
                  </CardContent>
                </Card>

                <Card className="card-dark">
                  <CardHeader>
                    <CardTitle className="text-white text-lg">Mix de pagamentos</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {Array.isArray(managementData.paymentMixComparison) && managementData.paymentMixComparison.length > 0 ? (
                      managementData.paymentMixComparison.slice(0, 8).map((row) => {
                        const positive = Number(row.diff || 0) >= 0;
                        const hasPercent = row.percentChange !== null && row.percentChange !== undefined;

                        return (
                          <div key={row.method} className="p-3 bg-slate-800 rounded-lg">
                            <div className="flex items-center justify-between gap-3">
                              <span className="text-slate-400 capitalize">{String(row.method || '').replace('_', ' ')}</span>
                              <span className="text-white font-medium">{formatCurrency(row.total || 0)}</span>
                            </div>

                            <div className="flex items-center justify-between gap-3 mt-1 text-xs">
                              <span className="text-slate-500">{Number(row.count || 0)} transação(ões)</span>
                              <span className="text-slate-500">{Number(row.percent || 0).toFixed(1)}%</span>
                            </div>

                            <div className="flex items-center justify-between gap-3 mt-1 text-xs">
                              <span className="text-slate-500">Anterior: {formatCurrency(row.previousTotal || 0)}</span>
                              <span className={`${positive ? 'text-emerald-400' : 'text-rose-400'} font-medium`}>
                                {positive ? '+' : ''}{formatCurrency(row.diff || 0)}
                              </span>
                            </div>

                            <div className="flex items-center justify-end mt-1">
                              <span className={`text-xs ${positive ? 'text-emerald-400' : 'text-rose-400'}`}>
                                {hasPercent ? `${positive ? '+' : ''}${Number(row.percentChange || 0).toFixed(1)}%` : 'n/d'}
                              </span>
                            </div>
                          </div>
                        );
                      })
                    ) : (
                      <p className="text-slate-500 text-center py-4">Sem dados de pagamentos no período</p>
                    )}
                  </CardContent>
                </Card>

                <Card className="card-dark lg:col-span-2">
                  <CardHeader>
                    <CardTitle className="text-white text-lg">Top Prestadores</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {Array.isArray(managementData.topProviders) && managementData.topProviders.length > 0 ? (
                      managementData.topProviders.map((row, idx) => (
                        <div key={`${row.name}-${idx}`} className="p-3 bg-slate-800 rounded-lg">
                          <div className="flex items-center justify-between gap-3">
                            <span className="text-slate-300 font-medium">{row.name}</span>
                            <span className="text-white font-semibold">{formatCurrency(row.produced)}</span>
                          </div>

                          <div className="mt-1 text-xs text-slate-500">
                            Produzido
                          </div>

                          <div className="flex items-center justify-between gap-3 mt-3 text-xs">
                            <span className="text-slate-500">A pagar ao prestador</span>
                            <span className="text-sky-400 font-medium">{formatCurrency(row.due)}</span>
                          </div>

                          <div className="flex items-center justify-between gap-3 mt-1 text-xs">
                            <span className="text-slate-500">Pago</span>
                            <span className="text-emerald-400 font-medium">{formatCurrency(row.paid)}</span>
                          </div>

                          <div className="flex items-center justify-between gap-3 mt-1 text-xs">
                            <span className="text-slate-500">Em aberto</span>
                            <span className="text-orange-400 font-medium">{formatCurrency(row.open)}</span>
                          </div>
                        </div>
                      ))
                    ) : (
                      <p className="text-slate-500 text-center py-4">Sem dados de prestadores no período</p>
                    )}
                  </CardContent>
                </Card>

                <Card className="card-dark">
                  <CardHeader>
                    <CardTitle className="text-white text-lg">Alertas</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {managementData.alerts.length > 0 ? (
                      managementData.alerts.map((alert, idx) => (
                        <div key={idx} className="p-3 bg-slate-800 rounded-lg">
                          <p className={`text-sm font-medium ${
                            alert.level === 'high'
                              ? 'text-rose-400'
                              : alert.level === 'medium'
                                ? 'text-amber-400'
                                : 'text-sky-400'
                          }`}>
                            {alert.text}
                          </p>
                        </div>
                      ))
                    ) : (
                      <p className="text-slate-400">Sem alertas relevantes no período.</p>
                    )}
                  </CardContent>
                </Card>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <Card className="card-dark">
                  <CardHeader>
                    <CardTitle className="text-white text-lg">Top Produtos</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {Array.isArray(topProducts) && topProducts.length > 0 ? (
                      <div className="space-y-3">
                        {topProducts.slice(0, 10).map((item, idx) => (
                          <div key={`${item.product_id || item.product_name || idx}`} className="p-3 bg-slate-800 rounded-lg">
                            <div className="flex items-start justify-between gap-3">
                              <div>
                                <p className="text-white font-medium">{item.product_name || 'Produto'}</p>
                                <p className="text-xs text-slate-500 mt-1">
                                  {Number(item.quantity || 0)} un. · {Number(item.sales_count || 0)} venda(s)
                                </p>
                                <p className="text-xs text-slate-500 mt-1">
                                  {salesReport?.summary?.total_revenue > 0
                                    ? `${(((Number(item.total || 0) / Number(salesReport.summary.total_revenue || 1)) * 100)).toFixed(1)}% do total`
                                    : '0,0% do total'}
                                </p>
                              </div>
                              <div className="text-right">
                                <p className="text-emerald-400 font-semibold">{formatCurrency(item.total || 0)}</p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-slate-500 text-center py-4">Sem dados de produtos no período</p>
                    )}
                  </CardContent>
                </Card>

                <Card className="card-dark">
                  <CardHeader>
                    <CardTitle className="text-white text-lg">Top Clientes</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {Array.isArray(topCustomers) && topCustomers.length > 0 ? (
                      <div className="space-y-3">
                        {topCustomers.slice(0, 10).map((item, idx) => (
                          <div key={`${item.customer_id || item.customer_name || idx}`} className="p-3 bg-slate-800 rounded-lg">
                            <div className="flex items-start justify-between gap-3">
                              <div>
                                <p className="text-white font-medium">{item.customer_name || 'Consumidor Final'}</p>
                                <p className="text-xs text-slate-500 mt-1">
                                  {Number(item.sales_count || 0)} venda(s)
                                </p>
                                <p className="text-xs text-slate-500 mt-1">
                                  {salesReport?.summary?.total_revenue > 0
                                    ? `${(((Number(item.total || 0) / Number(salesReport.summary.total_revenue || 1)) * 100)).toFixed(1)}% do total`
                                    : '0,0% do total'}
                                </p>
                              </div>
                              <div className="text-right">
                                <p className="text-emerald-400 font-semibold">{formatCurrency(item.total || 0)}</p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-slate-500 text-center py-4">Sem dados de clientes no período</p>
                    )}
                  </CardContent>
                </Card>

                <Card className="card-dark">
                  <CardHeader>
                    <CardTitle className="text-white text-lg">Top Categorias</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {Array.isArray(topCategories) && topCategories.length > 0 ? (
                      topCategories.slice(0, 10).map((item, idx) => (
                        <div key={`${item.category}-${idx}`} className="p-3 bg-slate-800 rounded-lg">
                          <div className="flex items-center justify-between gap-3">
                            <span className="text-slate-300 text-sm">{item.category || 'SEM CATEGORIA'}</span>
                            <span className="text-white font-medium">{formatCurrency(item.total || 0)}</span>
                          </div>
                          <div className="flex items-center justify-between gap-3 mt-1 text-xs">
                            <span className="text-slate-500">{Number(item.quantity || 0).toFixed(2)} un.</span>
                            <span className="text-slate-500">{item.sales_count || 0} linhas</span>
                          </div>
                          <div className="flex items-center justify-between gap-3 mt-1 text-xs">
                            <span className="text-sky-400">{formatCurrency(item.margin || 0)} margem</span>
                            <span className="text-slate-500">{Number(item.margin_percent || 0).toFixed(1)}% margem</span>
                          </div>
                          <div className="flex items-center justify-between gap-3 mt-1 text-xs">
                            <span className="text-slate-500">
                              {salesReport?.summary?.total_revenue > 0
                                ? `${(((Number(item.total || 0) / Number(salesReport.summary.total_revenue || 1)) * 100)).toFixed(1)}% do total`
                                : '0,0% do total'}
                            </span>
                            <span className="text-slate-500">{formatCurrency(item.cost || 0)} custo</span>
                          </div>
                        </div>
                      ))
                    ) : (
                      <p className="text-slate-500 text-center py-4">Sem dados de categorias no período</p>
                    )}
                  </CardContent>
                </Card>
              </div>

              <Card className="card-dark lg:col-span-2">
                <CardHeader>
                  <CardTitle className="text-white text-lg">Categorias vs período anterior</CardTitle>
                </CardHeader>
                <CardContent>
                  {Array.isArray(managementData.categoryComparisons) && managementData.categoryComparisons.length > 0 ? (
                    <div className="space-y-3">
                      {managementData.categoryComparisons.slice(0, 10).map((row) => {
                        const positive = Number(row.diff || 0) >= 0;
                        const hasPercent = row.percent !== null && row.percent !== undefined;

                        return (
                          <div key={row.category} className="p-3 bg-slate-800 rounded-lg">
                            <div className="flex items-center justify-between gap-3">
                              <span className="text-slate-300 font-medium">{row.category}</span>
                              <span className="text-white font-semibold">{formatCurrency(row.currentTotal)}</span>
                            </div>

                            <div className="flex items-center justify-between gap-3 mt-1 text-xs">
                              <span className="text-slate-500">Anterior: {formatCurrency(row.previousTotal)}</span>
                              <span className={`${positive ? 'text-emerald-400' : 'text-rose-400'} font-medium`}>
                                {positive ? '+' : ''}{formatCurrency(row.diff)}
                              </span>
                            </div>

                            <div className="flex items-center justify-between gap-3 mt-1 text-xs">
                              <span className="text-slate-500">{Number(row.quantity || 0).toFixed(2)} un. · {row.salesCount || 0} linhas</span>
                              <span className="text-slate-500">
                                {hasPercent ? `${row.percent >= 0 ? '+' : ''}${row.percent.toFixed(1)}%` : 'n/d'}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-slate-500 text-center py-4">Sem comparação de categorias disponível</p>
                  )}
                </CardContent>
              </Card>

              <Card className="card-dark lg:col-span-3">
                <CardHeader>
                  <CardTitle className="text-white text-lg">Tendência diária de vendas</CardTitle>
                </CardHeader>
                <CardContent>
                  {salesReport?.by_day && Object.keys(salesReport.by_day).length > 0 ? (
                    <div className="space-y-3">
                      {(() => {
                        const rows = Object.entries(salesReport.by_day)
                          .sort(([a], [b]) => a.localeCompare(b))
                          .slice(-15);

                        const maxTotal = Math.max(...rows.map(([, data]) => Number(data?.total || 0)), 1);

                        return rows.map(([date, data]) => {
                          const total = Number(data?.total || 0);
                          const count = Number(data?.count || 0);
                          const width = Math.max(6, (total / maxTotal) * 100);

                          return (
                            <div key={date} className="space-y-1">
                              <div className="flex items-center justify-between gap-3 text-sm">
                                <span className="text-slate-300">{formatDate(date)}</span>
                                <span className="text-slate-500">{count} trans.</span>
                              </div>

                              <div className="w-full h-3 bg-slate-800 rounded-full overflow-hidden">
                                <div
                                  className="h-3 rounded-full bg-emerald-500"
                                  style={{ width: `${width}%` }}
                                />
                              </div>

                              <div className="flex items-center justify-end">
                                <span className="text-emerald-400 font-medium text-sm">{formatCurrency(total)}</span>
                              </div>
                            </div>
                          );
                        });
                      })()}
                    </div>
                  ) : (
                    <p className="text-slate-500 text-center py-4">Sem tendência diária disponível no período</p>
                  )}
                </CardContent>
              </Card>
            </div>
          )}
        </TabsContent>

        {/* Cash close audit report */}
        <TabsContent value="cash-closes">
          <div className="space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <h2 className="text-xl font-bold text-white">Relatório de Fechos de Caixa</h2>
                <p className="text-sm text-slate-400">Arquivo dos valores registados no momento de cada fecho. Não recalcula nem altera documentos históricos.</p>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={downloadCashClosesCSV} className="border-slate-700 text-slate-200">
                  <FileSpreadsheet className="w-4 h-4 mr-2" /> CSV
                </Button>
                <Button variant="outline" onClick={() => window.print()} className="border-slate-700 text-slate-200">
                  <FileText className="w-4 h-4 mr-2" /> Imprimir / PDF
                </Button>
              </div>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-6 gap-3">
              {[
                ['Fechos', cashClosesReport?.totals?.close_count || 0, 'text-white'],
                ['Caixas', cashClosesReport?.totals?.session_count || 0, 'text-white'],
                ['Vendas', formatCurrency(cashClosesReport?.totals?.sales_total), 'text-emerald-400'],
                ['Conferido', formatCurrency(cashClosesReport?.totals?.actual_total), 'text-blue-400'],
                ['Faltas', formatCurrency(cashClosesReport?.totals?.shortage), 'text-rose-400'],
                ['Sobras', formatCurrency(cashClosesReport?.totals?.overage), 'text-amber-400'],
              ].map(([label, value, color]) => (
                <Card key={label} className="card-dark p-4">
                  <p className="text-xs text-slate-400">{label}</p>
                  <p className={`text-lg font-bold ${color}`}>{value}</p>
                </Card>
              ))}
            </div>

            <Card className="card-dark overflow-hidden">
              <CardContent className="p-0">
                {(cashClosesReport?.closes || []).length === 0 ? (
                  <p className="text-slate-400 text-center py-12">Não existem fechos registados neste período.</p>
                ) : (
                  <div className="divide-y divide-slate-700">
                    {(cashClosesReport?.closes || []).map((row) => {
                      const difference = Number(row.difference || 0);
                      const isOpen = expandedCashClose === row.id;
                      const methodLabels = {
                        cash: 'Dinheiro', card_total: 'Multibanco/Cartão', card: 'Cartão', multibanco: 'Multibanco',
                        mbway: 'MBWay', transfer: 'Transferência', voucher: 'Voucher', account_current: 'Conta corrente', other: 'Outro'
                      };
                      return (
                        <div key={row.id} className="p-4">
                          <button type="button" onClick={() => setExpandedCashClose(isOpen ? null : row.id)} className="w-full text-left">
                            <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-8 gap-3 items-center">
                              <div><p className="text-xs text-slate-500">Data / hora</p><p className="text-white font-semibold">{formatDate(row.date)}</p><p className="text-xs text-slate-400">{row.created_at ? new Date(row.created_at).toLocaleTimeString('pt-PT', { hour: '2-digit', minute: '2-digit' }) : '-'}</p></div>
                              <div><p className="text-xs text-slate-500">Tipo</p><Badge className={row.kind === 'consolidated' ? 'bg-blue-500/20 text-blue-300' : 'bg-slate-700 text-slate-200'}>{row.kind === 'consolidated' ? 'Consolidado' : 'Individual'}</Badge></div>
                              <div><p className="text-xs text-slate-500">Fechado por</p><p className="text-slate-200 font-medium">{row.closed_by_name || '-'}</p></div>
                              <div><p className="text-xs text-slate-500">Caixas / operadores</p><p className="text-slate-200">{row.session_count} · {(row.operator_names || []).join(', ') || '-'}</p></div>
                              <div><p className="text-xs text-slate-500">Vendas</p><p className="text-white font-semibold">{row.sales_count} · {formatCurrency(row.sales_total)}</p></div>
                              <div><p className="text-xs text-slate-500">Esperado</p><p className="text-slate-200 font-semibold">{formatCurrency(row.expected_total)}</p></div>
                              <div><p className="text-xs text-slate-500">Conferido</p><p className="text-slate-200 font-semibold">{formatCurrency(row.actual_total)}</p></div>
                              <div><p className="text-xs text-slate-500">Resultado</p><p className={`font-bold ${Math.abs(difference) < 0.005 ? 'text-emerald-400' : difference < 0 ? 'text-rose-400' : 'text-amber-400'}`}>{Math.abs(difference) < 0.005 ? 'Certo' : `${difference < 0 ? 'Falta' : 'Sobra'} ${formatCurrency(Math.abs(difference))}`}</p></div>
                            </div>
                          </button>

                          {isOpen && (
                            <div className="mt-4 pt-4 border-t border-slate-700 space-y-4">
                              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                <div className="bg-slate-800 rounded-lg p-3"><p className="text-xs text-slate-400">Aberturas</p><p className="text-white font-bold">{formatCurrency(row.opening)}</p></div>
                                <div className="bg-slate-800 rounded-lg p-3"><p className="text-xs text-slate-400">Entradas de caixa</p><p className="text-emerald-400 font-bold">{formatCurrency(row.cash_in)}</p></div>
                                <div className="bg-slate-800 rounded-lg p-3"><p className="text-xs text-slate-400">Saídas de caixa</p><p className="text-rose-400 font-bold">{formatCurrency(row.cash_out)}</p></div>
                                <div className="bg-slate-800 rounded-lg p-3"><p className="text-xs text-slate-400">Diferença global</p><p className="text-white font-bold">{formatCurrency(difference)}</p></div>
                              </div>
                              <div className="overflow-x-auto rounded-lg border border-slate-700">
                                <table className="w-full text-sm">
                                  <thead className="bg-slate-800 text-slate-400"><tr><th className="text-left p-3">Meio</th><th className="text-right p-3">Esperado</th><th className="text-right p-3">Conferido</th><th className="text-right p-3">Diferença</th></tr></thead>
                                  <tbody className="divide-y divide-slate-700">
                                    {Object.entries(row.comparison || {}).map(([key, values]) => {
                                      const methodDiff = Number(values?.difference || 0);
                                      return <tr key={key}><td className="p-3 text-slate-200">{methodLabels[key] || key}</td><td className="p-3 text-right text-slate-300">{formatCurrency(values?.expected)}</td><td className="p-3 text-right text-slate-300">{formatCurrency(values?.actual)}</td><td className={`p-3 text-right font-semibold ${Math.abs(methodDiff) < 0.005 ? 'text-emerald-400' : 'text-rose-400'}`}>{formatCurrency(methodDiff)}</td></tr>;
                                    })}
                                  </tbody>
                                </table>
                              </div>
                              {(row.tpas || []).length > 0 && <p className="text-sm text-slate-300"><strong>TPAs:</strong> {row.tpas.map(tpa => `${tpa.name}: ${formatCurrency(tpa.amount)}`).join(' · ')}</p>}
                              {row.note && <p className="text-sm text-slate-300"><strong>Observações:</strong> {row.note}</p>}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Sales Report Tab */}
        <TabsContent value="sales">
          {salesReport && (
            <div className="space-y-4">
              {/* Summary Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-4">
                <Card className="card-dark p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-emerald-500/20 flex items-center justify-center">
                      <Euro className="w-5 h-5 text-emerald-400" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-400">Vendas FamaPOS</p>
                      <p className="text-xl font-bold text-white">{formatCurrency(salesReport.summary?.total_revenue)}</p>
                    </div>
                  </div>
                </Card>
                <Card className="card-dark p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-blue-500/20 flex items-center justify-center">
                      <ShoppingCart className="w-5 h-5 text-blue-400" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-400">Transações</p>
                      <p className="text-xl font-bold text-white">{salesReport.summary?.total_sales || 0}</p>
                    </div>
                  </div>
                </Card>
                <Card className="card-dark p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-purple-500/20 flex items-center justify-center">
                      <FileText className="w-5 h-5 text-purple-400" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-400">Internas (NCP)</p>
                      <p className="text-xl font-bold text-white">{formatCurrency(salesReport.internal_sales?.total)}</p>
                    </div>
                  </div>
                </Card>
                <Card className="card-dark p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-amber-500/20 flex items-center justify-center">
                      <FileText className="w-5 h-5 text-amber-400" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-400">Fiscais diretas no POS</p>
                      <p className="text-xl font-bold text-white">{formatCurrency(salesReport.fiscal_sales?.total)}</p>
                    </div>
                  </div>
                </Card>
                <Card className="card-dark p-4 border border-emerald-500/30">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-emerald-500/20 flex items-center justify-center">
                      <BadgeEuro className="w-5 h-5 text-emerald-400" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-400">Documentos Vendus</p>
                      <p className="text-xl font-bold text-emerald-400">{formatCurrency(salesReport.vendus_documents?.total)}</p>
                      <p className="text-[11px] text-slate-500">{salesReport.vendus_documents?.count || 0} docs. · inclui Fecho do Dia</p>
                    </div>
                  </div>
                </Card>
              </div>

              <div className="rounded-xl border border-blue-500/30 bg-blue-500/10 px-4 py-3 text-sm text-slate-300">
                <strong className="text-blue-300">Dois valores, duas finalidades:</strong> FamaPOS mantém o valor realmente cobrado no POS. “Documentos Vendus” mostra o valor fiscal emitido, incluindo as vendas internas posteriormente faturadas no Fecho do Dia pelos valores fiscais editados.
              </div>

              {/* Payment Methods */}
              {salesReport.by_payment_method && (
                <Card className="card-dark">
                  <CardHeader>
                    <CardTitle className="text-white text-lg">Por Método de Pagamento</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {Object.entries(salesReport.by_payment_method).map(([method, data]) => (
                        <div key={method} className="p-3 bg-slate-800 rounded-lg">
                          <p className="text-xs text-slate-400 capitalize">{method.replace('_', ' ')}</p>
                          <p className="text-lg font-bold text-white">{formatCurrency(data?.total || data)}</p>
                          {data?.count && <p className="text-xs text-slate-500">{data.count} transações</p>}
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Daily Breakdown */}
              {salesReport.by_day && Object.keys(salesReport.by_day).length > 0 && (
                <Card className="card-dark">
                  <CardHeader>
                    <CardTitle className="text-white text-lg">Vendas por Dia</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <Table className="table-dark">
                      <TableHeader>
                        <TableRow>
                          <TableHead>Data</TableHead>
                          <TableHead>Transações</TableHead>
                          <TableHead className="text-right">Total</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {Object.entries(salesReport.by_day)
                          .sort(([a], [b]) => b.localeCompare(a))
                          .slice(0, 15)
                          .map(([date, data]) => (
                            <TableRow key={date}>
                              <TableCell className="text-white">{formatDate(date)}</TableCell>
                              <TableCell className="text-slate-400">{data.count}</TableCell>
                              <TableCell className="text-right text-emerald-400 font-medium">
                                {formatCurrency(data.total)}
                              </TableCell>
                            </TableRow>
                          ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </TabsContent>

        {/* Stock Report Tab */}
        <TabsContent value="stock">
          {stockReport && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card className="card-dark p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-blue-500/20 flex items-center justify-center">
                      <Package className="w-5 h-5 text-blue-400" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-400">Total Produtos</p>
                      <p className="text-xl font-bold text-white">{stockReport.total_products}</p>
                    </div>
                  </div>
                </Card>

                <Card className="card-dark p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-purple-500/20 flex items-center justify-center">
                      <Package className="w-5 h-5 text-purple-400" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-400">Total Unidades</p>
                      <p className="text-xl font-bold text-white">{stockReport.total_units}</p>
                    </div>
                  </div>
                </Card>

                <Card className="card-dark p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-slate-500/20 flex items-center justify-center">
                      <Euro className="w-5 h-5 text-slate-400" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-400">Valor Custo</p>
                      <p className="text-xl font-bold text-white">{formatCurrency(stockReport.total_cost_value)}</p>
                    </div>
                  </div>
                </Card>

                <Card className="card-dark p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-emerald-500/20 flex items-center justify-center">
                      <TrendingUp className="w-5 h-5 text-emerald-400" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-400">Valor Venda</p>
                      <p className="text-xl font-bold text-emerald-400">{formatCurrency(stockReport.total_sell_value)}</p>
                    </div>
                  </div>
                </Card>
              </div>

              {stockReport.categories && stockReport.categories.length > 0 && (
                <Card className="card-dark">
                  <CardHeader>
                    <CardTitle className="text-white text-lg">Stock por Categoria</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <Table className="table-dark">
                      <TableHeader>
                        <TableRow>
                          <TableHead>Categoria</TableHead>
                          <TableHead>Produtos</TableHead>
                          <TableHead>Unidades</TableHead>
                          <TableHead className="text-right">Valor Custo</TableHead>
                          <TableHead className="text-right">Valor Venda</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {stockReport.categories.map((cat) => (
                          <TableRow key={cat.category}>
                            <TableCell className="text-white font-medium">{cat.category || 'Sem categoria'}</TableCell>
                            <TableCell className="text-slate-400">{cat.product_count}</TableCell>
                            <TableCell className="text-slate-400">{cat.total_units}</TableCell>
                            <TableCell className="text-right text-slate-300">{formatCurrency(cat.cost_value)}</TableCell>
                            <TableCell className="text-right text-emerald-400">{formatCurrency(cat.sell_value)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </TabsContent>

        {/* Expiring Products Tab */}
        <TabsContent value="expiring">
          <>
            <div className="space-y-4">
              <Card className="card-dark p-4">
                <div className="flex flex-wrap items-end gap-3">
                  <div>
                    <Label className="text-slate-400 text-xs">Categoria</Label>
                    <Select value={expiryCategoryFilter} onValueChange={setExpiryCategoryFilter}>
                      <SelectTrigger className="bg-slate-800 border-slate-700 w-56">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todas</SelectItem>
                        {expiryCategories.map((cat) => (
                          <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label className="text-slate-400 text-xs">Prioridade</Label>
                    <Select value={expiryPriorityFilter} onValueChange={setExpiryPriorityFilter}>
                      <SelectTrigger className="bg-slate-800 border-slate-700 w-52">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todas</SelectItem>
                        <SelectItem value="very_high">Máxima</SelectItem>
                        <SelectItem value="high">Alta</SelectItem>
                        <SelectItem value="medium">Média</SelectItem>
                        <SelectItem value="low">Baixa</SelectItem>
                        <SelectItem value="expired">Expirados</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <label className="flex items-center gap-2 text-sm text-slate-300 pb-2">
                    <input
                      type="checkbox"
                      checked={expiryOnlyActionable}
                      onChange={(e) => setExpiryOnlyActionable(e.target.checked)}
                    />
                    Só acionáveis
                  </label>
                </div>
              </Card>

              <div className="grid grid-cols-1 md:grid-cols-4 xl:grid-cols-8 gap-4">
                <Card className="card-dark p-4">
                  <p className="text-xs text-slate-400">Expirados</p>
                  <p className="text-2xl font-bold text-rose-400">{expiryReport?.summary?.expired_count || 0}</p>
                </Card>
                <Card className="card-dark p-4">
                  <p className="text-xs text-slate-400">Críticos até 30 dias</p>
                  <p className="text-2xl font-bold text-amber-400">{expiryReport?.summary?.critical_30_count || 0}</p>
                </Card>
                <Card className="card-dark p-4">
                  <p className="text-xs text-slate-400">A expirar até 60 dias</p>
                  <p className="text-2xl font-bold text-yellow-300">{expiryReport?.summary?.expiring_60_count || 0}</p>
                </Card>
                <Card className="card-dark p-4">
                  <p className="text-xs text-slate-400">A expirar até 90 dias</p>
                  <p className="text-2xl font-bold text-sky-400">{expiryReport?.summary?.expiring_90_count || 0}</p>
                </Card>
                <Card className="card-dark p-4">
                  <p className="text-xs text-slate-400">Valor custo em risco</p>
                  <p className="text-2xl font-bold text-rose-300">{formatCurrency(expiryReport?.summary?.total_cost_at_risk || 0)}</p>
                </Card>
                <Card className="card-dark p-4">
                  <p className="text-xs text-slate-400">Prioridade máxima</p>
                  <p className="text-2xl font-bold text-rose-400">{expiryReport?.summary?.very_high_priority_count || 0}</p>
                </Card>
                <Card className="card-dark p-4">
                  <p className="text-xs text-slate-400">Prioridade alta</p>
                  <p className="text-2xl font-bold text-amber-400">{expiryReport?.summary?.high_priority_count || 0}</p>
                </Card>
                <Card className="card-dark p-4">
                  <p className="text-xs text-slate-400">Prioridade média</p>
                  <p className="text-2xl font-bold text-yellow-300">{expiryReport?.summary?.medium_priority_count || 0}</p>
                </Card>
              </div>

              <Card className="card-dark">
                <CardHeader>
                  <CardTitle className="text-white text-lg flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5 text-amber-400" />
                    Produtos a Expirar, ordenados do mais urgente para o menos urgente
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {filteredExpiringProducts.length > 0 ? (
                    <Table className="table-dark">
                      <TableHeader>
                        <TableRow>
                          <TableHead className="min-w-[260px]">Produto</TableHead>
                          <TableHead className="whitespace-nowrap">Validade</TableHead>
                          <TableHead>Stock</TableHead>
                          <TableHead>Prioridade</TableHead>
                          <TableHead className="min-w-[220px]">Risco / sugestão</TableHead>
                          {isManager && <TableHead className="w-[150px]">Ações</TableHead>}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredExpiringProducts.map((item, idx) => (
                          <TableRow key={idx}>
                            <TableCell className="text-white align-top">
                              <div className="max-w-[260px]">
                                <p className="font-medium leading-snug break-words">{item.product_name}</p>
                                <div className="mt-1 space-y-1 text-xs text-slate-400">
                                  <p>{item.category || '-'}</p>
                                  <p>
                                    <code className="px-2 py-1 bg-slate-800 rounded text-[11px] text-emerald-400">
                                      {item.lot_number}
                                    </code>
                                  </p>
                                </div>
                              </div>
                            </TableCell>

                            <TableCell className="align-top">
                              <div className="space-y-2 whitespace-nowrap">
                                <p className="text-slate-300">{item.expiry_date}</p>
                                <Badge className={
                                  item.days_remaining < 0
                                    ? 'bg-rose-500/20 text-rose-400'
                                    : item.days_remaining <= 30
                                      ? 'bg-amber-500/20 text-amber-300'
                                      : item.days_remaining <= 60
                                        ? 'badge-warning'
                                        : 'badge-info'
                                }>
                                  {item.days_remaining} dias
                                </Badge>
                              </div>
                            </TableCell>

                            <TableCell className="align-top">
                              <div className="space-y-2 text-sm">
                                <p className="text-white">Qtd.: <span className="font-medium">{item.quantity}</span></p>
                                <p className="text-slate-400">Vend. 30d: {item.sold_last_30 || 0}</p>
                                <Badge className={
                                  item.rotation_status === 'parado'
                                    ? 'bg-rose-500/20 text-rose-400'
                                    : item.rotation_status === 'baixa'
                                      ? 'bg-amber-500/20 text-amber-300'
                                      : item.rotation_status === 'moderada'
                                        ? 'badge-warning'
                                        : 'bg-emerald-500/20 text-emerald-400'
                                }>
                                  {item.rotation_status}
                                </Badge>
                              </div>
                            </TableCell>

                            <TableCell className="align-top">
                              <div className="space-y-2">
                                <Badge className={
                                  item.campaign_priority === 'very_high'
                                    ? 'bg-rose-500/20 text-rose-400'
                                    : item.campaign_priority === 'high'
                                      ? 'bg-amber-500/20 text-amber-300'
                                      : item.campaign_priority === 'medium'
                                        ? 'badge-warning'
                                        : item.campaign_priority === 'expired'
                                          ? 'bg-rose-700/30 text-rose-300'
                                          : 'badge-info'
                                }>
                                  {item.campaign_priority === 'very_high'
                                    ? 'Máxima'
                                    : item.campaign_priority === 'high'
                                      ? 'Alta'
                                      : item.campaign_priority === 'medium'
                                        ? 'Média'
                                        : item.campaign_priority === 'expired'
                                          ? 'Expirado'
                                          : 'Baixa'}
                                </Badge>

                                <div>
                                  <Badge className={
                                    item.commercial_status === 'campaign_created'
                                      ? 'bg-emerald-500/20 text-emerald-400'
                                      : item.commercial_status === 'campaign_suggested'
                                        ? 'bg-amber-500/20 text-amber-300'
                                        : item.commercial_status === 'monitor'
                                          ? 'bg-sky-500/20 text-sky-300'
                                          : 'bg-slate-700 text-slate-300'
                                  }>
                                    {item.commercial_status === 'campaign_created'
                                      ? 'Campanha criada'
                                      : item.commercial_status === 'campaign_suggested'
                                        ? 'Campanha sugerida'
                                        : item.commercial_status === 'monitor'
                                          ? 'Monitorizar'
                                          : 'Sem ação'}
                                  </Badge>
                                  {item.matching_campaign_name ? (
                                    <p className="text-[11px] text-slate-500 mt-1 break-words">{item.matching_campaign_name}</p>
                                  ) : null}
                                </div>
                              </div>
                            </TableCell>

                            <TableCell className="align-top">
                              <div className="max-w-[220px] space-y-2 text-sm">
                                <p className="text-rose-300 font-medium">{formatCurrency(item.cost_risk)}</p>
                                <p className="text-slate-200">
                                  Desconto: {item.suggested_discount_percent ? `${item.suggested_discount_percent}%` : '-'}
                                </p>
                                <p className="text-slate-300 leading-snug break-words">{item.campaign_suggestion}</p>
                              </div>
                            </TableCell>

                            {isManager && (
                              <TableCell className="align-top">
                                <div className="flex flex-col gap-2 min-w-[140px]">
                                  {item.commercial_status !== 'campaign_suggested' && item.commercial_status !== 'campaign_created' && (
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      className="border-amber-500/40 text-amber-300 hover:bg-amber-500/10 justify-center"
                                      onClick={() => setExpiryAction(item, 'campaign_suggested')}
                                    >
                                      Sugerir
                                    </Button>
                                  )}

                                  {item.commercial_status !== 'monitor' && (
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      className="border-sky-500/40 text-sky-300 hover:bg-sky-500/10 justify-center"
                                      onClick={() => setExpiryAction(item, 'monitor')}
                                    >
                                      Monitorizar
                                    </Button>
                                  )}

                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="border-purple-500/40 text-purple-300 hover:bg-purple-500/10 justify-center"
                                    onClick={() => openExpiryCampaignModal(item)}
                                  >
                                    Campanha
                                  </Button>

                                  {item.commercial_status && item.commercial_status !== 'none' && (
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      className="border-slate-600 text-slate-300 hover:bg-slate-700/40 justify-center"
                                      onClick={() => clearExpiryAction(item)}
                                    >
                                      Limpar
                                    </Button>
                                  )}
                                </div>
                              </TableCell>
                            )}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  ) : (
                    <div className="text-center py-8 text-slate-500">
                      <Clock className="w-12 h-12 mx-auto mb-3 opacity-50" />
                      <p>Sem produtos para os filtros escolhidos</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              <Dialog open={showExpiryCampaignModal} onOpenChange={setShowExpiryCampaignModal}>
                <DialogContent className="bg-slate-900 border-slate-800 max-w-lg">
                  <DialogHeader>
                    <DialogTitle className="text-white">Criar campanha a partir da validade</DialogTitle>
                  </DialogHeader>

                  <div className="space-y-4">
                    {selectedExpiryItem && (
                      <div className="rounded-lg border border-slate-700 bg-slate-800 p-3 text-sm text-slate-300">
                        <p><strong className="text-white">Produto:</strong> {selectedExpiryItem.product_name}</p>
                        <p><strong className="text-white">Categoria:</strong> {selectedExpiryItem.category || '-'}</p>
                        <p><strong className="text-white">Lote:</strong> {selectedExpiryItem.lot_number || '-'}</p>
                        <p><strong className="text-white">Validade:</strong> {selectedExpiryItem.expiry_date || '-'}</p>
                      </div>
                    )}

                    <div>
                      <Label className="text-slate-300">Nome da campanha</Label>
                      <Input
                        value={expiryCampaignForm.name}
                        onChange={(e) => setExpiryCampaignForm(prev => ({ ...prev, name: e.target.value }))}
                        className="bg-slate-800 border-slate-700"
                      />
                    </div>

                    <div>
                      <Label className="text-slate-300">Descrição</Label>
                      <Input
                        value={expiryCampaignForm.description}
                        onChange={(e) => setExpiryCampaignForm(prev => ({ ...prev, description: e.target.value }))}
                        className="bg-slate-800 border-slate-700"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-slate-300">Tipo de desconto</Label>
                        <Select
                          value={expiryCampaignForm.campaign_type}
                          onValueChange={(val) => setExpiryCampaignForm(prev => ({ ...prev, campaign_type: val }))}
                        >
                          <SelectTrigger className="bg-slate-800 border-slate-700">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="percentage">Percentagem (%)</SelectItem>
                            <SelectItem value="fixed_value">Valor fixo (€)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div>
                        <Label className="text-slate-300">Valor do desconto</Label>
                        <Input
                          type="number"
                          min="0"
                          value={expiryCampaignForm.discount_value}
                          onChange={(e) => setExpiryCampaignForm(prev => ({ ...prev, discount_value: parseFloat(String(e.target.value).replace(',', '.')) || 0 }))}
                          className="bg-slate-800 border-slate-700"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-slate-300">Data de início</Label>
                        <Input
                          type="date"
                          value={expiryCampaignForm.start_date}
                          onChange={(e) => setExpiryCampaignForm(prev => ({ ...prev, start_date: e.target.value }))}
                          className="bg-slate-800 border-slate-700"
                        />
                      </div>

                      <div>
                        <Label className="text-slate-300">Data de fim</Label>
                        <Input
                          type="date"
                          value={expiryCampaignForm.end_date}
                          onChange={(e) => setExpiryCampaignForm(prev => ({ ...prev, end_date: e.target.value }))}
                          className="bg-slate-800 border-slate-700"
                        />
                      </div>
                    </div>

                    <div>
                      <Label className="text-slate-300">Prefixo do voucher</Label>
                      <Input
                        value={expiryCampaignForm.voucher_prefix}
                        onChange={(e) => setExpiryCampaignForm(prev => ({ ...prev, voucher_prefix: e.target.value.toUpperCase() }))}
                        className="bg-slate-800 border-slate-700"
                      />
                    </div>
                  </div>

                  <DialogFooter>
                    <Button
                      variant="outline"
                      onClick={() => setShowExpiryCampaignModal(false)}
                      className="border-slate-700"
                    >
                      Cancelar
                    </Button>
                    <Button
                      onClick={createExpiryCampaign}
                      disabled={creatingExpiryCampaign}
                      className="bg-purple-500 hover:bg-purple-600"
                    >
                      {creatingExpiryCampaign ? 'A criar...' : 'Criar campanha'}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
          </>
        </TabsContent>

        {/* Profit/Loss Tab */}
        <TabsContent value="profit">
          {profitLoss && (
            <div className="space-y-4">
              {/* Summary */}
              <div className="grid grid-cols-1 md:grid-cols-4 xl:grid-cols-8 gap-4">
                <Card className="card-dark p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-emerald-500/20 flex items-center justify-center">
                      <TrendingUp className="w-5 h-5 text-emerald-400" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-400">Receitas</p>
                      <p className="text-xl font-bold text-emerald-400">{formatCurrency(profitLoss.total_revenue)}</p>
                    </div>
                  </div>
                </Card>
                <Card className="card-dark p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-rose-500/20 flex items-center justify-center">
                      <TrendingDown className="w-5 h-5 text-rose-400" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-400">Despesas</p>
                      <p className="text-xl font-bold text-rose-400">{formatCurrency(profitLoss.total_expenses)}</p>
                    </div>
                  </div>
                </Card>
                <Card className="card-dark p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-amber-500/20 flex items-center justify-center">
                      <Package className="w-5 h-5 text-amber-400" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-400">Custo Mercadoria</p>
                      <p className="text-xl font-bold text-amber-400">{formatCurrency(profitLoss.cost_of_goods)}</p>
                    </div>
                  </div>
                </Card>
                <Card className="card-dark p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-rose-500/20 flex items-center justify-center">
                      <Package className="w-5 h-5 text-rose-400" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-400">Perdas de Stock</p>
                      <p className="text-xl font-bold text-rose-400">{formatCurrency(profitLoss.total_stock_losses)}</p>
                    </div>
                  </div>
                </Card>
                <Card className="card-dark p-4">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${profitLoss.net_profit >= 0 ? 'bg-emerald-500/20' : 'bg-rose-500/20'}`}>
                      <Euro className={`w-5 h-5 ${profitLoss.net_profit >= 0 ? 'text-emerald-400' : 'text-rose-400'}`} />
                    </div>
                    <div>
                      <p className="text-xs text-slate-400">Lucro Líquido</p>
                      <p className={`text-xl font-bold ${profitLoss.net_profit >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                        {formatCurrency(profitLoss.net_profit)}
                      </p>
                    </div>
                  </div>
                </Card>
                <Card className="card-dark p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-sky-500/20 flex items-center justify-center">
                      <Receipt className="w-5 h-5 text-sky-400" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-400">IVA</p>
                      <p className="text-xl font-bold text-sky-400">{formatCurrency(profitLoss.total_vat)}</p>
                    </div>
                  </div>
                </Card>
                <Card className="card-dark p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-emerald-500/20 flex items-center justify-center">
                      <Wallet className="w-5 h-5 text-emerald-400" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-400">Despesas pagas</p>
                      <p className="text-xl font-bold text-emerald-400">{formatCurrency(profitLoss.paid_expenses)}</p>
                    </div>
                  </div>
                </Card>
                <Card className="card-dark p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-orange-500/20 flex items-center justify-center">
                      <Landmark className="w-5 h-5 text-orange-400" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-400">Despesas pendentes</p>
                      <p className="text-xl font-bold text-orange-400">{formatCurrency(profitLoss.pending_expenses)}</p>
                    </div>
                  </div>
                </Card>
              </div>

              {/* Breakdown */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Revenue Breakdown */}
                <Card className="card-dark">
                  <CardHeader>
                    <CardTitle className="text-white text-lg">Detalhe Receitas</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex justify-between items-center p-3 bg-slate-800 rounded-lg">
                      <span className="text-slate-400">Vendas Totais</span>
                      <span className="text-white font-medium">{formatCurrency(profitLoss.total_revenue)}</span>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-slate-800 rounded-lg">
                      <span className="text-slate-400">Receitas internas</span>
                      <span className="text-sky-400 font-medium">{formatCurrency(profitLoss.total_internal_revenue)}</span>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-slate-800 rounded-lg">
                      <span className="text-slate-400">Receitas fiscais</span>
                      <span className="text-amber-400 font-medium">{formatCurrency(profitLoss.total_fiscal_revenue)}</span>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-slate-800 rounded-lg">
                      <span className="text-slate-400">Margem Bruta</span>
                      <span className="text-emerald-400 font-medium">{formatCurrency(profitLoss.gross_margin)}</span>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-slate-800 rounded-lg">
                      <span className="text-slate-400">% Margem</span>
                      <span className="text-white font-medium">{profitLoss.margin_percent?.toFixed(1)}%</span>
                    </div>
                  </CardContent>
                </Card>

                {/* Expense Breakdown */}
                <Card className="card-dark">
                  <CardHeader>
                    <CardTitle className="text-white text-lg">Detalhe Despesas</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex justify-between items-center p-3 bg-slate-800 rounded-lg">
                      <span className="text-slate-400">Total despesas</span>
                      <span className="text-rose-400 font-medium">{formatCurrency(profitLoss.total_expenses)}</span>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-slate-800 rounded-lg">
                      <span className="text-slate-400">Pagas</span>
                      <span className="text-emerald-400 font-medium">{formatCurrency(profitLoss.paid_expenses)}</span>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-slate-800 rounded-lg">
                      <span className="text-slate-400">Pendentes</span>
                      <span className="text-orange-400 font-medium">{formatCurrency(profitLoss.pending_expenses)}</span>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-slate-800 rounded-lg">
                      <span className="text-slate-400">Perdas de stock (sem IVA)</span>
                      <span className="text-rose-400 font-medium">{formatCurrency(profitLoss.total_stock_losses)}</span>
                    </div>
                    {profitLoss.expenses_by_category && Object.entries(profitLoss.expenses_by_category).map(([cat, value]) => (
                      <div key={cat} className="flex justify-between items-center p-3 bg-slate-800 rounded-lg">
                        <span className="text-slate-400 capitalize">{cat.replace('_', ' ')}</span>
                        <span className="text-rose-400 font-medium">{formatCurrency(value)}</span>
                      </div>
                    ))}
                    {(!profitLoss.expenses_by_category || Object.keys(profitLoss.expenses_by_category).length === 0) && (
                      <p className="text-slate-500 text-center py-4">Sem despesas no período</p>
                    )}
                  </CardContent>
                </Card>

                {/* Payment Methods Breakdown */}
                <Card className="card-dark">
                  <CardHeader>
                    <CardTitle className="text-white text-lg">Recebimentos por Método</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {profitLoss.by_payment_method && Object.entries(profitLoss.by_payment_method).map(([method, data]) => (
                      <div key={method} className="p-3 bg-slate-800 rounded-lg">
                        <div className="flex items-center justify-between gap-3">
                          <span className="text-slate-400 capitalize">{method.replace('_', ' ')}</span>
                          <span className="text-white font-medium">{formatCurrency(data?.total || 0)}</span>
                        </div>
                        <p className="text-xs text-slate-500 mt-1">{data?.count || 0} transação(ões)</p>
                      </div>
                    ))}
                    {(!profitLoss.by_payment_method || Object.keys(profitLoss.by_payment_method).length === 0) && (
                      <p className="text-slate-500 text-center py-4">Sem recebimentos no período</p>
                    )}
                  </CardContent>
                </Card>
              </div>
            </div>
          )}
        </TabsContent>

        <TabsContent value="financial">
          {financialOverview && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-4 xl:grid-cols-9 gap-4">
                <Card className="card-dark p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-emerald-500/20 flex items-center justify-center">
                      <CircleDollarSign className="w-5 h-5 text-emerald-400" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-400">Receitas</p>
                      <p className="text-xl font-bold text-emerald-400">{formatCurrency(financialOverview.summary?.total_revenue)}</p>
                    </div>
                  </div>
                </Card>

                <Card className="card-dark p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-rose-500/20 flex items-center justify-center">
                      <TrendingDown className="w-5 h-5 text-rose-400" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-400">Despesas</p>
                      <p className="text-xl font-bold text-rose-400">{formatCurrency(financialOverview.summary?.total_expenses)}</p>
                    </div>
                  </div>
                </Card>

                <Card className="card-dark p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-amber-500/20 flex items-center justify-center">
                      <Package className="w-5 h-5 text-amber-400" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-400">Custo Mercadoria</p>
                      <p className="text-xl font-bold text-amber-400">{formatCurrency(financialOverview.summary?.cost_of_goods)}</p>
                    </div>
                  </div>
                </Card>

                <Card className="card-dark p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-rose-500/20 flex items-center justify-center">
                      <Package className="w-5 h-5 text-rose-400" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-400">Perdas de Stock</p>
                      <p className="text-xl font-bold text-rose-400">{formatCurrency(financialOverview.summary?.total_stock_losses)}</p>
                    </div>
                  </div>
                </Card>

                <Card className="card-dark p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-sky-500/20 flex items-center justify-center">
                      <Stethoscope className="w-5 h-5 text-sky-400" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-400">Margem Clínica</p>
                      <p className="text-xl font-bold text-sky-400">{formatCurrency(financialOverview.summary?.total_clinical_margin)}</p>
                    </div>
                  </div>
                </Card>

                <Card className="card-dark p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-blue-500/20 flex items-center justify-center">
                      <Users className="w-5 h-5 text-blue-400" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-400">Pago Prestadores</p>
                      <p className="text-xl font-bold text-blue-400">{formatCurrency(financialOverview.summary?.total_provider_paid)}</p>
                    </div>
                  </div>
                </Card>

                <Card className="card-dark p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-orange-500/20 flex items-center justify-center">
                      <Users className="w-5 h-5 text-orange-400" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-400">Aberto Prestadores</p>
                      <p className="text-xl font-bold text-orange-400">{formatCurrency(financialOverview.summary?.total_provider_open)}</p>
                    </div>
                  </div>
                </Card>

                <Card className="card-dark p-4">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${(financialOverview.summary?.net_profit || 0) >= 0 ? 'bg-emerald-500/20' : 'bg-rose-500/20'}`}>
                      <Euro className={`w-5 h-5 ${(financialOverview.summary?.net_profit || 0) >= 0 ? 'text-emerald-400' : 'text-rose-400'}`} />
                    </div>
                    <div>
                      <p className="text-xs text-slate-400">Lucro Líquido</p>
                      <p className={`text-xl font-bold ${(financialOverview.summary?.net_profit || 0) >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                        {formatCurrency(financialOverview.summary?.net_profit)}
                      </p>
                    </div>
                  </div>
                </Card>

                <Card className="card-dark p-4">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                      financialOverview.health?.status === 'healthy'
                        ? 'bg-emerald-500/20'
                        : financialOverview.health?.status === 'attention'
                          ? 'bg-amber-500/20'
                          : 'bg-rose-500/20'
                    }`}>
                      <Activity className={`w-5 h-5 ${
                        financialOverview.health?.status === 'healthy'
                          ? 'text-emerald-400'
                          : financialOverview.health?.status === 'attention'
                            ? 'text-amber-400'
                            : 'text-rose-400'
                      }`} />
                    </div>
                    <div>
                      <p className="text-xs text-slate-400">Estado</p>
                      <p className={`text-xl font-bold ${
                        financialOverview.health?.status === 'healthy'
                          ? 'text-emerald-400'
                          : financialOverview.health?.status === 'attention'
                            ? 'text-amber-400'
                            : 'text-rose-400'
                      }`}>
                        {financialOverview.health?.label || '-'}
                      </p>
                    </div>
                  </div>
                </Card>
              </div>

              <Card className="card-dark">
                <CardHeader>
                  <CardTitle className="text-white text-lg">Painel Financeiro Integrado</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
                    <div className="p-4 bg-slate-800 rounded-lg">
                      <p className="text-xs text-slate-400">IVA estimado</p>
                      <p className={`text-xl font-bold mt-1 ${integratedFinancialData.vatNet > 0 ? 'text-amber-400' : integratedFinancialData.vatNet < 0 ? 'text-emerald-400' : 'text-white'}`}>
                        {formatCurrency(integratedFinancialData.vatNet)}
                      </p>
                      <p className="text-xs text-slate-500 mt-1">{integratedFinancialData.vatLabel}</p>
                    </div>

                    <div className="p-4 bg-slate-800 rounded-lg">
                      <p className="text-xs text-slate-400">Compras pendentes</p>
                      <p className="text-xl font-bold text-amber-400 mt-1">
                        {formatCurrency(integratedFinancialData.purchasesPending)}
                      </p>
                      <p className="text-xs text-slate-500 mt-1">
                        Total comprado: {formatCurrency(integratedFinancialData.purchasesTotal)}
                      </p>
                    </div>

                    <div className="p-4 bg-slate-800 rounded-lg">
                      <p className="text-xs text-slate-400">Fornecedores vencidos</p>
                      <p className="text-xl font-bold text-rose-400 mt-1">
                        {formatCurrency(integratedFinancialData.suppliersOverdue)}
                      </p>
                      <p className="text-xs text-slate-500 mt-1">
                        Por vencer: {formatCurrency(integratedFinancialData.supplierNotDue)}
                      </p>
                    </div>

                    <div className="p-4 bg-slate-800 rounded-lg">
                      <p className="text-xs text-slate-400">Prestadores em aberto</p>
                      <p className="text-xl font-bold text-orange-400 mt-1">
                        {formatCurrency(integratedFinancialData.providerOpen)}
                      </p>
                      <p className="text-xs text-slate-500 mt-1">
                        Pressão total: {formatCurrency(integratedFinancialData.pressureTotal)}
                      </p>
                    </div>

                    <div className="p-4 bg-slate-800 rounded-lg">
                      <p className="text-xs text-slate-400">IVA a pagar</p>
                      <p className="text-xl font-bold text-amber-400 mt-1">
                        {formatCurrency(integratedFinancialData.vatPayable)}
                      </p>
                      <p className="text-xs text-slate-500 mt-1">
                        IVA a recuperar: {formatCurrency(integratedFinancialData.vatRecoverable)}
                      </p>
                    </div>

                    <div className="p-4 bg-slate-800 rounded-lg">
                      <p className="text-xs text-slate-400">Prestadores pagos</p>
                      <p className="text-xl font-bold text-sky-400 mt-1">
                        {formatCurrency(integratedFinancialData.providerPaid)}
                      </p>
                      <p className="text-xs text-slate-500 mt-1">
                        Em aberto: {formatCurrency(integratedFinancialData.providerOpen)}
                      </p>
                    </div>

                    <div className="p-4 bg-slate-800 rounded-lg">
                      <p className="text-xs text-slate-400">Pressão vs recebimentos</p>
                      <p className="text-xl font-bold text-white mt-1">
                        {integratedFinancialData.pressureVsReceiptsPercent !== null
                          ? `${Number(integratedFinancialData.pressureVsReceiptsPercent || 0).toFixed(1)}%`
                          : 'n/d'}
                      </p>
                      <p className={`text-xs mt-1 ${integratedFinancialData.riskClass}`}>
                        Risco: {integratedFinancialData.riskLabel}
                      </p>
                    </div>

                    <div className="p-4 bg-slate-800 rounded-lg">
                      <p className="text-xs text-slate-400">Compras vencidas</p>
                      <p className="text-xl font-bold text-rose-400 mt-1">
                        {integratedFinancialData.overduePurchasesPercent !== null
                          ? `${Number(integratedFinancialData.overduePurchasesPercent || 0).toFixed(1)}%`
                          : 'n/d'}
                      </p>
                      <p className="text-xs text-slate-500 mt-1">
                        Sobre compras pendentes
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <div className="p-4 bg-slate-800 rounded-lg">
                      <p className="text-sm text-slate-300 font-medium">Leitura financeira rápida</p>
                      <div className="space-y-2 mt-3 text-sm">
                        <div className="flex items-center justify-between gap-3">
                          <span className="text-slate-400">IVA</span>
                          <span className={integratedFinancialData.vatNet > 0 ? 'text-amber-400 font-medium' : integratedFinancialData.vatNet < 0 ? 'text-emerald-400 font-medium' : 'text-white font-medium'}>
                            {integratedFinancialData.vatLabel}
                          </span>
                        </div>
                        <div className="flex items-center justify-between gap-3">
                          <span className="text-slate-400">Risco fornecedores</span>
                          <span className="text-rose-400 font-medium">{formatCurrency(integratedFinancialData.suppliersOverdue)}</span>
                        </div>
                        <div className="flex items-center justify-between gap-3">
                          <span className="text-slate-400">Risco prestadores</span>
                          <span className="text-orange-400 font-medium">{formatCurrency(integratedFinancialData.providerOpen)}</span>
                        </div>
                        <div className="flex items-center justify-between gap-3">
                          <span className="text-slate-300">Pressão financeira total</span>
                          <span className="text-white font-semibold">{formatCurrency(integratedFinancialData.pressureTotal)}</span>
                        </div>
                      </div>
                    </div>

                    <div className="p-4 bg-slate-800 rounded-lg">
                      <p className="text-sm text-slate-300 font-medium">Composição da pressão</p>
                      <div className="space-y-2 mt-3 text-sm">
                        <div className="flex items-center justify-between gap-3">
                          <span className="text-slate-400">IVA a pagar considerado</span>
                          <span className="text-amber-400 font-medium">{formatCurrency(Math.max(0, integratedFinancialData.vatNet))}</span>
                        </div>
                        <div className="flex items-center justify-between gap-3">
                          <span className="text-slate-400">Compras pendentes</span>
                          <span className="text-amber-400 font-medium">{formatCurrency(integratedFinancialData.purchasesPending)}</span>
                        </div>
                        <div className="flex items-center justify-between gap-3">
                          <span className="text-slate-400">Prestadores em aberto</span>
                          <span className="text-orange-400 font-medium">{formatCurrency(integratedFinancialData.providerOpen)}</span>
                        </div>
                        <div className="flex items-center justify-between gap-3">
                          <span className="text-slate-300">Total integrado</span>
                          <span className="text-white font-semibold">{formatCurrency(integratedFinancialData.pressureTotal)}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card className="card-dark">
                  <CardHeader>
                    <CardTitle className="text-white text-lg">Resumo Executivo</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex justify-between items-center p-3 bg-slate-800 rounded-lg">
                      <span className="text-slate-400">Recebimentos</span>
                      <span className="text-white font-medium">{formatCurrency(financialOverview.summary?.total_receipts)}</span>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-slate-800 rounded-lg">
                      <span className="text-slate-400">Saldo Operacional</span>
                      <span className={`font-medium ${(financialOverview.summary?.operational_balance || 0) >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                        {formatCurrency(financialOverview.summary?.operational_balance)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-slate-800 rounded-lg">
                      <span className="text-slate-400">IVA</span>
                      <span className="text-sky-400 font-medium">{formatCurrency(financialOverview.summary?.total_vat)}</span>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-slate-800 rounded-lg">
                      <span className="text-slate-400">% Margem</span>
                      <span className="text-white font-medium">{financialOverview.summary?.margin_percent?.toFixed(1)}%</span>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-slate-800 rounded-lg">
                      <span className="text-slate-400">Serviços</span>
                      <span className="text-sky-400 font-medium">{formatCurrency(financialOverview.summary?.total_service_value)}</span>
                    </div>
                  </CardContent>
                </Card>

                <Card className="card-dark">
                  <CardHeader>
                    <CardTitle className="text-white text-lg">Recebimentos por Método</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {financialOverview.by_payment_method && Object.entries(financialOverview.by_payment_method).map(([method, data]) => (
                      <div key={method} className="p-3 bg-slate-800 rounded-lg">
                        <div className="flex items-center justify-between gap-3">
                          <span className="text-slate-400 capitalize">{method.replace('_', ' ')}</span>
                          <span className="text-white font-medium">{formatCurrency(data?.total || 0)}</span>
                        </div>
                        <p className="text-xs text-slate-500 mt-1">{data?.count || 0} transação(ões)</p>
                      </div>
                    ))}
                    {(!financialOverview.by_payment_method || Object.keys(financialOverview.by_payment_method).length === 0) && (
                      <p className="text-slate-500 text-center py-4">Sem recebimentos no período</p>
                    )}
                  </CardContent>
                </Card>

                <Card className="card-dark">
                  <CardHeader>
                    <CardTitle className="text-white text-lg">Despesas por Categoria</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {financialOverview.expenses_by_category && Object.entries(financialOverview.expenses_by_category).map(([cat, value]) => (
                      <div key={cat} className="flex justify-between items-center p-3 bg-slate-800 rounded-lg">
                        <span className="text-slate-400 capitalize">{cat.replace('_', ' ')}</span>
                        <span className="text-rose-400 font-medium">{formatCurrency(value)}</span>
                      </div>
                    ))}
                    {(!financialOverview.expenses_by_category || Object.keys(financialOverview.expenses_by_category).length === 0) && (
                      <p className="text-slate-500 text-center py-4">Sem despesas no período</p>
                    )}
                  </CardContent>
                </Card>
              </div>
            </div>
          )}
        </TabsContent>

        <TabsContent value="tax">
          {taxOverview && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-4 xl:grid-cols-7 gap-4">
                <Card className="card-dark p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-emerald-500/20 flex items-center justify-center">
                      <Receipt className="w-5 h-5 text-emerald-400" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-400">IVA fiscal relevante</p>
                      <p className="text-xl font-bold text-emerald-400">{formatCurrency(taxOverview.summary?.vat_sales_fiscal)}</p>
                    </div>
                  </div>
                </Card>

                <Card className="card-dark p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-sky-500/20 flex items-center justify-center">
                      <Receipt className="w-5 h-5 text-sky-400" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-400">IVA interno informativo</p>
                      <p className="text-xl font-bold text-sky-400">{formatCurrency(taxOverview.summary?.vat_sales_internal)}</p>
                    </div>
                  </div>
                </Card>

                <Card className="card-dark p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-blue-500/20 flex items-center justify-center">
                      <Wallet className="w-5 h-5 text-blue-400" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-400">IVA dedutível compras</p>
                      <p className="text-xl font-bold text-blue-400">{formatCurrency(taxOverview.summary?.vat_purchase_deductible)}</p>
                    </div>
                  </div>
                </Card>

                <Card className="card-dark p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-cyan-500/20 flex items-center justify-center">
                      <Wallet className="w-5 h-5 text-cyan-400" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-400">IVA dedutível despesas</p>
                      <p className="text-xl font-bold text-cyan-400">{formatCurrency(taxOverview.summary?.vat_expenses_deductible)}</p>
                    </div>
                  </div>
                </Card>

                <Card className="card-dark p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-indigo-500/20 flex items-center justify-center">
                      <BadgeEuro className="w-5 h-5 text-indigo-400" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-400">IVA dedutível total</p>
                      <p className="text-xl font-bold text-indigo-400">{formatCurrency(taxOverview.summary?.vat_deductible_total)}</p>
                    </div>
                  </div>
                </Card>

                <Card className="card-dark p-4">
                  <div className={`flex items-center gap-3`}>
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${(taxOverview.summary?.vat_net_estimated || 0) <= 0 ? 'bg-emerald-500/20' : 'bg-amber-500/20'}`}>
                      <Euro className={`w-5 h-5 ${(taxOverview.summary?.vat_net_estimated || 0) <= 0 ? 'text-emerald-400' : 'text-amber-400'}`} />
                    </div>
                    <div>
                      <p className="text-xs text-slate-400">Saldo IVA estimado</p>
                      <p className={`text-xl font-bold ${(taxOverview.summary?.vat_net_estimated || 0) <= 0 ? 'text-emerald-400' : 'text-amber-400'}`}>
                        {formatCurrency(taxOverview.summary?.vat_net_estimated)}
                      </p>
                    </div>
                  </div>
                </Card>

                <Card className="card-dark p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-purple-500/20 flex items-center justify-center">
                      <Package className="w-5 h-5 text-purple-400" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-400">Compras período</p>
                      <p className="text-xl font-bold text-purple-400">{formatCurrency(taxOverview.summary?.total_purchases)}</p>
                    </div>
                  </div>
                </Card>

                <Card className="card-dark p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-orange-500/20 flex items-center justify-center">
                      <Landmark className="w-5 h-5 text-orange-400" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-400">Compras pendentes</p>
                      <p className="text-xl font-bold text-orange-400">{formatCurrency(taxOverview.summary?.total_purchases_pending)}</p>
                    </div>
                  </div>
                </Card>

                <Card className="card-dark p-4">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                      taxOverview.status?.status === 'recoverable'
                        ? 'bg-emerald-500/20'
                        : taxOverview.status?.status === 'payable'
                          ? 'bg-amber-500/20'
                          : 'bg-slate-500/20'
                    }`}>
                      <BadgeEuro className={`w-5 h-5 ${
                        taxOverview.status?.status === 'recoverable'
                          ? 'text-emerald-400'
                          : taxOverview.status?.status === 'payable'
                            ? 'text-amber-400'
                            : 'text-slate-300'
                      }`} />
                    </div>
                    <div>
                      <p className="text-xs text-slate-400">Estado Fiscal</p>
                      <p className={`text-xl font-bold ${
                        taxOverview.status?.status === 'recoverable'
                          ? 'text-emerald-400'
                          : taxOverview.status?.status === 'payable'
                            ? 'text-amber-400'
                            : 'text-slate-300'
                      }`}>
                        {taxOverview.status?.label || '-'}
                      </p>
                    </div>
                  </div>
                </Card>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Card className="card-dark">
                  <CardHeader>
                    <CardTitle className="text-white text-lg">Resumo Fiscal</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex justify-between items-center p-3 bg-slate-800 rounded-lg">
                      <span className="text-slate-400">Vendas fiscais</span>
                      <span className="text-white font-medium">{taxOverview.summary?.sales_count_fiscal || 0}</span>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-slate-800 rounded-lg">
                      <span className="text-slate-400">Vendas internas</span>
                      <span className="text-white font-medium">{taxOverview.summary?.sales_count_internal || 0}</span>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-slate-800 rounded-lg">
                      <span className="text-slate-400">IVA fiscal relevante</span>
                      <span className="text-emerald-400 font-medium">{formatCurrency(taxOverview.summary?.vat_sales_fiscal)}</span>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-slate-800 rounded-lg">
                      <span className="text-slate-400">IVA interno informativo</span>
                      <span className="text-sky-400 font-medium">{formatCurrency(taxOverview.summary?.vat_sales_internal)}</span>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-slate-800 rounded-lg">
                      <span className="text-slate-400">IVA dedutível compras</span>
                      <span className="text-blue-400 font-medium">{formatCurrency(taxOverview.summary?.vat_purchase_deductible)}</span>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-slate-800 rounded-lg">
                      <span className="text-slate-400">IVA dedutível despesas</span>
                      <span className="text-cyan-400 font-medium">{formatCurrency(taxOverview.summary?.vat_expenses_deductible)}</span>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-slate-800 rounded-lg">
                      <span className="text-slate-400">IVA dedutível total</span>
                      <span className="text-indigo-400 font-medium">{formatCurrency(taxOverview.summary?.vat_deductible_total)}</span>
                    </div>
                  </CardContent>
                </Card>

                <Card className="card-dark">
                  <CardHeader>
                    <CardTitle className="text-white text-lg">Notas Metodológicas</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {taxOverview.notes && Object.entries(taxOverview.notes).map(([key, value]) => (
                      <div key={key} className="p-3 bg-slate-800 rounded-lg">
                        <p className="text-slate-300 text-sm">{value}</p>
                      </div>
                    ))}
                    {(!taxOverview.notes || Object.keys(taxOverview.notes).length === 0) && (
                      <p className="text-slate-500 text-center py-4">Sem notas disponíveis</p>
                    )}
                  </CardContent>
                </Card>
              </div>
            </div>
          )}
        </TabsContent>

        <TabsContent value="prepayments">
          {prepaymentsDetail && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-bold text-white">Pré-pagamentos</h2>
                  <p className="text-slate-400">Controlo de valores recebidos antecipadamente e respetivo consumo em vendas</p>
                </div>

                <Button
                  variant="outline"
                  onClick={handleRebuildPrepaymentsLedger}
                  disabled={rebuildingPrepayments}
                  className="border-slate-700"
                >
                  <RefreshCw className={`w-4 h-4 mr-2 ${rebuildingPrepayments ? 'animate-spin' : ''}`} />
                  Reconstruir ledger
                </Button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card className="card-dark p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-emerald-500/20 flex items-center justify-center">
                      <Wallet className="w-5 h-5 text-emerald-400" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-400">Recebido encomendas</p>
                      <p className="text-xl font-bold text-white">{formatCurrency(prepaymentsDetail.summary?.received_customer_orders)}</p>
                    </div>
                  </div>
                </Card>

                <Card className="card-dark p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-cyan-500/20 flex items-center justify-center">
                      <Stethoscope className="w-5 h-5 text-cyan-400" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-400">Recebido consultas</p>
                      <p className="text-xl font-bold text-white">{formatCurrency(prepaymentsDetail.summary?.received_consultations)}</p>
                    </div>
                  </div>
                </Card>

                <Card className="card-dark p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-amber-500/20 flex items-center justify-center">
                      <Receipt className="w-5 h-5 text-amber-400" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-400">Consumido em vendas</p>
                      <p className="text-xl font-bold text-white">{formatCurrency(prepaymentsDetail.summary?.consumed_in_sales)}</p>
                    </div>
                  </div>
                </Card>

                <Card className="card-dark p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-rose-500/20 flex items-center justify-center">
                      <BadgeEuro className="w-5 h-5 text-rose-400" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-400">Em aberto</p>
                      <p className="text-xl font-bold text-white">{formatCurrency(prepaymentsDetail.summary?.open_total_estimated)}</p>
                    </div>
                  </div>
                </Card>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card className="card-dark p-4">
                  <p className="text-xs text-slate-400">Consumos ligados a encomendas</p>
                  <p className="text-lg font-bold text-emerald-400">{formatCurrency(prepaymentsDetail.summary?.consumed_linked_customer_orders)}</p>
                </Card>

                <Card className="card-dark p-4">
                  <p className="text-xs text-slate-400">Consumos sem ligação</p>
                  <p className="text-lg font-bold text-amber-400">{formatCurrency(prepaymentsDetail.summary?.consumed_unlinked_sales)}</p>
                </Card>

                <Card className="card-dark p-4">
                  <p className="text-xs text-slate-400">Vendas consumidas ligadas</p>
                  <p className="text-lg font-bold text-white">{prepaymentsDetail.summary?.linked_consumed_count || 0}</p>
                </Card>

                <Card className="card-dark p-4">
                  <p className="text-xs text-slate-400">Vendas consumidas sem ligação</p>
                  <p className="text-lg font-bold text-white">{prepaymentsDetail.summary?.unlinked_consumed_count || 0}</p>
                </Card>
              </div>

              <Card className="card-dark">
                <CardHeader>
                  <CardTitle className="text-white text-lg">Encomendas com pré-pagamento em aberto</CardTitle>
                </CardHeader>
                <CardContent>
                  {(prepaymentsDetail.customer_orders_open || []).length > 0 ? (
                    <Table className="table-dark">
                      <TableHeader>
                        <TableRow>
                          <TableHead>Encomenda</TableHead>
                          <TableHead>Cliente</TableHead>
                          <TableHead>Método</TableHead>
                          <TableHead>Estado</TableHead>
                          <TableHead className="text-right">Recebido</TableHead>
                          <TableHead className="text-right">Consumido</TableHead>
                          <TableHead className="text-right">Em aberto</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {(prepaymentsDetail.customer_orders_open || []).map((row) => (
                          <TableRow key={row.source_id}>
                            <TableCell className="text-white font-medium">{row.source_number || '-'}</TableCell>
                            <TableCell className="text-slate-300">{row.customer_name || '-'}</TableCell>
                            <TableCell className="text-slate-400">{row.payment_method || '-'}</TableCell>
                            <TableCell className="text-slate-400">{row.payment_status || '-'}</TableCell>
                            <TableCell className="text-right text-cyan-400">{formatCurrency(row.received_amount)}</TableCell>
                            <TableCell className="text-right text-amber-400">{formatCurrency(row.consumed_amount)}</TableCell>
                            <TableCell className="text-right text-rose-400 font-medium">{formatCurrency(row.open_amount)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  ) : (
                    <p className="text-slate-400">Sem encomendas com pré-pagamentos em aberto.</p>
                  )}
                </CardContent>
              </Card>

              <Card className="card-dark">
                <CardHeader>
                  <CardTitle className="text-white text-lg">Consultas pré-pagas em aberto</CardTitle>
                </CardHeader>
                <CardContent>
                  {(prepaymentsDetail.consultations_open || []).length > 0 ? (
                    <Table className="table-dark">
                      <TableHeader>
                        <TableRow>
                          <TableHead>Pedido</TableHead>
                          <TableHead>Cliente</TableHead>
                          <TableHead>Método</TableHead>
                          <TableHead>Estado</TableHead>
                          <TableHead className="text-right">Recebido</TableHead>
                          <TableHead className="text-right">Consumido</TableHead>
                          <TableHead className="text-right">Em aberto</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {(prepaymentsDetail.consultations_open || []).map((row) => (
                          <TableRow key={row.source_id}>
                            <TableCell className="text-white font-medium">{row.source_number || '-'}</TableCell>
                            <TableCell className="text-slate-300">{row.customer_name || '-'}</TableCell>
                            <TableCell className="text-slate-400">{row.payment_method || '-'}</TableCell>
                            <TableCell className="text-slate-400">{row.payment_status || '-'}</TableCell>
                            <TableCell className="text-right text-cyan-400">{formatCurrency(row.received_amount)}</TableCell>
                            <TableCell className="text-right text-amber-400">{formatCurrency(row.consumed_amount)}</TableCell>
                            <TableCell className="text-right text-rose-400 font-medium">{formatCurrency(row.open_amount)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  ) : (
                    <p className="text-slate-400">Sem consultas pré-pagas em aberto.</p>
                  )}
                </CardContent>
              </Card>

              <Card className="card-dark">
                <CardHeader>
                  <CardTitle className="text-white text-lg">Vendas com consumo de pré-pagamento</CardTitle>
                </CardHeader>
                <CardContent>
                  {(prepaymentsDetail.consumed_sales || []).length > 0 ? (
                    <Table className="table-dark">
                      <TableHeader>
                        <TableRow>
                          <TableHead>Venda</TableHead>
                          <TableHead>Cliente</TableHead>
                          <TableHead>Origem</TableHead>
                          <TableHead className="text-right">Consumido</TableHead>
                          <TableHead className="text-right">Falta pagar</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {(prepaymentsDetail.consumed_sales || []).map((row) => (
                          <TableRow key={row.source_id}>
                            <TableCell className="text-white font-medium">{row.source_number || '-'}</TableCell>
                            <TableCell className="text-slate-300">{row.customer_name || '-'}</TableCell>
                            <TableCell className="text-slate-400">{row.linked_source_number || row.linked_source_type || '-'}</TableCell>
                            <TableCell className="text-right text-amber-400">{formatCurrency(row.amount)}</TableCell>
                            <TableCell className="text-right text-rose-400">{formatCurrency(row.remaining_amount)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  ) : (
                    <p className="text-slate-400">Sem vendas com consumo de pré-pagamentos.</p>
                  )}
                </CardContent>
              </Card>
            </div>
          )}
        </TabsContent>


        <TabsContent value="categories">
          <div className="space-y-4">
            {Array.isArray(topCategories) && topCategories.length > 0 ? (
              <>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <Card className="card-dark p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-emerald-500/20 flex items-center justify-center">
                        <Euro className="w-5 h-5 text-emerald-400" />
                      </div>
                      <div>
                        <p className="text-xs text-slate-400">Faturação categorias</p>
                        <p className="text-xl font-bold text-white">
                          {formatCurrency(topCategories.reduce((acc, item) => acc + Number(item.total || 0), 0))}
                        </p>
                      </div>
                    </div>
                  </Card>

                  <Card className="card-dark p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-sky-500/20 flex items-center justify-center">
                        <Package className="w-5 h-5 text-sky-400" />
                      </div>
                      <div>
                        <p className="text-xs text-slate-400">Custo total</p>
                        <p className="text-xl font-bold text-sky-400">
                          {formatCurrency(topCategories.reduce((acc, item) => acc + Number(item.cost || 0), 0))}
                        </p>
                      </div>
                    </div>
                  </Card>

                  <Card className="card-dark p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-amber-500/20 flex items-center justify-center">
                        <TrendingUp className="w-5 h-5 text-amber-400" />
                      </div>
                      <div>
                        <p className="text-xs text-slate-400">Margem total</p>
                        <p className="text-xl font-bold text-amber-400">
                          {formatCurrency(topCategories.reduce((acc, item) => acc + Number(item.margin || 0), 0))}
                        </p>
                      </div>
                    </div>
                  </Card>

                  <Card className="card-dark p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-purple-500/20 flex items-center justify-center">
                        <BarChart3 className="w-5 h-5 text-purple-400" />
                      </div>
                      <div>
                        <p className="text-xs text-slate-400">N.º categorias</p>
                        <p className="text-xl font-bold text-white">{topCategories.length}</p>
                      </div>
                    </div>
                  </Card>
                </div>

                <Card className="card-dark">
                  <CardHeader>
                    <CardTitle className="text-white text-lg">Resumo por categoria</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {topCategories.map((item, idx) => (
                        <div key={`${item.category}-${idx}`} className="p-3 bg-slate-800 rounded-lg">
                          <div className="flex items-center justify-between gap-3">
                            <span className="text-slate-200 font-medium">{item.category || 'SEM CATEGORIA'}</span>
                            <span className="text-white font-semibold">{formatCurrency(item.total || 0)}</span>
                          </div>

                          <div className="flex items-center justify-between gap-3 mt-1 text-xs">
                            <span className="text-slate-500">{Number(item.quantity || 0).toFixed(2)} un.</span>
                            <span className="text-slate-500">{Number(item.sales_count || 0)} linhas</span>
                          </div>

                          <div className="flex items-center justify-between gap-3 mt-1 text-xs">
                            <span className="text-sky-400">{formatCurrency(item.cost || 0)} custo</span>
                            <span className="text-amber-400">{formatCurrency(item.margin || 0)} margem</span>
                          </div>

                          <div className="flex items-center justify-between gap-3 mt-1 text-xs">
                            <span className="text-slate-500">
                              {salesReport?.summary?.total_revenue > 0
                                ? `${(((Number(item.total || 0) / Number(salesReport.summary.total_revenue || 1)) * 100)).toFixed(1)}% do total`
                                : '0,0% do total'}
                            </span>
                            <span className="text-slate-500">{Number(item.margin_percent || 0).toFixed(1)}% margem</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                <Card className="card-dark">
                  <CardHeader>
                    <CardTitle className="text-white text-lg">Comparação com período anterior</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {Array.isArray(managementData.categoryComparisons) && managementData.categoryComparisons.length > 0 ? (
                      <div className="space-y-3">
                        {managementData.categoryComparisons.map((row) => {
                          const positive = Number(row.diff || 0) >= 0;
                          const hasPercent = row.percent !== null && row.percent !== undefined;

                          return (
                            <div key={row.category} className="p-3 bg-slate-800 rounded-lg">
                              <div className="flex items-center justify-between gap-3">
                                <span className="text-slate-300 font-medium">{row.category}</span>
                                <span className="text-white font-semibold">{formatCurrency(row.currentTotal || 0)}</span>
                              </div>

                              <div className="flex items-center justify-between gap-3 mt-1 text-xs">
                                <span className="text-slate-500">Anterior: {formatCurrency(row.previousTotal || 0)}</span>
                                <span className={`${positive ? 'text-emerald-400' : 'text-rose-400'} font-medium`}>
                                  {positive ? '+' : ''}{formatCurrency(row.diff || 0)}
                                </span>
                              </div>

                              <div className="flex items-center justify-end gap-3 mt-1 text-xs">
                                <span className={`${positive ? 'text-emerald-400' : 'text-rose-400'} font-medium`}>
                                  {hasPercent ? `${positive ? '+' : ''}${Number(row.percent || 0).toFixed(1)}%` : 'n/d'}
                                </span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <p className="text-slate-500 text-center py-4">Sem comparação de categorias disponível</p>
                    )}
                  </CardContent>
                </Card>
              </>
            ) : (
              <Card className="card-dark">
                <CardContent className="py-8">
                  <p className="text-slate-500 text-center">Sem dados de categorias no período</p>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>


        <TabsContent value="clients">
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Card className="card-dark p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-emerald-500/20 flex items-center justify-center">
                    <Users className="w-5 h-5 text-emerald-400" />
                  </div>
                  <div>
                    <p className="text-xs text-slate-400">Clientes listados</p>
                    <p className="text-xl font-bold text-white">{managementData.clientsWithMetrics.length}</p>
                  </div>
                </div>
              </Card>

              <Card className="card-dark p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-blue-500/20 flex items-center justify-center">
                    <RefreshCcw className="w-5 h-5 text-blue-400" />
                  </div>
                  <div>
                    <p className="text-xs text-slate-400">Recorrentes</p>
                    <p className="text-xl font-bold text-white">{managementData.recurringCustomers}</p>
                  </div>
                </div>
              </Card>

              <Card className="card-dark p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-amber-500/20 flex items-center justify-center">
                    <User className="w-5 h-5 text-amber-400" />
                  </div>
                  <div>
                    <p className="text-xs text-slate-400">Compra única</p>
                    <p className="text-xl font-bold text-white">{managementData.oneTimeCustomers}</p>
                  </div>
                </div>
              </Card>

              <Card className="card-dark p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-slate-500/20 flex items-center justify-center">
                    <UserX className="w-5 h-5 text-slate-300" />
                  </div>
                  <div>
                    <p className="text-xs text-slate-400">Consumidor final</p>
                    <p className="text-xl font-bold text-white">{managementData.anonymousCustomers}</p>
                  </div>
                </div>
              </Card>
            </div>

            <Card className="card-dark">
              <CardHeader>
                <CardTitle className="text-white text-lg">Resumo Clientes</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between items-center p-3 bg-slate-800 rounded-lg">
                  <span className="text-slate-400">Ticket médio desta lista</span>
                  <span className="text-white font-medium">{formatCurrency(managementData.averageTicketTopCustomers)}</span>
                </div>
                <div className="flex justify-between items-center p-3 bg-slate-800 rounded-lg">
                  <span className="text-slate-400">Maior cliente</span>
                  <span className="text-emerald-400 font-medium">
                    {managementData.clientsWithMetrics[0]?.customerLabel || '-'}
                  </span>
                </div>
              </CardContent>
            </Card>

            <Card className="card-dark">
              <CardHeader>
                <CardTitle className="text-white text-lg">Top Clientes</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {Array.isArray(managementData.clientsWithMetrics) && managementData.clientsWithMetrics.length > 0 ? (
                  managementData.clientsWithMetrics.slice(0, 15).map((item, idx) => (
                    <div key={`${item.customer_id || item.customerLabel}-${idx}`} className="p-3 bg-slate-800 rounded-lg">
                      <div className="flex items-center justify-between gap-3">
                        <span className="text-slate-300 font-medium">{item.customerLabel}</span>
                        <span className="text-white font-semibold">{formatCurrency(item.total || 0)}</span>
                      </div>
                      <div className="flex items-center justify-between gap-3 mt-1 text-xs">
                        <span className="text-slate-500">{Number(item.sales_count || 0)} venda(s)</span>
                        <span className="text-slate-500">{Number(item.revenueWeight || 0).toFixed(1)}% do total</span>
                      </div>
                      <div className="flex items-center justify-between gap-3 mt-1 text-xs">
                        <span className="text-sky-400">Ticket médio: {formatCurrency(item.averageTicket || 0)}</span>
                        <span className="text-slate-500">{item.isAnonymous ? 'Sem ficha cliente' : 'Cliente identificado'}</span>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-slate-500 text-center py-4">Sem dados de clientes no período</p>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>



        <TabsContent value="comparison">
          <div className="space-y-4">
            <Card className="card-dark">
              <CardHeader>
                <CardTitle className="text-white text-lg">Comparação anual</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-slate-300">Ano de comparação</Label>
                    <select
                      value={comparisonYear}
                      onChange={(e) => setComparisonYear(e.target.value)}
                      className="bg-slate-800 border border-slate-700 text-slate-100 rounded-md h-10 px-3 w-full"
                    >
                      {Array.from({ length: 6 }).map((_, idx) => {
                        const year = String(new Date().getFullYear() - idx);
                        return (
                          <option key={year} value={year}>{year}</option>
                        );
                      })}
                    </select>
                  </div>

                  <div className="p-3 bg-slate-800 rounded-lg text-sm text-slate-300 flex items-center">
                    Base contabilística ativa: ano civil de 1 janeiro a 31 dezembro.
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
                  <Card className="card-dark p-4">
                    <p className="text-xs text-slate-400">Vendas</p>
                    <p className="text-xl font-bold text-emerald-400">{formatCurrency(comparisonData.sales.current)}</p>
                    <p className="text-xs text-slate-500 mt-1">Anterior: {formatCurrency(comparisonData.sales.previous)}</p>
                  </Card>

                  <Card className="card-dark p-4">
                    <p className="text-xs text-slate-400">Lucro líquido</p>
                    <p className="text-xl font-bold text-white">{formatCurrency(comparisonData.netProfit.current)}</p>
                    <p className="text-xs text-slate-500 mt-1">Anterior: {formatCurrency(comparisonData.netProfit.previous)}</p>
                  </Card>

                  <Card className="card-dark p-4">
                    <p className="text-xs text-slate-400">Recebimentos</p>
                    <p className="text-xl font-bold text-sky-400">{formatCurrency(comparisonData.receipts.current)}</p>
                    <p className="text-xs text-slate-500 mt-1">Anterior: {formatCurrency(comparisonData.receipts.previous)}</p>
                  </Card>

                  <Card className="card-dark p-4">
                    <p className="text-xs text-slate-400">Compras</p>
                    <p className="text-xl font-bold text-amber-400">{formatCurrency(comparisonData.purchases.current)}</p>
                    <p className="text-xs text-slate-500 mt-1">Pendentes: {formatCurrency(comparisonData.purchases.pending)}</p>
                  </Card>
                </div>
              </CardContent>
            </Card>

            
              <Card className="card-dark">
                <CardHeader>
                  <CardTitle className="text-white text-lg">Comparação homóloga do período</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="p-3 bg-slate-800 rounded-lg text-sm text-slate-300">
                    Período atual: {dateFrom} a {dateTo} · Homólogo: {getHomologuePeriod(dateFrom, dateTo).dateFrom} a {getHomologuePeriod(dateFrom, dateTo).dateTo}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
                    {[
                      ['Vendas', homologueData.sales, true],
                      ['Internas (NCP)', homologueData.internalSales, true],
                      ['Fiscais diretas POS', homologueData.fiscalSales, true],
                      ['Ticket médio', homologueData.averageSale, true],
                      ['Lucro líquido', homologueData.netProfit, true],
                      ['Recebimentos', homologueData.receipts, true],
                      ['Compras', homologueData.purchases, true],
                      ['Compras pendentes', homologueData.purchasesPending, true],
                      ['Clientes', homologueData.customers, false],
                      ['Categorias', homologueData.categories, false],
                      ['Métodos pagamento', homologueData.paymentMethods, false],
                      ['Prestadores', homologueData.providers, false],
                    ].map(([label, row, isMoney]) => {
                      const positive = Number(row?.diff || 0) >= 0;
                      const hasPercent = row?.percent !== null && row?.percent !== undefined;
                      const formatValue = (value) =>
                        isMoney ? formatCurrency(value || 0) : String(Number(value || 0));

                      return (
                        <div key={label} className="p-3 bg-slate-800 rounded-lg">
                          <div className="flex items-center justify-between gap-3">
                            <span className="text-slate-300">{label}</span>
                            <span className={`font-medium ${positive ? 'text-emerald-400' : 'text-rose-400'}`}>
                              {positive ? '+' : ''}{formatValue(row?.diff || 0)}
                            </span>
                          </div>
                          <div className="flex items-center justify-between gap-3 mt-1 text-xs">
                            <span className="text-slate-500">
                              Atual: {formatValue(row?.current || 0)} · Homólogo: {formatValue(row?.previous || 0)}
                            </span>
                            <span className={`${positive ? 'text-emerald-400' : 'text-rose-400'}`}>
                              {hasPercent ? `${positive ? '+' : ''}${Number(row?.percent || 0).toFixed(1)}%` : 'n/d'}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>

<div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <Card className="card-dark">
                <CardHeader>
                  <CardTitle className="text-white text-lg">Indicadores principais</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {[
                    ['Vendas', comparisonData.sales, true],
                    ['Internas (NCP)', comparisonData.internalSales, true],
                    ['Fiscais diretas POS', comparisonData.fiscalSales, true],
                    ['Ticket médio', comparisonData.averageSale, true],
                    ['Lucro líquido', comparisonData.netProfit, true],
                    ['Recebimentos', comparisonData.receipts, true],
                    ['Clientes', comparisonData.customers, false],
                    ['Categorias', comparisonData.categories, false],
                    ['Métodos pagamento', comparisonData.paymentMethods, false],
                  ].map(([label, row, isMoney]) => {
                    const positive = Number(row?.diff || 0) >= 0;
                    const hasPercent = row?.percent !== null && row?.percent !== undefined;

                    const formatValue = (value) =>
                      isMoney ? formatCurrency(value || 0) : String(Number(value || 0));

                    return (
                      <div key={label} className="p-3 bg-slate-800 rounded-lg">
                        <div className="flex items-center justify-between gap-3">
                          <span className="text-slate-300">{label}</span>
                          <span className={`font-medium ${positive ? 'text-emerald-400' : 'text-rose-400'}`}>
                            {positive ? '+' : ''}{formatValue(row?.diff || 0)}
                          </span>
                        </div>
                        <div className="flex items-center justify-between gap-3 mt-1 text-xs">
                          <span className="text-slate-500">
                            Atual: {formatValue(row?.current || 0)} · Anterior: {formatValue(row?.previous || 0)}
                          </span>
                          <span className={`${positive ? 'text-emerald-400' : 'text-rose-400'}`}>
                            {hasPercent ? `${positive ? '+' : ''}${Number(row?.percent || 0).toFixed(1)}%` : 'n/d'}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </CardContent>
              </Card>

              <div className="space-y-4">
                <Card className="card-dark">
                  <CardHeader>
                    <CardTitle className="text-white text-lg">Leitura rápida</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="p-3 bg-slate-800 rounded-lg">
                      <p className="text-slate-400 text-sm">Vendas vs ano anterior</p>
                      <p className={`mt-1 font-medium ${Number(comparisonData.sales.diff || 0) >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                        {Number(comparisonData.sales.diff || 0) >= 0 ? 'Subida' : 'Descida'} de {formatCurrency(Math.abs(Number(comparisonData.sales.diff || 0)))}
                      </p>
                    </div>

                    <div className="p-3 bg-slate-800 rounded-lg">
                      <p className="text-slate-400 text-sm">Lucro líquido vs ano anterior</p>
                      <p className={`mt-1 font-medium ${Number(comparisonData.netProfit.diff || 0) >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                        {Number(comparisonData.netProfit.diff || 0) >= 0 ? 'Melhoria' : 'Quebra'} de {formatCurrency(Math.abs(Number(comparisonData.netProfit.diff || 0)))}
                      </p>
                    </div>

                    <div className="p-3 bg-slate-800 rounded-lg">
                      <p className="text-slate-400 text-sm">Compras pendentes</p>
                      <p className="mt-1 font-medium text-amber-400">
                        {formatCurrency(comparisonData.purchases.pending || 0)}
                      </p>
                    </div>

                    <div className="p-3 bg-slate-800 rounded-lg">
                      <p className="text-slate-400 text-sm">Prestadores em aberto</p>
                      <p className="mt-1 font-medium text-orange-400">
                        {formatCurrency(comparisonData.providers.open || 0)}
                      </p>
                    </div>
                  </CardContent>
                </Card>

                <Card className="card-dark">
                  <CardHeader>
                    <CardTitle className="text-white text-lg">Topos do ano</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="p-3 bg-slate-800 rounded-lg">
                      <p className="text-slate-400 text-sm">Cliente topo</p>
                      <p className="mt-1 font-medium text-white">{comparisonData.topCustomer.currentName}</p>
                      <p className="text-xs text-slate-500 mt-1">{formatCurrency(comparisonData.topCustomer.currentTotal)}</p>
                    </div>

                    <div className="p-3 bg-slate-800 rounded-lg">
                      <p className="text-slate-400 text-sm">Categoria topo</p>
                      <p className="mt-1 font-medium text-white">{comparisonData.topCategory.currentName}</p>
                      <p className="text-xs text-slate-500 mt-1">{formatCurrency(comparisonData.topCategory.currentTotal)}</p>
                    </div>

                    <div className="p-3 bg-slate-800 rounded-lg">
                      <p className="text-slate-400 text-sm">Fornecedores com compras</p>
                      <p className="mt-1 font-medium text-white">{Number(comparisonData.purchases.suppliers || 0)}</p>
                    </div>

                    <div className="p-3 bg-slate-800 rounded-lg">
                      <p className="text-slate-400 text-sm">Prestadores listados</p>
                      <p className="mt-1 font-medium text-white">{Number(comparisonData.providers.count || 0)}</p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="purchases">
          <div className="space-y-4">
            {purchasesData.usingCivilYearFallback && (
              <Card className="card-dark border-amber-500/30">
                <CardContent className="pt-6">
                  <p className="text-amber-300 text-sm">
                    Sem movimentos de compras no período selecionado. A mostrar dados do ano civil de {purchasesData.selectedYear}.
                  </p>
                </CardContent>
              </Card>
            )}
            <Card className="card-dark">
              <CardContent className="pt-6">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label className="text-slate-300">Fornecedor</Label>
                    <select
                      value={purchaseSupplierFilter}
                      onChange={(e) => setPurchaseSupplierFilter(e.target.value)}
                      className="bg-slate-800 border border-slate-700 text-slate-100 rounded-md h-10 px-3 w-full"
                    >
                      <option value="all">Todos os fornecedores</option>
                      {purchaseSupplierOptions.map((opt) => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-slate-300">Faturas no período</Label>
                    <div className="h-10 px-3 rounded-md bg-slate-800 border border-slate-700 flex items-center text-slate-100">
                      {purchasesData.filtered.length}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-slate-300">Vencidas no filtro atual</Label>
                    <div className="h-10 px-3 rounded-md bg-slate-800 border border-slate-700 flex items-center text-rose-400 font-medium">
                      {purchasesData.overdueCount}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              <Card className="card-dark p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-sky-500/20 flex items-center justify-center">
                    <Receipt className="w-5 h-5 text-sky-400" />
                  </div>
                  <div>
                    <p className="text-xs text-slate-400">Total comprado</p>
                    <p className="text-xl font-bold text-white">{formatCurrency(purchasesData.totalPurchased)}</p>
                  </div>
                </div>
              </Card>

              <Card className="card-dark p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-emerald-500/20 flex items-center justify-center">
                    <Wallet className="w-5 h-5 text-emerald-400" />
                  </div>
                  <div>
                    <p className="text-xs text-slate-400">Pago</p>
                    <p className="text-xl font-bold text-emerald-400">{formatCurrency(purchasesData.totalPaid)}</p>
                  </div>
                </div>
              </Card>

              <Card className="card-dark p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-amber-500/20 flex items-center justify-center">
                    <Landmark className="w-5 h-5 text-amber-400" />
                  </div>
                  <div>
                    <p className="text-xs text-slate-400">Pendente</p>
                    <p className="text-xl font-bold text-amber-400">{formatCurrency(purchasesData.totalPending)}</p>
                  </div>
                </div>
              </Card>

              <Card className="card-dark p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-purple-500/20 flex items-center justify-center">
                    <BadgeEuro className="w-5 h-5 text-purple-400" />
                  </div>
                  <div>
                    <p className="text-xs text-slate-400">IVA dedutível</p>
                    <p className="text-xl font-bold text-purple-400">{formatCurrency(purchasesData.totalVatDeductible)}</p>
                  </div>
                </div>
              </Card>

              <Card className="card-dark p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-rose-500/20 flex items-center justify-center">
                    <AlertTriangle className="w-5 h-5 text-rose-400" />
                  </div>
                  <div>
                    <p className="text-xs text-slate-400">Vencidas</p>
                    <p className="text-xl font-bold text-rose-400">{purchasesData.overdueCount}</p>
                  </div>
                </div>
              </Card>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card className="card-dark">
                <CardHeader>
                  <CardTitle className="text-white text-lg">Estado das faturas</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex items-center justify-between p-3 bg-slate-800 rounded-lg">
                    <span className="text-slate-400">Pagas</span>
                    <span className="text-emerald-400 font-medium">{purchasesData.paidCount}</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-slate-800 rounded-lg">
                    <span className="text-slate-400">Parciais</span>
                    <span className="text-amber-400 font-medium">{purchasesData.partialCount}</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-slate-800 rounded-lg">
                    <span className="text-slate-400">Pendentes / abertas</span>
                    <span className="text-white font-medium">{purchasesData.pendingCount}</span>
                  </div>
                </CardContent>
              </Card>

              <Card className="card-dark md:col-span-2">
                <CardHeader>
                  <CardTitle className="text-white text-lg">Resumo rápido</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex items-center justify-between p-3 bg-slate-800 rounded-lg">
                    <span className="text-slate-400">Compras do dashboard mensal</span>
                    <span className="text-white font-medium">{formatCurrency(purchasesData.dashboard?.total_paid_month || 0)}</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-slate-800 rounded-lg">
                    <span className="text-slate-400">Pendente global dashboard</span>
                    <span className="text-amber-400 font-medium">{formatCurrency(purchasesData.dashboard?.total_pending || 0)}</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-slate-800 rounded-lg">
                    <span className="text-slate-400">Vencido global dashboard</span>
                    <span className="text-rose-400 font-medium">{formatCurrency(purchasesData.dashboard?.total_overdue || 0)}</span>
                  </div>
                </CardContent>
              </Card>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Card className="card-dark">
                <CardHeader>
                  <CardTitle className="text-white text-lg">Top Fornecedores</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {Array.isArray(purchasesData.topSuppliers) && purchasesData.topSuppliers.length > 0 ? (
                    purchasesData.topSuppliers.map((item, idx) => (
                      <div key={`${item.supplier_id || item.supplier_name}-${idx}`} className="p-3 bg-slate-800 rounded-lg">
                        <div className="flex items-center justify-between gap-3">
                          <span className="text-slate-300 font-medium">{item.supplier_name || 'Fornecedor'}</span>
                          <span className="text-white font-semibold">{formatCurrency(item.total || 0)}</span>
                        </div>
                        <div className="flex items-center justify-between gap-3 mt-1 text-xs">
                          <span className="text-slate-500">{Number(item.invoices_count || 0)} fatura(s)</span>
                          <span className="text-slate-500">{purchasesData.totalPurchased > 0 ? `${((Number(item.total || 0) / purchasesData.totalPurchased) * 100).toFixed(1)}% do total` : '0,0% do total'}</span>
                        </div>
                        <div className="flex items-center justify-between gap-3 mt-1 text-xs">
                          <span className="text-emerald-400">Pago: {formatCurrency(item.paid || 0)}</span>
                          <span className="text-amber-400">Pendente: {formatCurrency(item.pending || 0)}</span>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-slate-500 text-center py-4">Sem dados de fornecedores no período</p>
                  )}
                </CardContent>
              </Card>

              <Card className="card-dark">
                <CardHeader>
                  <CardTitle className="text-white text-lg">Antiguidade da dívida</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
                    <div className="p-3 bg-slate-800 rounded-lg">
                      <p className="text-xs text-slate-400">Por vencer</p>
                      <p className="text-lg font-semibold text-white mt-1">
                        {formatCurrency(purchasesData.agingBuckets?.not_due || 0)}
                      </p>
                    </div>

                    <div className="p-3 bg-slate-800 rounded-lg">
                      <p className="text-xs text-slate-400">Vencido 1-30 dias</p>
                      <p className="text-lg font-semibold text-amber-400 mt-1">
                        {formatCurrency(purchasesData.agingBuckets?.overdue_1_30 || 0)}
                      </p>
                    </div>

                    <div className="p-3 bg-slate-800 rounded-lg">
                      <p className="text-xs text-slate-400">Vencido 31-60 dias</p>
                      <p className="text-lg font-semibold text-orange-400 mt-1">
                        {formatCurrency(purchasesData.agingBuckets?.overdue_31_60 || 0)}
                      </p>
                    </div>

                    <div className="p-3 bg-slate-800 rounded-lg">
                      <p className="text-xs text-slate-400">Vencido +60 dias</p>
                      <p className="text-lg font-semibold text-rose-400 mt-1">
                        {formatCurrency(purchasesData.agingBuckets?.overdue_61_plus || 0)}
                      </p>
                    </div>

                    <div className="p-3 bg-slate-800 rounded-lg">
                      <p className="text-xs text-slate-400">Sem vencimento</p>
                      <p className="text-lg font-semibold text-slate-200 mt-1">
                        {formatCurrency(purchasesData.agingBuckets?.no_due_date || 0)}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="card-dark">
                <CardHeader>
                  <CardTitle className="text-white text-lg">Fornecedores a liquidar</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {Array.isArray(purchasesData.suppliersToPay) && purchasesData.suppliersToPay.length > 0 ? (
                    purchasesData.suppliersToPay.map((item, idx) => (
                      <div key={`${item.supplierName}-${idx}`} className="p-3 bg-slate-800 rounded-lg">
                        <div className="flex items-center justify-between gap-3">
                          <span className="text-slate-200 font-medium">{item.supplierName}</span>
                          <span className="text-white font-semibold">{formatCurrency(item.totalOpen || 0)}</span>
                        </div>

                        <div className="flex items-center justify-between gap-3 mt-1 text-xs">
                          <span className="text-slate-500">{Number(item.invoicesCount || 0)} fatura(s)</span>
                          <span className="text-rose-400">Vencido: {formatCurrency(item.overdueOpen || 0)}</span>
                        </div>

                        <div className="flex items-center justify-between gap-3 mt-1 text-xs">
                          <span className="text-amber-400">Não vencido: {formatCurrency(item.notDueOpen || 0)}</span>
                          <span className="text-slate-500">
                            Próx. venc.: {item.nextDueDate ? formatDate(item.nextDueDate) : '-'}
                          </span>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-slate-500 text-center py-4">Sem fornecedores com saldo em aberto no filtro atual</p>
                  )}
                </CardContent>
              </Card>

              <Card className="card-dark">
                <CardHeader>
                  <CardTitle className="text-white text-lg">Últimas faturas de compra</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {Array.isArray(purchasesData.latestInvoices) && purchasesData.latestInvoices.length > 0 ? (
                    purchasesData.latestInvoices.map((item, idx) => {
                      const supplierName =
                        item?.supplier_name ||
                        item?.supplier ||
                        item?.supplier_id ||
                        'Fornecedor';

                      const pending = Math.max(0, Number(item?.total || 0) - Number(item?.amount_paid || 0));
                      const rawStatus = String(item?.payment_status || '').toLowerCase();
                      const dueDate = String(item?.due_date || '').slice(0, 10);
                      const today = new Date().toISOString().split('T')[0];
                      const isOverdue = rawStatus === 'overdue' || (!!dueDate && dueDate < today && rawStatus !== 'paid');

                      let statusLabel = item?.payment_status || '-';
                      let statusClass = 'text-slate-400';

                      if (rawStatus === 'paid') {
                        statusLabel = 'Paga';
                        statusClass = 'text-emerald-400';
                      } else if (rawStatus === 'partial') {
                        statusLabel = 'Parcial';
                        statusClass = 'text-amber-400';
                      } else if (isOverdue) {
                        statusLabel = 'Vencida';
                        statusClass = 'text-rose-400';
                      } else if (rawStatus === 'pending') {
                        statusLabel = 'Pendente';
                        statusClass = 'text-sky-400';
                      }

                      return (
                        <div key={`${item?.id || item?.invoice_number || idx}`} className="p-3 bg-slate-800 rounded-lg">
                          <div className="flex items-center justify-between gap-3">
                            <span className="text-slate-300 font-medium">{item?.invoice_number || item?.invoice_no || 'Fatura compra'}</span>
                            <span className="text-white font-semibold">{formatCurrency(item?.total || 0)}</span>
                          </div>
                          <div className="flex items-center justify-between gap-3 mt-1 text-xs">
                            <span className="text-slate-500">{supplierName}</span>
                            <span className="text-slate-500">{formatDate(item?.invoice_date)}</span>
                          </div>
                          <div className="flex items-center justify-between gap-3 mt-1 text-xs">
                            <span className={statusClass}>Estado: {statusLabel}</span>
                            <span className="text-amber-400">Falta: {formatCurrency(pending)}</span>
                          </div>
                          <div className="flex items-center justify-between gap-3 mt-1 text-xs">
                            <span className="text-slate-500">Pago: {formatCurrency(item?.amount_paid || 0)}</span>
                            <span className="text-slate-500">Venc.: {dueDate ? formatDate(dueDate) : '-'}</span>
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <p className="text-slate-500 text-center py-4">Sem faturas de compra no período</p>
                  )}
                </CardContent>
              </Card>

              <Card className="card-dark">
                <CardHeader>
                  <CardTitle className="text-white text-lg">Faturas vencidas</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {Array.isArray(purchasesData.overdueInvoices) && purchasesData.overdueInvoices.length > 0 ? (
                    purchasesData.overdueInvoices.map((item, idx) => {
                      const supplierName =
                        item?.supplier_name ||
                        item?.supplier ||
                        item?.supplier_id ||
                        'Fornecedor';

                      const pending = Math.max(0, Number(item?.total || 0) - Number(item?.amount_paid || 0));

                      return (
                        <div key={`overdue-${item?.id || item?.invoice_number || idx}`} className="p-3 bg-slate-800 rounded-lg">
                          <div className="flex items-center justify-between gap-3">
                            <span className="text-slate-300 font-medium">{item?.invoice_number || item?.invoice_no || 'Fatura compra'}</span>
                            <span className="text-rose-400 font-semibold">{formatCurrency(pending)}</span>
                          </div>
                          <div className="flex items-center justify-between gap-3 mt-1 text-xs">
                            <span className="text-slate-500">{supplierName}</span>
                            <span className="text-slate-500">Venc.: {item?.due_date ? formatDate(item.due_date) : '-'}</span>
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <p className="text-slate-500 text-center py-4">Sem faturas vencidas no filtro atual</p>
                  )}
                </CardContent>
              </Card>

            </div>
          </div>
        </TabsContent>

        <TabsContent value="providers">
          {providersFinancial && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
                <Card className="card-dark p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-blue-500/20 flex items-center justify-center">
                      <Users className="w-5 h-5 text-blue-400" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-400">Prestadores</p>
                      <p className="text-xl font-bold text-white">{providersFinancial.summary?.providers_count || 0}</p>
                    </div>
                  </div>
                </Card>

                <Card className="card-dark p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-purple-500/20 flex items-center justify-center">
                      <ShoppingCart className="w-5 h-5 text-purple-400" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-400">Serviços</p>
                      <p className="text-xl font-bold text-white">{providersFinancial.summary?.services_count || 0}</p>
                    </div>
                  </div>
                </Card>

                <Card className="card-dark p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-sky-500/20 flex items-center justify-center">
                      <Euro className="w-5 h-5 text-sky-400" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-400">Valor serviços</p>
                      <p className="text-xl font-bold text-sky-400">{formatCurrency(providersFinancial.summary?.total_service_value)}</p>
                    </div>
                  </div>
                </Card>

                <Card className="card-dark p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-amber-500/20 flex items-center justify-center">
                      <Euro className="w-5 h-5 text-amber-400" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-400">Total a pagar</p>
                      <p className="text-xl font-bold text-white">{formatCurrency(providersFinancial.summary?.total_due)}</p>
                    </div>
                  </div>
                </Card>

                <Card className="card-dark p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-emerald-500/20 flex items-center justify-center">
                      <TrendingDown className="w-5 h-5 text-emerald-400" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-400">Pago</p>
                      <p className="text-xl font-bold text-emerald-400">{formatCurrency(providersFinancial.summary?.total_paid)}</p>
                    </div>
                  </div>
                </Card>

                <Card className="card-dark p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-rose-500/20 flex items-center justify-center">
                      <BarChart3 className="w-5 h-5 text-rose-400" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-400">Em aberto</p>
                      <p className="text-xl font-bold text-rose-400">{formatCurrency(providersFinancial.summary?.total_open)}</p>
                    </div>
                  </div>
                </Card>
              </div>

              <Card className="card-dark">
                <CardHeader>
                  <CardTitle className="text-white text-lg">Resumo por Prestador</CardTitle>
                </CardHeader>
                <CardContent>
                  {(providersFinancial.providers || []).length > 0 ? (
                    <Table className="table-dark">
                      <TableHeader>
                        <TableRow>
                          <TableHead>Prestador</TableHead>
                          <TableHead>Especialidade</TableHead>
                          <TableHead>Tipo</TableHead>
                          <TableHead>Serviços</TableHead>
                          <TableHead className="text-right">Valor serviços</TableHead>
                          <TableHead className="text-right">A pagar</TableHead>
                          <TableHead className="text-right">Pago</TableHead>
                          <TableHead className="text-right">Em aberto</TableHead>
                          <TableHead className="text-right">Margem clínica</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {(providersFinancial.providers || []).map((row) => (
                          <TableRow key={row.provider_id}>
                            <TableCell className="text-white font-medium">{row.provider_name || '-'}</TableCell>
                            <TableCell className="text-slate-400">{row.provider_specialty || '-'}</TableCell>
                            <TableCell className="text-slate-400">{row.provider_type || '-'}</TableCell>
                            <TableCell className="text-slate-400">{row.services_count || 0}</TableCell>
                            <TableCell className="text-right text-sky-400">{formatCurrency(row.total_service_value)}</TableCell>
                            <TableCell className="text-right text-white">{formatCurrency(row.total_due)}</TableCell>
                            <TableCell className="text-right text-emerald-400">{formatCurrency(row.total_paid)}</TableCell>
                            <TableCell className="text-right text-rose-400">{formatCurrency(row.total_open)}</TableCell>
                            <TableCell className="text-right text-blue-400">{formatCurrency(row.total_company_margin)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  ) : (
                    <p className="text-slate-400">Sem dados de prestadores no período.</p>
                  )}
                </CardContent>
              </Card>

              <Card className="card-dark">
                <CardHeader>
                  <CardTitle className="text-white text-lg">Detalhe por Serviço</CardTitle>
                </CardHeader>
                <CardContent>
                  {(providersFinancial.rows || []).length > 0 ? (
                    <Table className="table-dark">
                      <TableHeader>
                        <TableRow>
                          <TableHead>Data</TableHead>
                          <TableHead>Prestador</TableHead>
                          <TableHead>Serviço</TableHead>
                          <TableHead>Cliente</TableHead>
                          <TableHead>Venda</TableHead>
                          <TableHead className="text-right">Valor serviço</TableHead>
                          <TableHead className="text-right">A pagar</TableHead>
                          <TableHead className="text-right">Pago</TableHead>
                          <TableHead className="text-right">Em aberto</TableHead>
                          <TableHead>Estado</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {(providersFinancial.rows || []).map((row, idx) => (
                          <TableRow key={`${row.sale_id}-${row.sale_item_id}-${idx}`}>
                            <TableCell className="text-slate-400">{formatDate(row.service_date)}</TableCell>
                            <TableCell className="text-white font-medium">{row.provider_name || '-'}</TableCell>
                            <TableCell className="text-slate-300">{row.service_name || '-'}</TableCell>
                            <TableCell className="text-slate-400">{row.customer_name || '-'}</TableCell>
                            <TableCell className="text-slate-400">{row.sale_number || '-'}</TableCell>
                            <TableCell className="text-right text-sky-400">{formatCurrency(row.service_total)}</TableCell>
                            <TableCell className="text-right text-white">{formatCurrency(row.amount_due)}</TableCell>
                            <TableCell className="text-right text-emerald-400">{formatCurrency(row.amount_paid)}</TableCell>
                            <TableCell className="text-right text-rose-400">{formatCurrency(row.amount_open)}</TableCell>
                            <TableCell>
                              <Badge
                                className={
                                  row.provider_type === 'internal'
                                    ? 'bg-sky-500/20 text-sky-300 border border-sky-500/30'
                                    : row.status === 'paid'
                                      ? 'badge-success'
                                      : 'badge-warning'
                                }
                              >
                                {row.provider_type === 'internal'
                                  ? 'Interno'
                                  : row.status === 'paid'
                                    ? 'Pago'
                                    : 'Em aberto'}
                              </Badge>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  ) : (
                    <p className="text-slate-400">Sem serviços com prestador no período selecionado.</p>
                  )}
                </CardContent>
              </Card>
            </div>
          )}
        </TabsContent>

      </Tabs>
    </div>
  );
};

export default ReportsDashboardPage;
